import Phaser from 'phaser';

import {HexMath} from '../utils/HexMath.js';
import {HexGridRenderer} from '../utils/HexGridRenderer.js';
import {getUnitTypeConfig, UNIT_TYPES} from '../configs/units.js';
import {FIELD_FLAG_TEXTURE_PATHS} from '../configs/fieldFlags.js';
import {resolveRevealedStartingQuarters} from '../utils/gameState/setup/resolveRevealedStartingQuarters';
import {checkIfReplayRewinding} from '../utils/replay/replayPlaybackSpeed';
import {trimTextureFrame} from '../utils/sprites/trimTextureFrame';
import {checkIfGroundFadeInView} from '../utils/visuals/groundFadeView';
import {CONSTRUCTION_MARKER_TEXTURE_PATHS} from '../configs/liveFieldMarkers.js';
import {
  addConstructionPlanEntry,
  addPendingManeuverAction,
  addPendingMovementAction,
  addPendingRecruitmentAction,
  addPendingShootingAction,
  agrarianismSelectFarmForConversion,
  calculateMeleeCombatDamage,
  clearHex,
  clearInspectedHex,
  deselectBarracksForRecruitment,
  declineSupplyAutoFarm,
  deselectFarm,
  deselectUnit,
  distributeSupply,
  elementalsSpendElement,
  giantsResetAdditionalSupplySelection,
  giantsSelectUnitForAdditionalSupply,
  hideMeleeTargetsForPrioritySelect,
  moveUnit,
  placeMissionUnit,
  placeNeutralUnit,
  removeConstructionPlanEntry,
  selectBarracksForRecruitment,
  selectFarm,
  selectUnit,
  setHexToken,
  setInspectedHex,
  setInvalidPendingBuildings,
  setSelectedToken,
  setSpecialBuilding,
  setStartingQuarter,
  showRepairBuildingModal,
  showToast, withToastIcon,
  succubesSeduceUnit,
  undoFarmPendingSupply,
  undoPendingManeuverAction,
  undoPendingMovementAction,
  undoPendingRecruitmentAction,
  undoUnitPendingSupply
} from '../redux/actions';
import {findUnitUndoableRecruitmentAction} from "../utils/gameState/recruitment/findUnitUndoableRecruitmentAction";
import {checkIfUnitUpgradeUndoExceedsLimit} from "../utils/gameState/recruitment/checkIfUnitUpgradeUndoExceedsLimit";
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectCurrentPlayerCities,
  selectFortressesUnderConstruction,
  selectGameResults,
  selectGameStartedAt,
  selectGameType,
  selectGiantsSelectedUnitId,
  selectGroundBackground,
  selectHexes,
  selectInvalidPendingBuildings,
  selectIsAgrarianismConversionOn,
  selectIsParallelSupply,
  selectLeprechaunsStealCoinsActions,
  selectNextInitiativePlayerId,
  selectPendingBuildings,
  selectPendingManeuverActions,
  selectPendingMeleeDamage,
  selectPendingMovementActions,
  selectPendingRecruitmentActions,
  selectPendingShootingActions,
  selectPhase,
  selectPlayers,
  selectRound,
  selectFinalRound,
  selectSelectedFarmsForConversion,
  selectSelectedUnit,
  selectSharedGameState,
  selectSpecialBuildings,
  selectStartingQuarters,
  selectUnits,
  selectWarPhaseStage
} from "../redux/reducers/sharedStateReducer/selectors";
import {PHASES, WAR_PHASE_STAGES} from "../constants/phases";
import {getFieldInsets, subscribeFieldInsets, ZERO_FIELD_INSETS} from "../utils/visuals/fieldInsets";
import {
  calcOccupiedCellsBounds,
  calcCameraBoundsPadding,
  clampFreeAreaCenterToPlayArea,
  getFreeAreaBoundsPadding,
  getFreeAreaWorldOffset,
  getFreeAreaWorldSize,
} from "../utils/camera/freeAreaCamera";
import {getAutoSelectionFrameCenter} from "../utils/camera/autoSelectionFraming";
import {ABILITY_SPARKLES} from "../configs/abilitySparkleAnimation";
import {WINGED_FLIGHT_TEXTURE_PATHS} from "../configs/wingedFlightAnimation";
import {checkIfFactionHasFlyingUnits} from "../configs/factions";
import {checkIfAnyGamePreparationPhase} from "../utils/checkIfAnyGamePreparationPhase";
import {getMatchFieldBufferSize} from "../utils/visuals/fieldBuffer";
import {calculateAvailableRecruitmentCells} from "../utils/gameState/calculateAvailableRecruitmentCells";
import {recruitNewUnitOnBarracks} from "../utils/gameState/recruitment/recruitNewUnitOnBarracks";
import {calculateAvailableMovementCells} from "../utils/gameState/calculateAvailableMovementCells";
import {calculateAvailableSupplyCells} from "../utils/gameState/calculateAvailableSupplyCells";
import {calcFarmRemainingSupply} from "../utils/gameState/calcFarmRemainingSupply";
import {hasAdjacentEnemyUnit} from "../utils/gameState/hasAdjacentEnemyUnit";
import {calculateAvailableSeductionCells} from "../utils/factions/succubes/calculateAvailableSeductionCells";
import {checkIfSuccubesUnitCouldSeduce} from "../utils/factions/checkIfSuccubesUnitCouldSeduce";
import {getD20Roll} from "../utils/dice/getD20Roll";
import {calculateSeductionSuccess} from "../utils/factions/succubes/calculateSeductionSuccess";
import {
  selectDestructionToolActive,
  selectHexCoordinatesVisible,
  selectInspectedHexKey,
  selectIsFarmAutoSelected,
  selectIsTutorialActive,
  selectSelectedFarmHexKey,
  selectSelectedToken,
  selectSelectedUnitId,
  selectShowMeleeTargets,
  selectUserId,
  selectConstructionPlanEntries
} from "../redux/reducers/privateStateReducer/selectors";
import {checkIfTutorialUndoAllowed} from "../utils/tutorial/useTutorialUiFlags";
import {findHexUndoableConstruction} from "../utils/gameState/construction/findHexUndoableConstruction";
import {checkIfFarmHasUndoableSupply} from "../utils/gameState/supply/checkIfFarmHasUndoableSupply";
import {findConstructionUndoConflict} from "../utils/gameState/construction/findConstructionUndoConflict";
import {getTokenConfig} from "../utils/buildings/getTokenConfig";
import {createUndoConstructionAction} from "../utils/gameState/construction/createUndoConstructionAction";
import {findMovementUndoConflict} from "../utils/gameState/movement/findMovementUndoConflict";
import {getPlayerFarms} from "../utils/gameState/getPlayerFarms";
import {checkIfNewUnitSelected} from "../utils/gameState/checkIfNewUnitSelected";
import {checkIfUnitHasPendingAdditionalSupply} from "../utils/gameState/checkIfUnitHasPendingAdditionalSupply";
import {COMMON_BUILDING_TOKENS, KEY_POINT_TOKENS, SPECIAL_BUILDING_TOKENS, TERRAIN_TOKENS, TOKENS} from "../constants/tokens";
import {getEmptyHexImage, getGroundBackgroundImage, resolveGroundBackground} from "../configs/groundBackgrounds";
import {getMountainTile, getMountainTileImage} from "../configs/mountainTiles";
import {MISSION_UNIT_TOKEN_PREFIX} from "../configs/campaigns/missionFormat";
import {UNIT_ID_PREFIX} from "../constants/units";
import {checkIfFinalRound} from "../utils/checkIfFinalRound";
import {FARM_SUPPLY_BLOCKED_COUNT} from "../constants/supply";
import {
  buildSupplyPathContext,
  findBlockedSupplyPathFromFarm,
  findNearestFarmSupplyInfo,
  findSupplyPathFromFarm,
  findSupplyPathRelayFarms,
} from "../utils/gameState/supply/findSupplyPaths";
import {isPlayerPolicy} from "../utils/factions/isPlayerPolicy";
import {getPlayerNextPolicy} from "../utils/policy/getPlayerNextPolicy";
import {POLICIES} from "../constants/policies";
import {calcCoinsRecruitmentSize} from "../utils/gameState/recruitment/calcCoinsRecruitmentSize";
import {checkIfOwner} from "../utils/gameState/checkIfOwner";
import {checkIfHexBlockedByEnemyUnit} from "../utils/gameState/checkIfHexBlockedByEnemyUnit";
import {
  CONSTRUCTION_PLACEMENT_KINDS,
  resolveConstructionPlacement
} from "../utils/gameState/construction/resolveConstructionPlacement";
import {checkIfHexClickable} from "../utils/hexes/checkIfHexClickable";
import {
  CONSTRUCTION_PLANNING_CLICK_ACTIONS,
  decideConstructionPlanningClick
} from "../utils/gameState/construction/planning/decideConstructionPlanningClick";
import {calculateAvailableShootingCells} from "../utils/gameState/shooting/calculateAvailableShootingCells";
import {calculateAvailableManeuverCells} from "../utils/gameState/calculateAvailableManeuverCells";
import {keyToCoords} from "../utils/coords/keyToCoords";
import {SPECIAL_BUILDING_CONFIGS} from "../configs/tokens";
import {checkIfCouldManeuverFromHex} from "../utils/gameState/maneuver/checkIfCouldManeuverFromHex";
import {checkIfUnitCouldManeuverFromHex} from "../utils/gameState/maneuver/checkIfUnitCouldManeuverFromHex";
import {getDamageDiceRoll} from "../utils/dice/getDamageDiceRoll";
import {getBotD6MinRoll} from "../utils/dice/getBotD6MinRoll";
import {getBotCombatD6MinRoll} from "../utils/dice/getBotCombatD6MinRoll";
import {getBotD6RollBonus} from "../utils/dice/getBotD6RollBonus";
import {getLowRatingAdvantageDicePenalty} from "../utils/dice/getLowRatingAdvantageDicePenalty";
import {getShotOpponentOwnerIds} from "../utils/shooting/getShotOpponentOwnerIds";
import {checkIfOutOfSupplyPenaltyAhead} from "../utils/gameState/supply/checkIfOutOfSupplyPenaltyAhead";
import {getPlayersByIds} from "../utils/players/getPlayersByIds";
import {getPlayerById} from "../utils/players/getPlayerById";
import {ID_PREFIX_FOR_GOBLINS_TRAP, ID_PREFIX_FOR_SHOOTING_ACTION, ID_PREFIX_FOR_UNIT} from "../constants/ids";
import {createRandomId} from "../utils/ids/createRandomId";
import {MINUS_SIGN} from "../constants/text";
import SoundManager from "./SoundManager";
import FieldAmbience from "./FieldAmbience";
import {getFieldAmbience} from "../configs/fieldAmbience";
import {REGISTRY_SINGLETON} from "./Registry";
import {calculateShootingDamage} from "../utils/gameState/shooting/calculateShootingDamage";
import {getNeighborEnemyUnits} from "../utils/unit/getNeighborEnemyUnits";
import {calcMeleeCombatInput} from "../utils/gameState/melee/calcMeleeCombatInput";
import {D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE} from "../configs/war";
import {checkIfCurrentUserTurn} from "../utils/gameState/user/checkIfCurrentUserTurn";
import {getActingPlayer, getActingPlayerFromState} from "../utils/players/getActingPlayer";
import {findConstructionPlanUndo} from "../utils/gameState/construction/planning/findConstructionPlanUndo";
import {playPlanPlacementIncomeAnimations} from "./playPlanPlacementIncomeAnimations";
import {checkIfConstructionPlanningAvailable} from "../utils/gameState/construction/planning/getConstructionPlanContext";
import {buildTextPlanUndoConflict} from "../constants/text/construction";
import {checkIfParallelTurn} from "../utils/gameState/turn/checkIfParallelTurn";
import {checkIfCentralQuarter} from "../utils/buildings/checkIfCentralQuarter";
import {isBuildingDestroyed} from "../utils/gameState/shooting/buildingDamageUtils";
import {FACTIONS} from "../constants/factions";
import {isUnitFaction} from "../utils/factions/isUnitFaction";
import {isPlayerFaction} from "../utils/factions/isPlayerFaction";
import {checkIfPlayerUnitsNeverStarve} from "../utils/gameState/supply/checkIfPlayerUnitsNeverStarve";
import {validatePendingBuildings} from "../utils/gameState/validatePendingBuildings";
import {getD6Roll} from "../utils/dice/getD6Roll";
import {getUnitById} from "../utils/unit/getUnitById";
import {findOwner} from "../utils/gameState/findOwner";
import {playSupplyRecruitEffects, playUnitSizeRecruitmentEffects} from "./playUnitRecruitmentEffects";
import {resolveRevealedSupplyRecruits} from "../utils/gameState/supply/resolveRevealedSupplyRecruits";
import {BLOCK_SYMBOL_TEXTURE_KEY} from "../configs/liveFieldMarkers";
import {
  playBuildingPlacementEffects,
  playPlannedBuildingPlacementEffects,
  playSpecialBuildingPlacementEffects,
  playStartingQuarterPlacementEffects,
  playStartingQuartersRevealEffects,
} from "./playBuildingPlacementEffects";
import {playInspectedFarmSupplyNetworkAnimation} from "./playFarmSupplyNetworkAnimation";
import {CAMERA_GESTURE, resolveCameraGestureMode} from "../utils/input/resolveCameraGestureMode";
import {resolveLiveTouchIdentifiers} from "../utils/input/resolveLiveTouchIdentifiers";
import {calculateCitiesCache} from "../utils/gameState/calculateCitiesCache";
import {calculateAvailableShootingCellsForElves} from "../utils/factions/elves/calculateAvailableShootingCellsForElves";
import {checkIfUnitMeleeElf} from "../utils/factions/elves/checkIfUnitMeleeElf";
import {getSizeRecruitmentPrice} from "../utils/recruitment/getSizeRecruitmentPrice";
import {FACTION_CONFIGS_IMPLEMENTED} from "../configs/factions";
import {getFactionUnitSkinIconPath} from "../utils/factions/getFactionUnitSkinIconPath";
import {NEUTRAL_PLAYER_COLOR, SPECTATOR_ID} from "../constants/players";
import {checkTutorialActionWouldApply} from "../utils/tutorial/tutorialActionGate";
import {getActiveTutorialContext} from "../configs/tutorial";
import {
  AUTO_SELECTION_CAMERA_MIN_SHIFT_PX,
  AUTO_SELECTION_CAMERA_PAN_DURATION_MS,
  AUTO_SELECTION_CAMERA_PAN_EASE,
  AUTO_SELECTION_CAMERA_UP_SHIFT_FRACTION,
} from "../configs/autoSelectionCamera";
import {getTurnKey, playTurnStartCitiesAnimation} from "./playTurnStartCitiesAnimation";
import {CityCaptureAnimation} from "./CityCaptureAnimation";
import {RuinsIncomeAnimation} from "./RuinsIncomeAnimation";
import {selectIsRuinsIncomeAnimationPending} from "../redux/reducers/privateStateReducer/selectors";
import {playShotAnimation} from "./playShotAnimation";
import {
  playGoblinsBuildingDamageEffects,
  playGoblinsTrapRemovalEffects,
  playGoblinsTrapSetupEffects,
  playSuccubesSeductionEffects,
} from "./playAbilityCastEffects";
import {applyAbilityResultHolds, resetAbilityResultHolds} from "../utils/effects/abilityResultHolds";
import {
  selectIsParallelSupplyPhase,
  selectSupplyActingPlayer,
  selectSupplyActingPlayerCities
} from "../utils/gameState/supply/parallelSupplySelectors";
import {FARM_TOKEN_IMAGES} from "../configs/farmTokenImages";
import {buildSupplyPanelModel, getSupplyTurnKey, pickSupplyFarmForUnit} from "../utils/gameState/supply/buildSupplyPanelModel";
import {selectSupplyPlayerPendingSupply} from "../utils/hexes/calculateTokenImage";
import {createDeferredCallGate} from "./deferredCallGate";
import {checkIfFieldStateUnchanged} from "./fieldStoreUpdate";
import {calculateAvailableTrapSetupCells} from "../utils/factions/goblins/calculateAvailableTrapSetupCells";
import {calculateAvailableTrapRemovalCells} from "../utils/factions/goblins/calculateAvailableTrapRemovalCells";
import {calculateAvailableBuildingDamageCells} from "../utils/factions/goblins/calculateAvailableBuildingDamageCells";
import {calculateGoblinsBuildingDamageOutcome} from "../utils/factions/goblins/calculateGoblinsBuildingDamageOutcome";
import {checkIfGoblinsUnitCouldUseAbility} from "../utils/factions/checkIfGoblinsUnitCouldUseAbility";
import {
  GOBLINS_ABILITY_MODES,
  GOBLINS_ABILITY_SUCCESS_THRESHOLD,
  GOBLINS_TRAP_REMOVAL_REWARD,
  GOBLINS_TRAP_SETUP_COST
} from "../configs/factions/goblins";
import {SUCCUBES_SEDUCTION_COST} from "../configs/factions/succubes";
import {checkIfUnitAbilityApplied} from "../utils/factions/checkIfUnitAbilityApplied";
import {
  goblinsDamageBuilding,
  goblinsRemoveTrap,
  goblinsSetTrap,
  setGoblinsAbilityMode
} from "../redux/actions";
import {selectGoblinsAbilityMode} from "../redux/reducers/privateStateReducer/selectors";
import {selectGoblinsTraps} from "../redux/reducers/sharedStateReducer/selectors";
import {resolveMovementWithTraps} from "../utils/movement/resolveMovementWithTraps";
import {applyGoblinsTrapMovement} from "../redux/actions";
import {checkIfTurnInputBlocked, notifyTurnInputBlocked} from "../utils/gameState/turn/turnInputLock";
import {showGoblinsTrapKillNotice} from "../redux/actions";
import {selectGoblinsTrapDeathHexKeys} from "../redux/reducers/sharedStateReducer/selectors";
import {selectTurnOrder} from "../redux/reducers/sharedStateReducer/selectors";
import {TOAST_ICONS} from "../constants/toast";

// Стартовый зум камеры для компактной витрины главного меню (узкие экраны):
// сцена там размером с ширину телефона, и при обычных 0.8-0.9 в кадр попадала
// бы горстка гексов вместо партии. Отъезд до 0.4 показывал поле целиком, но
// мелко — на телефоне важнее разглядеть, что происходит, чем увидеть все
// разом, поэтому подъехали в полтора раза ближе
const COMPACT_SHOWCASE_INITIAL_ZOOM = 0.6;

// Пределы зума камеры (колесо, щипок). Нижний нужен и для границ прокрутки:
// при самом дальнем зуме отступы интерфейса занимают больше всего мира
const CAMERA_MIN_ZOOM = 0.5;
const CAMERA_MAX_ZOOM = 3;

/**
 * Смещение центра свободной области от центра кадра в мире (см.
 * utils/camera/freeAreaCamera). Функция, а не метод: камеру ставят и
 * прототипные методы, которые тесты зовут на простых объектах-сценах — у тех
 * нет ни отступов, ни ScaleManager, и смещение просто нулевое
 */
const resolveFreeAreaWorldOffset = (scene) => {
  const camera = scene.cameras?.main;
  if (!scene.isMatchField || !camera) {
    return {x: 0, y: 0};
  }
  // zoom ScaleManager — 1 / DPR (см. HexGrid): CSS-пиксель равен DPR буферных
  const dpr = scene.scale?.zoom ? 1 / scene.scale.zoom : 1;
  return getFreeAreaWorldOffset(scene.fieldInsets || ZERO_FIELD_INSETS, dpr, camera.zoom);
};

/**
 * Поле не принимает ввод игрока: камерой и кликами владеет облет захваченных
 * городов (см. CityCaptureAnimation) — пан, зум, клавиши и клики по полю
 * игнорируются, пропустить облет нельзя. Функция, а не метод, по той же
 * причине, что resolveFreeAreaWorldOffset: методы сцены тесты зовут на простых
 * объектах
 */
const isFieldInputLocked = (scene) => Boolean(
  scene.cityCaptureAnimation?.isInputLocked || scene.ruinsIncomeAnimation?.isInputLocked
);

/** Поставить точку мира в центр свободной области (а не окна) */
const centerCameraOnFreeArea = (scene, worldX, worldY) => {
  const offset = resolveFreeAreaWorldOffset(scene);
  scene.cameras.main.centerOn(worldX - offset.x, worldY - offset.y);
};

// Переезд кадра, когда интерфейс поменял занятые края (футер вырос или
// свернулся, появилась полоса этапов): плавный, а не скачком — иначе поле
// дергалось бы на каждой смене этапа и при сворачивании футера
const FREE_AREA_PAN_DURATION_MS = 250;
const FREE_AREA_PAN_EASE = 'Sine.easeInOut';

// Обучение смотрит на поле ниже его середины: и свой город игрока, и почти все
// ячейки, на которые указывают стрелки глав, лежат в нижней половине. Камера
// опускается на четверть высоты игровой части — центр кадра оказывается на
// высоте 1/4 поля от его низа
const TUTORIAL_CAMERA_DOWN_SHIFT_FRACTION = 0.25;

// Окно двойного клика по одной ячейке — жест точечной отмены действия
// (снабжение, строительство, найм, перемещение и маневр)
const UNDO_DOUBLE_CLICK_MS = 350;

/**
 * Подложка поля — картинка пейзажа под сеткой вместо однотонного фона.
 *
 * Какая именно — решает не сцена: в сетевом матче подложку выбирает сервер при
 * старте и присылает в состоянии (см. groundBackground в START_GAME), в
 * локальной партии выбирает клиент, в записи матча она лежит в самом потоке
 * действий и переигрывается той же. Сцена лишь читает готовый выбор из стора и
 * грузит РОВНО ОДНУ картинку из перечня: распакованная текстура 4096x4096
 * занимает в видеопамяти около 64 МБ, и держать несколько разом телефоны не
 * переживут. Перечень и пути — src/configs/groundBackgrounds.js
 */
const GROUND_IMAGE_KEY = 'ground';
/**
 * Какую долю картинки занимает ее центральная равнина — то самое пустое место,
 * в которое должно поместиться игровое поле (все ячейки, кроме морских).
 * Меньше значение — крупнее выглядит пейзаж вокруг поля, больше — мельче.
 */
const GROUND_PLAIN_FRACTION = 0.5;
/**
 * Ширина полосы затухания у краев картинки, в долях ее стороны: за этой
 * полосой подложка полностью переходит в черный фон сцены, так что уехавшая
 * далеко вбок камера видит именно черноту, а не обрезанный край картинки.
 */
const GROUND_FADE_FRACTION = 0.12;
// Сторона служебной текстуры градиента. Растягивается на всю подложку, поэтому
// большой ей быть незачем — важна плавность, а ее дает линейная фильтрация
const GROUND_FADE_TEXTURE_KEY = 'ground_fade';
const GROUND_FADE_TEXTURE_SIZE = 256;
// Ниже всего остального: сетка гексов рисуется на глубине по умолчанию (0)
const GROUND_DEPTH = -2;
const GROUND_FADE_DEPTH = -1;
// Тени на всю ячейку и на каждый отряд: треть каждой картинки прозрачна с краев
const FIELD_TRIMMED_TEXTURES = ['hex_shadow', 'unit_shadow', 'unit_shadow_neutral'];

export default class GameScene extends Phaser.Scene {
  constructor() {
    super('GameScene');
    // Перерисовки, отложенные на время шага бота (см.
    // runWithDeferredStoreUpdates): после шага поле перерисуется перед
    // ближайшим кадром
    this.storeUpdateGate = createDeferredCallGate(() => this.requestStoreUpdate({isForced: true}));
  }

  /**
   * Выполнить fn, не перерисовывая сцену внутри него — даже по прямому
   * запросу (requestFieldRedraw, досрочная перерисовка flushStoreUpdate):
   * поле перерисуется один раз после fn, по итоговому состоянию. Шаг бота —
   * несколько диспатчей подряд в одной задаче, и промежуточные состояния на
   * экран не попадают. Внутри fn ничто не должно читать то, что строит
   * handleStoreUpdate (маркеры, спрайты), — только стор
   */
  runWithDeferredStoreUpdates(fn) {
    return this.storeUpdateGate.run(fn);
  }

  /**
   * Стор изменился — поле перерисуется один раз перед ближайшим кадром
   * (flushStoreUpdate на POST_STEP игры), а не на каждый экшен.
   *
   * Перерисовка поля полная: ~1000 объектов сцены уничтожаются и создаются
   * заново, на большом поле это десятки миллисекунд даже на десктопе. Экшены
   * же приходят пачками — сервер рассылает каждое действие отдельным
   * сообщением (в начале облета захваченных городов это три десятка
   * авто-раздач снабжения), бот делает шаг из нескольких диспатчей, запись
   * матча перематывается сотнями. Phaser рисует кадр только раз за
   * requestAnimationFrame, и промежуточные перерисовки на экран не попадали
   * никогда, а поток стоял на них секундами (на телефоне).
   *
   * Кто читает построенное перерисовкой сразу после своего диспатча (спрайты
   * отрядов и жетонов, высоту накладок), сначала зовет flushStoreUpdate — так
   * делают методы HexGridRenderer, отдающие спрайты наружу
   * (ensureFieldCurrent)
   * @param {{isForced?: boolean}} [options] - isForced: перерисовать, даже
   *   если стор с прошлой перерисовки не менялся: сменилось то, что поле
   *   берет не из стора (подмена облета), или прямую перерисовку отложил шаг
   *   бота (runWithDeferredStoreUpdates)
   */
  requestStoreUpdate({isForced = false} = {}) {
    this.isStoreUpdatePending = true;
    if (isForced) {
      this.isStoreUpdateForced = true;
    }
  }

  /**
   * Перерисовать поле, если с прошлой перерисовки пришли изменения (см.
   * requestStoreUpdate). Зовется перед каждым кадром и досрочно — кодом,
   * которому нужно поле по текущему состоянию. Пустой вызов дешев
   */
  flushStoreUpdate() {
    if (!this.isStoreUpdatePending || this.isFieldRebuilding) {
      return;
    }
    if (this.storeUpdateGate.deferIfRunning()) {
      return;
    }
    const isForced = this.isStoreUpdateForced;
    this.isStoreUpdatePending = false;
    this.isStoreUpdateForced = false;
    // Поменялось только то, что поле не рисует (тосты, чат, реакции)
    if (!isForced && checkIfFieldStateUnchanged(this.lastFieldState, this.store.getState())) {
      return;
    }
    this.handleStoreUpdate();
  }

  init() {
    this.store = this.game.registry.get('store');
    this.gridConfig = this.game.registry.get('gridConfig');
    this.isDemoMode = Boolean(this.game.registry.get('isDemoMode'));

    // Получаем реальные размеры canvas с учётом DPR
    const canvas = this.sys.game.canvas;
    const width = canvas.width;
    const height = canvas.height;

    // Инициализация всех вспомогательных классов с реальными размерами canvas
    this.hexMath = new HexMath(this.gridConfig.hexSize, this.gridConfig.orientation, width, height);
    this.hexGridRenderer = new HexGridRenderer(this, this.hexMath);

    this.soundManager = new SoundManager(this);

    REGISTRY_SINGLETON.setGameScene(this);
  }

  preload() {
    // Отслеживание прогресса загрузки ресурсов
    this.load.on('progress', (value) => {
      const callback = this.game.registry.get('onLoadingProgress');
      if (callback) callback(value);
    });

    // Подложка поля: к этому моменту состояние партии уже в сторе — HexGrid
    // не монтируется, пока не определены игроки (см. GameContent/MenuDemoGame),
    // а вместе с ними приезжает и выбранная подложка. Если выбора все же нет
    // (снимок обучения, старая запись матча), берем случайную — но ОДИН раз на
    // сцену, чтобы картинка не менялась на каждой перерисовке
    this.groundBackgroundId = resolveGroundBackground(
      selectGroundBackground(this.store.getState())
    );
    this.load.image(GROUND_IMAGE_KEY, getGroundBackgroundImage(this.groundBackgroundId));

    // Существующие строения
    // Клубы пыли от поставленного жетона: ключи совпадают с
    // BUILDING_DUST_TEXTURES (см. configs/buildingDustAnimation)
    // Подсветка ячейки при найме отряда (см. configs/unitRecruitmentAnimation)
    this.load.image('dust_1', '/img/effects/dust/dust_1.png');
    this.load.image('dust_2', '/img/effects/dust/dust_2.png');
    this.load.image('dust_3', '/img/effects/dust/dust_3.png');
    this.load.image('dust_4', '/img/effects/dust/dust_4.png');
    // Выстрел катапульты: валуны и спрайт-лист осколков, на которые валун
    // раскалывается. Ключи, пути и размер кадра совпадают с
    // SHOT_PROJECTILE_IMAGES и SHOT_SHARDS_SHEET (см. configs/shotAnimation)
    this.load.image('shot_projectile_1', '/img/effects/shooting/projectile_1.png');
    this.load.image('shot_projectile_2', '/img/effects/shooting/projectile_2.png');
    this.load.image('shot_projectile_3', '/img/effects/shooting/projectile_3.png');
    this.load.spritesheet('shot_shards', '/img/effects/shooting/shards.png', {frameWidth: 64, frameHeight: 64});
    // Ближний бой: оружие фракций — белые силуэты, красятся в цвет игрока. Ключ,
    // путь и размер кадра совпадают с MELEE_WEAPONS_SHEET (см. configs/meleeAnimation)
    this.load.spritesheet('melee_weapons', '/img/effects/melee/weapons.v2.png', {frameWidth: 128, frameHeight: 128});
    // Следы за отрядами: ключ, путь и размер кадра совпадают с FOOTPRINT_SHEET (см.
    // configs/unitFootprintsAnimation)
    this.load.spritesheet('unit_footprints', '/img/effects/footprints/footprints.v1.png', {frameWidth: 96, frameHeight: 96});
    // Облака над пейзажем и их тени: ключ, путь и размер кадра должны
    // совпадать с FIELD_CLOUDS_SHEET (configs/fieldAmbience); путь вписывает
    // генератор листа. Грузим только там, где облака есть: солнечным
    // подложкам лист не нужен
    if (getFieldAmbience(this.groundBackgroundId).clouds.isEnabled) {
      this.load.spritesheet('ambience_clouds', '/img/effects/ambience/clouds.4606e19f.png', {frameWidth: 384, frameHeight: 240});
    }
    // Орел над горами и гриф костяной подложки — картинки владельца игры.
    // Ключи и пути должны совпадать с FIELD_EAGLE_IMAGE (configs/fieldAmbience)
    // и FIELD_BIOME_VULTURE_IMAGE (configs/fieldBiomeEffects); пути вписывает
    // generateFieldAmbienceBirds.cjs. Не загрузится орел — его нарисует код;
    // без картинки грифа грифов на костяной подложке не будет
    this.load.image('ambience_eagle_image', '/img/effects/other/eagle.3d6607db.png');
    this.load.image('biome_vulture', '/img/effects/other/vulture.fb100db9.png');
    this.load.image('quarter', '/img/hexes/quarter.f69660fa.png');
    this.load.image('quarter_base', '/img/hexes/quarter_base.4ec61fc0.png');
    Object.entries(FIELD_FLAG_TEXTURE_PATHS).forEach(([key, url]) => this.load.image(key, url));
    this.load.image('farm', '/img/hexes/farm.699e6dfc.png');
    Object.entries(FARM_TOKEN_IMAGES).forEach(([key, url]) => this.load.image(key, url));
    this.load.image('barracks', '/img/hexes/barracks.efab12f7.png');
    this.load.image('barracks_active', '/img/hexes/barracks_active.22121e1f.png');
    // Казарма рядом с рабочим военным лагерем — два меча (см. calculateTokenImage)
    this.load.image('barracks_2', '/img/hexes/barracks_2.319e3f5e.png');
    this.load.image('barracks_2_active', '/img/hexes/barracks_2_active.0c63cb8e.png');
    this.load.image('hex_shadow', '/img/hexes/hex_shadow.0747c381.png');
    // Трехмерной стрелки пока нет — берем обычную. Путь /img/icons/3d/... вел
    // в несуществующую папку, и стрелка в обучении просто не рисовалась
    // (проверку всех загрузок по диску см. gameSceneAssets.test.js)
    this.load.image('tutorial_arrow', '/img/icons/arrow_down_big.png');

    // Новые строения без ограничений
    this.load.image('mine', '/img/hexes/mine.ecae90cd.png');
    this.load.image('super_mine', '/img/hexes/super_mine.7a4a81f7.png');
    this.load.image('ruins', '/img/hexes/ruins.65862e11.png');
    this.load.image('super_ruins', '/img/hexes/super_ruins.97515170.png');
    this.load.image('road', '/img/hexes/road.1f281346.png');
    // Гора: у каждой подложки свой тайл (см. configs/mountainTiles), и на поле
    // он один на все горы. Грузится только он — тайлы остальных подложек в
    // этой партии не нужны. Ключ текстуры — имя тайла
    const mountainTile = getMountainTile(this.groundBackgroundId);
    this.load.image(mountainTile, getMountainTileImage(mountainTile));
    // Картинка пустой ячейки: под зимними подложками — затемненная, иначе
    // обычная (см. getEmptyHexImage). Ключ текстуры один, отрисовке все равно,
    // какой из двух файлов за ним стоит. Раньше вариантов было четыре, они
    // разыгрывались случайно и грузились только под флагом окружения — каждый
    // под полмегабайта; теперь файлы весят килобайты, и флаг не нужен
    this.load.image('empty_hex', getEmptyHexImage(this.groundBackgroundId));

    // Специальные строения
    SPECIAL_BUILDING_CONFIGS.forEach(building => {
      this.load.image(building.id, building.image);
    });
    this.load.image('fortress_construction', '/img/icons/fortress_construction.897ca848.png');

    // Иконки кубиков (для отрядов)
    this.load.image('unit_shadow', '/img/icons/unit_shadow.png');
    // Светлая тень нейтральных отрядов — они рисуются без контура
    this.load.image('unit_shadow_neutral', '/img/icons/unit_shadow_neutral.png');
    this.load.image('unit_d4_white', '/img/icons/unit d4 white.png');
    this.load.image('unit_d6_white', '/img/icons/unit d6 white.png');
    this.load.image('unit_d10_white', '/img/icons/unit d10 white.png');
    this.load.image('unit_d20_white', '/img/icons/unit d20 white.png');
    this.load.image('unit_d8_white', '/img/icons/unit d8 white.png');
    this.load.image('unit_d12_white', '/img/icons/unit d12 white.png');

    // Собственные иконки нейтральных отрядов (по одной на тип) — нейтралы
    // встречаются в любой партии, поэтому грузятся безусловно, в отличие от
    // фракционных скинов ниже (те — только для реально играющих фракций)
    Object.values(UNIT_TYPES).forEach((unitType) => {
      this.load.image(
        `${unitType.id}_${NEUTRAL_PLAYER_COLOR}`,
        getFactionUnitSkinIconPath(unitType.id, NEUTRAL_PLAYER_COLOR)
      );
    });

    // Фракционные скины отрядов — грузим только те фракции, которые реально
    // нужны в этой партии (игроки уже определены к моменту создания сцены,
    // см. гейтинг монтирования HexGrid на готовность players в GameContent/
    // MenuDemoGame): в демо-режиме нужны скины всех играющих фракций, в
    // остальных играх — только у чьих владельцев открыт hasUnitSkin
    const playersForPreload = selectPlayers(this.store.getState());
    const neededSkinFactionIds = new Set();
    playersForPreload.forEach((player) => {
      if (player.factionId && (this.isDemoMode || player.hasUnitSkin)) {
        neededSkinFactionIds.add(player.factionId);
      }
    });
    FACTION_CONFIGS_IMPLEMENTED
      .filter((faction) => neededSkinFactionIds.has(faction.id))
      .forEach((faction) => {
        const unitTypeIds = faction.unitTypes || Object.values(UNIT_TYPES).map((unitType) => unitType.id);
        unitTypeIds.forEach((unitTypeId) => {
          this.load.image(`${unitTypeId}_${faction.id}`, getFactionUnitSkinIconPath(unitTypeId, faction.id));
        });
      });

    // Дефолтная иконка отряда великанов - fallback для игроков БЕЗ купленного
    // фракционного скина (hasUnitSkin), поэтому грузится отдельно от блока выше
    // (тот грузит скины только для hasUnitSkin-игроков)
    const needsGiantsDefaultIcon = this.isDemoMode
      || playersForPreload.some((player) => player.factionId === FACTIONS.GIANTS);
    if (needsGiantsDefaultIcon) {
      this.load.image('unit_d20_giants_default', '/img/factions/unit_icons/unit_d20_giants_default.png');
    }

    // Крылья и перья анимации полета — только в партии с летающими отрядами
    const needsFlightTextures = this.isDemoMode
      || playersForPreload.some((player) => checkIfFactionHasFlyingUnits(player.factionId));
    if (needsFlightTextures) {
      Object.entries(WINGED_FLIGHT_TEXTURE_PATHS).forEach(([key, texturePath]) => this.load.image(key, texturePath));
    }

    // Иконки для характеристик и действий отрядов
    this.load.image('size_rect', '/img/icons/size rect.png');
    this.load.image('coins_icon', '/img/icons/coins.png');
    this.load.image('win_points', '/img/icons/win points.png');
    this.load.image('supply', '/img/icons/supply.png');
    this.load.image('supply_white', '/img/icons/supply white.png');
    this.load.image('supply_outline', '/img/icons/supply outline.png');
    this.load.image('supply_outline_shadow', '/img/icons/supply outline shadow.png');
    this.load.image('speed', '/img/icons/speed.png');
    this.load.image('speed_white', '/img/icons/speed white.png');
    this.load.image('speed_white_shadow', '/img/icons/speed white shadow.png');
    // Ячейки, доступные для перемещения летающего отряда (крылатая ступня)
    this.load.image('speed_fly_white_shadow', '/img/icons/speed_fly_white_shadow.png');
    this.load.image('maneuver', '/img/icons/maneuver.png');
    this.load.image('maneuver_shadow', '/img/icons/maneuver shadow.png');
    this.load.image('ability', '/img/icons/ability.png');
    this.load.image('ability_white', '/img/icons/ability_white.png');
    // Искра способности над отрядом, который еще может ее применить (см.
    // configs/abilitySparkleAnimation): самая крупная звезда иконки этапа
    this.load.image(ABILITY_SPARKLES.textureKey, '/img/icons/ability_sparkle_white.png');
    this.load.image('coins_white', '/img/icons/coins white.png');
    this.load.image('steal_hand_white', '/img/icons/steal_hand_white.png');
    this.load.image('shooting_outline', '/img/icons/shooting outline.png');
    this.load.image('shooting_white', '/img/icons/shooting white.png');
    this.load.image('barracks_outline', '/img/icons/barracks outline.png');
    this.load.image('barracks_white', '/img/icons/barracks white.png');
    this.load.image('damage', '/img/icons/damage.png');
    this.load.image('block_red_outline', '/img/icons/block red outline.png');
    this.load.image(BLOCK_SYMBOL_TEXTURE_KEY, '/img/icons/block.png');
    this.load.image('trap', '/img/icons/trap.png');
    this.load.image('trap_white', '/img/icons/trap_white.png');
    this.load.image('priority_target_outline', '/img/icons/priority_target_outline.png');

    // Hammer icon for pending buildings
    Object.entries(CONSTRUCTION_MARKER_TEXTURE_PATHS).forEach(([key, url]) => this.load.image(key, url));
    // Подсказка «рядом нужен квартал» у построенных ферм/казарм/кварталов
    this.load.image('quarter_hint_flag', '/img/icons/flag white.png');
    this.load.image('quarter_hint_settlement', '/img/icons/settlement white.png');
    this.load.image('hammer_red', '/img/icons/hammer red.png');
    this.load.image('hammer_white', '/img/icons/hammer white.png');

    // Quarter zone overlay
    this.load.image('quarter_zone', '/img/icons/quarter_zone.png');
    this.load.image('base_quarter_allowed', '/img/icons/base_quarter_allowed.png');

    // Building damage icon

    // Load Franklin Gothic Heavy font for Phaser text
    this.load.font('Franklin Gothic Heavy', '/fonts/Franklin_Gothic_Heavy.ttf');

    this.soundManager.preload();
  }

  create() {
    if (!this.store || !this.gridConfig) {
      console.error('Store or gridConfig not found');
      return;
    }

    this.isSceneActive = true;

    // Анимаций по кадрам на поле нет, а каждый Sprite на кадр проходит список обновления
    // и держит слушателя на общем менеджере анимаций: удаление сотен таких спрайтов на
    // перерисовке поля — квадратичный обход этого списка. Image выглядит так же
    if (this.add) {
      this.add.sprite = (x, y, texture, frame) => this.add.image(x, y, texture, frame);
    }
    FIELD_TRIMMED_TEXTURES.forEach((textureKey) => trimTextureFrame(this, textureKey));

    // Облет захваченных городов в конце раунда (см. CityCaptureAnimation) —
    // заново на каждый прогон сцены
    this.cityCaptureAnimation?.abort();
    this.cityCaptureAnimation = new CityCaptureAnimation(this);
    // Рост дохода руин в первом раунде окна (см. RuinsIncomeAnimation) — тоже
    this.ruinsIncomeAnimation?.abort();
    this.ruinsIncomeAnimation = new RuinsIncomeAnimation(this);

    // Phaser переиспользует экземпляр сцены при перезапуске, а спрайты старого
    // прогона к этому моменту уже уничтожены — иначе ensureGroundBackground
    // считал бы подложку живой и трогал бы мертвые объекты
    this.groundSprite = null;
    this.groundFadeSprite = null;
    this.groundBoundsSignature = null;
    this.groundPlace = null;
    // Антураж над пейзажем (облака, птицы) — тоже заново: строится по
    // подложке и игровой части поля (см. syncFieldAmbience)
    this.fieldAmbience = null;
    this.fieldAmbienceSignature = null;
    this.fieldMountainsSignature = null;
    this.isFieldAmbienceFailed = false;
    // Двигал ли игрок камеру сам: до этого кадр обучения держим сдвинутым
    // (см. applyTutorialCameraShift) — заново на каждый прогон сцены
    this.isCameraMovedByPlayer = false;
    this.groundFadeFailed = false;
    // Стартовый кадр (см. updateStartCameraFrame) — заново на каждый прогон
    this.startCameraTarget = null;
    // Отряд, к которому автовыбор подвел кадр (focusCameraOnAutoSelectedUnit)
    this.autoSelectedUnitCameraTarget = null;
    this.startCameraFrameKey = null;
    // Отступы свободной области — только у поля матча: витрина меню живет в
    // своем блоке без HUD поверх
    this.isMatchField = Boolean(this.game?.registry?.get('isMatchField'));
    this.fieldInsets = this.isMatchField ? getFieldInsets() : ZERO_FIELD_INSETS;
    if (this.unsubscribeFieldInsets) {
      this.unsubscribeFieldInsets();
    }
    this.unsubscribeFieldInsets = this.isMatchField
      ? subscribeFieldInsets((insets) => this.handleFieldInsetsChanged(insets))
      : null;

    this.events.on('destroy', this.onDestroy, this);

    const state = this.store.getState();
    const hexCoordinatesVisible = selectHexCoordinatesVisible(state)

    // Настройка камеры для зума и панорамирования
    this.setupCameraControls(state);

    // Генерация сетки через HexGridRenderer
    this.hexGridRenderer.generateHexGrid(this.gridConfig, hexCoordinatesVisible);

    // Границы панорамирования камеры — по фактически отрисованной сетке
    // (разные поля отличаются radius/hexSize, отдельного расчета не нужно)
    this.setupCameraBounds();

    this.handleStoreUpdate();

    // Для отслеживания кликов (отличие клика от перетаскивания)
    this.pointerDownTime = 0;
    this.pointerDownPos = {x: 0, y: 0};
    this.isPanning = false;

    this.input.on('pointerdown', (pointer) => {
      this.pointerDownTime = Date.now();
      this.pointerDownPos.x = pointer.x;
      this.pointerDownPos.y = pointer.y;
      this.isPanning = false;
    });

    this.input.on('pointermove', (pointer) => {
      // Если началось перетаскивание, помечаем это
      if (this.pointerDownTime > 0) {
        const moveDistance = Math.sqrt(
          Math.pow(pointer.x - this.pointerDownPos.x, 2) +
          Math.pow(pointer.y - this.pointerDownPos.y, 2)
        );
        if (moveDistance > 10) {
          this.isPanning = true;
        }
      }
    });

    this.input.on('pointerup', (pointer) => {
      // Вычисляем время и расстояние с момента нажатия
      const timeSinceDown = Date.now() - this.pointerDownTime;
      const moveDistance = Math.sqrt(
        Math.pow(pointer.x - this.pointerDownPos.x, 2) +
        Math.pow(pointer.y - this.pointerDownPos.y, 2)
      );

      // Если это короткое касание без перемещения - это клик
      const isClick = timeSinceDown < 300 && moveDistance < 10;

      // В демо-партии главного меню разрешены только пан и зум камеры —
      // сама игровая логика (выбор отрядов/гексов) остается недоступной
      if (isClick && !this.isPanning && !this.isDemoMode) {
        this.handleClick(pointer);
      }

      // Сбрасываем состояние
      this.pointerDownTime = 0;
      this.isPanning = false;
    });

    // Экшены только отмечают, что поле устарело, — перерисовка одна на кадр:
    // после обновления сцен и до отрисовки (см. requestStoreUpdate)
    this.unsubscribe = this.store.subscribe(() => this.requestStoreUpdate());
    this.game.events.off(Phaser.Core.Events.POST_STEP, this.flushStoreUpdate, this);
    this.game.events.on(Phaser.Core.Events.POST_STEP, this.flushStoreUpdate, this);

    // Сообщаем React, что сцена загружена и первый рендеринг выполнен
    const onReady = this.game.registry.get('onGameReady');
    if (onReady) onReady();
  }

  /**
   * Начался ход игрока — заливаем его города его цветом (см.
   * playTurnStartCitiesAnimation). Ключ хода запоминается, чтобы анимация шла
   * один раз на ход, а не на каждое обновление стора внутри хода
   */
  updateTurnStartCitiesAnimation(state) {
    // Пока камера облетает захваченные города, заливка городов ходящего
    // игрока легла бы поверх анимации захвата. Ключ хода не запоминаем —
    // заливка проиграется на первой перерисовке после облета
    if (this.cityCaptureAnimation?.isActive || this.ruinsIncomeAnimation?.isActive || selectIsRuinsIncomeAnimationPending(state)) {
      return;
    }
    const turnKey = getTurnKey(state);
    if (turnKey === this.lastTurnAnimationKey) {
      return;
    }

    this.lastTurnAnimationKey = turnKey;
    if (!turnKey) {
      return;
    }

    playTurnStartCitiesAnimation(state, this.hexGridRenderer);
  }

  handleStoreUpdate() {
    if (!this.isSceneActive || !this.add) {
      return;
    }
    // Идет шаг бота — перерисуемся один раз после него
    if (this.storeUpdateGate.deferIfRunning()) {
      return;
    }
    // Поле рисуется по текущему состоянию — отложенная перерисовка больше не
    // нужна
    this.isStoreUpdatePending = false;
    this.isStoreUpdateForced = false;
    this.isFieldRebuilding = true;
    // try/catch: перерисовку зовут и изнутри чужого кода (досрочная
    // перерисовка перед анимацией, облет городов) — ее ошибка не должна
    // ронять вызвавший код (например, цикл воспроизведения записи матча).
    // Следующий апдейт перерисует сцену по актуальному состоянию, а ошибка
    // останется в консоли
    try {
      // Сравнение «поменялось ли поле» идет по настоящему состоянию, а
      // рисуется поле с придержанными итогами способностей, чьи анимации еще
      // летят (см. abilityResultHolds)
      const storeState = this.store.getState();
      this.playStartingQuartersReveal(this.lastFieldState, storeState);
      this.playSupplyRecruitsReveal(this.lastFieldState, storeState);
      this.lastFieldState = storeState;
      const state = applyAbilityResultHolds(storeState);
      const hexes = selectHexes(state)
      const hexCoordinatesVisible = selectHexCoordinatesVisible(state)

      // Подложка-пейзаж под сеткой. Строится НЕ в create(): на старте сцены
      // поле еще не загружено, а в состоянии по умолчанию все ячейки числятся
      // морем (createDefaultSharedGameState) — игровой части попросту нет.
      // Внутри стоит проверка на изменение границ, так что на каждом апдейте
      // ничего не пересоздается
      this.ensureGroundBackground(state);
      this.syncFieldAmbience(state);
      this.updateStartCameraFrame(state);
      this.applyTutorialCameraShift(state);

      this.hexGridRenderer.clearTweens();
      this.hexGridRenderer.clearMarkers();

      // Передаем дополнительное состояние для отображения ферм в фазе снабжения
      this.hexGridRenderer.updateHexes(hexes, state);
      this.hexGridRenderer.updatePendingBuildingMarkers(state);
      // План строительства в чужой ход — те же молотки на запланированном
      this.hexGridRenderer.updatePlannedBuildingMarkers(state);
      // Подсказки кварталов — ДО маркеров доступности: на ячейках подсказок
      // те не рисуют иконку молотка (см. quarterHintCellKeys)
      this.hexGridRenderer.updateQuarterHintMarkers(state);
      // Ячейки строительства и зона кварталов; при планировании в чужой ход —
      // для самого игрока с учетом плана
      this.hexGridRenderer.updateConstructionAvailabilityMarkers(state);

      this.hexGridRenderer.updateSelectedUnitMarkers(state);
      this.hexGridRenderer.updateNewUnitRecruitmentMarkers(state);
      this.hexGridRenderer.updateUnitSizeRecruitmentMarkers(state);
      this.hexGridRenderer.updateShootingMarkers(state);
      this.hexGridRenderer.updateSeductionMarkers(state);
      this.hexGridRenderer.updateGoblinsAbilityMarkers(state);
      // Зона действия: у катапульт и отрядов эльфов — вне зависимости от фазы и этапа, и для
      // выбранного отряда, и для инспектируемой чужой ячейки; у соблазнения и способностей
      // гоблинов — на этапе способностей
      this.hexGridRenderer.updateRangeZone(state);
      this.hexGridRenderer.updateUnitMovementMarkers(state);
      this.hexGridRenderer.updateUnitManeuverMarkers(state);
      this.hexGridRenderer.updateSupplyMarkers(state);
      this.hexGridRenderer.updateSetupQuarterMarkers(state);
      this.hexGridRenderer.updateInspectedHexMarker(state);
      this.hexGridRenderer.updateTutorialMarkers(state);
      this.hexGridRenderer.updateHexCoordinatesVisibility(hexCoordinatesVisible, hexes);

      // Обновляем отряды только если не идет анимация перемещения
      if (!this.hexGridRenderer.isAnimatingMovement) {
        this.hexGridRenderer.updateUnits(state);
      }

      // Индикаторы срабатывания ловушек Гоблинов (см. add-goblins-faction
      // design.md Decision 21) - персистентный редрав из
      // state.private.goblinsTrapKillNotice, как и остальные индикаторы
      // урона; после updateUnits - мигание живого отряда добавляется поверх
      // уже созданного им спрайта (this.hexGridRenderer.unitsSprites)
      this.hexGridRenderer.updateGoblinsTrapTriggerDisplay(state);

      // Разовая анимация «ячейка заблокирована» над ячейками вражеских
      // городов, которые заблокировало это перемещение (фаза войны,
      // этапы перемещения/маневра) — дифф позиций отрядов внутри
      this.hexGridRenderer.updateBlockedCellsPulse(state);

      // Разовая анимация начала хода: города игрока, чей ход начался,
      // подсвечиваются его цветом
      this.updateTurnStartCitiesAnimation(state);
    } catch (error) {
      console.error('[GameScene] handleStoreUpdate error', error);
    } finally {
      this.isFieldRebuilding = false;
    }
  }

  /**
   * Конец расстановки: чужие стартовые кварталы падают на поле. Запускается до
   * перерисовки, как и настоящая постройка: перерисовка уже знает, что они в полете.
   * Анимация — украшение: ее сбой не должен оставить поле неперерисованным
   */
  playStartingQuartersReveal(prevState, state) {
    if (this.isDemoMode || !prevState || checkIfReplayRewinding()) {
      return;
    }
    try {
      const quarters = resolveRevealedStartingQuarters(prevState, state);
      if (quarters.length > 0) {
        playStartingQuartersRevealEffects(
          this.store, this.soundManager, this.hexGridRenderer, quarters,
          (callback, delayMs) => this.time.delayedCall(delayMs, callback),
        );
      }
    } catch (error) {
      console.warn('[GameScene] не удалось показать раскрытие стартовых кварталов', error);
    }
  }

  /**
   * Чужие новобранцы крысолюдов за снабжение встают на поле с итогом фазы. Запускается
   * до перерисовки, как и раскрытие кварталов: отряд ни на кадр не покажется готовым
   */
  playSupplyRecruitsReveal(prevState, state) {
    if (this.isDemoMode || !prevState || checkIfReplayRewinding()) {
      return;
    }
    try {
      const recruits = resolveRevealedSupplyRecruits(prevState, state);
      recruits.forEach((unit) => {
        playSupplyRecruitEffects(this.store, this.hexGridRenderer, this.soundManager, {unit, isPaced: true});
      });
    } catch (error) {
      console.warn('[GameScene] не удалось показать новобранцев за снабжение', error);
    }
  }

  update(time, delta) {
    this.updateKeyboardCameraPan();
    this.updateFieldAmbience(delta);
  }

  /**
   * Шаг антуража поля (облака плывут, стаи летят) — по времени кадра.
   *
   * Антураж — украшение: сбой в нем не должен повторяться каждый кадр (сторож
   * кадров принял бы это за зависшую отрисовку и пересобирал бы поле), поэтому
   * после первого же сбоя антураж до конца этой сцены отключается
   */
  updateFieldAmbience(delta) {
    if (!this.fieldAmbience) {
      return;
    }
    try {
      this.fieldAmbience.update(delta);
    } catch (error) {
      console.warn('[GameScene] Антураж поля отключен: сбой при обновлении', error);
      this.dropFieldAmbience();
    }
  }

  /** Антураж сломался: убрать его и до конца сцены не строить заново */
  dropFieldAmbience() {
    this.isFieldAmbienceFailed = true;
    const fieldAmbience = this.fieldAmbience;
    this.fieldAmbience = null;
    try {
      fieldAmbience?.destroy();
    } catch (error) {
      console.warn('[GameScene] Не удалось убрать антураж поля', error);
    }
  }

  updateKeyboardCameraPan() {
    if (!this.keys || isFieldInputLocked(this)) return;

    let dx = 0;
    let dy = 0;

    if (this.keys.up.isDown || this.keys.upArrow.isDown) dy -= 1;
    if (this.keys.down.isDown || this.keys.downArrow.isDown) dy += 1;
    if (this.keys.left.isDown || this.keys.leftArrow.isDown) dx -= 1;
    if (this.keys.right.isDown || this.keys.rightArrow.isDown) dx += 1;

    if (dx !== 0 || dy !== 0) {
      this.isCameraMovedByPlayer = true;
      this.stopCameraPan();
      const camera = this.cameras.main;
      const speed = 15 / camera.zoom;
      camera.scrollX += dx * speed;
      camera.scrollY += dy * speed;
    }
  }

  handleClick(pointer) {
    // Идет облет захваченных городов: поле не кликается, пропустить его нельзя
    if (isFieldInputLocked(this)) {
      return;
    }

    // Время хода игрока вышло, сервер вот-вот закроет ход (см. turnInputLock):
    // до закрытия поле на клики не отвечает — ни действием, ни выбором, ни
    // звуком. Проверка — раньше всего остального: клик под блокировкой не
    // должен даже добивать анимацию
    const stateOnClick = this.store.getState();
    if (checkIfTurnInputBlocked(stateOnClick)) {
      notifyTurnInputBlocked(this.store.dispatch, stateOnClick);
      return;
    }

    // Если идёт анимация перемещения отряда — мгновенно завершаем её
    if (this.hexGridRenderer.isAnimatingMovement) {
      this.hexGridRenderer.skipUnitMovementAnimation();
    }

    // Используем Phaser для правильного преобразования координат с учётом зума и позиции камеры
    const camera = this.cameras.main;
    const worldPoint = camera.getWorldPoint(pointer.x, pointer.y);
    const worldX = worldPoint.x;
    const worldY = worldPoint.y;

    // Используем HexGridRenderer для поиска гексагона. Ячейка, на которую
    // нажать нельзя (море в идущей партии, см. checkIfHexClickable), — клик
    // мимо поля
    const hexAtPointer = this.hexGridRenderer.getHexAt(worldX, worldY);
    const closestHex = hexAtPointer && checkIfHexClickable(this.store.getState(), hexAtPointer.key)
      ? hexAtPointer
      : null;

    // Отложенный эффект прошлого клика (см. deferFirstClickEffect): клик по
    // другой ячейке или мимо поля второй половиной двойного клика не будет —
    // эффект выполняется сейчас, до обработки нового клика
    if (this.deferredFirstClickEffect && this.lastUndoGestureClick?.hexKey !== closestHex?.key) {
      this.flushDeferredFirstClickEffect();
    }

    if (closestHex) {
      const state = this.store.getState();
      const userId = selectUserId(state);
      const gameType = selectGameType(state);
      const gameResults = selectGameResults(state);
      const round = selectRound(state);
      const phase = selectPhase(state);
      const warPhaseStage = selectWarPhaseStage(state);
      const hexes = selectHexes(state);
      const selectedToken = selectSelectedToken(state);
      const sharedGameState = selectSharedGameState(state);
      const currentPlayer = selectCurrentPlayer(state);
      const currentPlayerCities = selectCurrentPlayerCities(state);
      const destructionToolActive = selectDestructionToolActive(state);
      const pendingBuildings = selectPendingBuildings(state);
      const players = selectPlayers(state);
      const units = selectUnits(state);
      const citiesCache = selectCitiesCache(state);
      const pendingRecruitmentActions = selectPendingRecruitmentActions(state);
      const invalidBuildings = selectInvalidPendingBuildings(state);
      const nextInitiativePlayerId = selectNextInitiativePlayerId(state);
      const specialBuildings = selectSpecialBuildings(state)
      const startingQuarters = selectStartingQuarters(state)
      const buildingsDamage = selectBuildingsDamage(state);

      const actingPlayer = getActingPlayer(gameType, userId, players, currentPlayer);

      // Матч окончен: играть уже нечем, но поле остается на экране, и итоги
      // разбирают по нему — клик уходит в осмотр ячейки и дальше не идет.
      // Раньше клик после конца партии не делал вообще ничего
      if (gameResults) {
        this.handleInspectionClick(closestHex, state, actingPlayer);
        return;
      }

      // Для пользователей, которые являются зрителями
      if (sharedGameState.isStarted && !actingPlayer) {
        return;
      }

      // Одновременная фаза снабжения сетевой игры — тоже одновременный этап:
      // ход тут не у текущего игрока (он лишь якорь хода), а у каждого, кто еще
      // не закончил свою часть фазы (см. ветку фазы снабжения ниже)
      if (sharedGameState.isStarted && !checkIfParallelTurn(phase, warPhaseStage, selectIsParallelSupply(state))) {
        if (!checkIfCurrentUserTurn(gameType, userId, currentPlayer)) {
          // Исключение из инспекции — отряд, уже выстреливший в этом этапе:
          // расчет урона (SelectedUnitShooting) интересен всем участникам
          // партии, не только игроку, чей сейчас ход — сама кнопка переброса
          // внутри компонента и так ограничена настоящим владельцем отряда
          // (checkIfCurrentUserOwner), а не ходом, так что открыть ее по клику
          // здесь безопасно для любого игрока
          if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.SHOOTING) {
            const clickedUnit = units.find((unit) => unit.hexKey === closestHex.key);
            const pendingShootingActions = selectPendingShootingActions(state);
            if (clickedUnit && pendingShootingActions.some((action) => action.shooterUnitId === clickedUnit.id)) {
              // Активная инспекция другой ячейки имеет приоритет в футере
              // (TokensToolbar) — без сброса SelectedUnitShooting не покажется
              this.store.dispatch(clearInspectedHex());
              this.store.dispatch(selectUnit(clickedUnit.id));
              // Звук зависит от самого отряда (см. SoundManager.playUnitSelect);
              // «свой» здесь считается относительно ДЕЙСТВУЮЩЕГО пользователя,
              // а не ходящего игрока
              this.soundManager.playUnitSelect(clickedUnit.type, clickedUnit.ownerId, actingPlayer, clickedUnit);
              return;
            }
          }
          // Тот же карман-исключение, что выше для стрельбы — отряд, уже
          // применивший способность в этом этапе (соблазнение у Суккубов,
          // любая из трёх способностей у Гоблинов - общий флаг
          // hasUsedAbilityThisWarPhase, см. configs/units.js), должен
          // показывать разбор формулы ЛЮБОМУ игроку (спека succubes-seduction,
          // Requirement «Просмотр формулы расчёта любым игроком»; design.md
          // Decision 25 для Гоблинов), а не только ходящему сейчас. Какой
          // именно футер показать (SelectedUnitSeduction/SelectedUnitGoblinsAbility)
          // решает TokensToolbar.js по factionId уже выбранного юнита - здесь
          // достаточно просто выбрать отряд
          if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.ABILITIES) {
            const clickedUnit = units.find((unit) => unit.hexKey === closestHex.key);
            if (clickedUnit && clickedUnit.hasUsedAbilityThisWarPhase) {
              this.store.dispatch(clearInspectedHex());
              this.store.dispatch(selectUnit(clickedUnit.id));
              this.soundManager.playUnitSelect(clickedUnit.type, clickedUnit.ownerId, actingPlayer, clickedUnit);
              return;
            }
          }
          // План строительства: в чужой ход фазы строительства клик по полю
          // размечает запланированное строение (или снимает его), а не
          // инспектирует ячейку
          if (phase === PHASES.CONSTRUCTION && this.handleConstructionPlanningClick(closestHex, state)) {
            return;
          }
          this.handleInspectionClick(closestHex, state, actingPlayer);
          return;
        }
      }

      // Handle destruction tool - remove token and any units from clicked cell
      const hasUnitsOnClosestHex = units.some((unit) => unit.hexKey === closestHex.key);
      if (destructionToolActive && phase === PHASES.FIELD_PREPARATION
        && (hexes[closestHex.key] || hasUnitsOnClosestHex)) {
        console.log('Destroying token/units:', hexes[closestHex.key], 'from cell:', closestHex.key);
        this.store.dispatch(clearHex(closestHex.key));
        return;
      }

      // Для снятия выбора инспектируемой ячейки кликом в игровое действие
      // (сравнивается с состоянием после обработки клика ниже)
      const inspectedHexKeyBeforeClick = selectInspectedHexKey(state);

      if (!sharedGameState.isStarted) {
        if (!selectedToken) {
          this.store.dispatch(showToast(
            'Выберите жетон.',
            'info',
            3000));
          return;
        }

        // Редактор миссий: отряд конкретного игрока — токен вида
        // mission_unit:<цвет>:<тип> (см. MissionEditorToolbars)
        if (selectedToken && selectedToken.startsWith(MISSION_UNIT_TOKEN_PREFIX)) {
          const existingUnit = sharedGameState.units?.find(u => u.hexKey === closestHex.key);
          if (existingUnit) {
            this.store.dispatch(showToast('Ячейка уже занята отрядом.', 'warning', 3000));
            return;
          }
          const [, ownerColor, unitType] = selectedToken.split(':');
          this.store.dispatch(placeMissionUnit(
            closestHex.key,
            unitType,
            createRandomId(ID_PREFIX_FOR_UNIT),
            ownerColor
          ));
          return;
        }

        // Проверяем, является ли выбранный токен нейтральным отрядом
        if (selectedToken && selectedToken.startsWith(UNIT_ID_PREFIX)) {
          // Проверяем, нет ли уже юнита на этой ячейке
          const existingUnit = sharedGameState.units?.find(u => u.hexKey === closestHex.key);
          if (existingUnit) {
            this.store.dispatch(showToast(
              'Ячейка уже занята отрядом.',
              'warning',
              3000));
            return;
          }
          const newUnitId = createRandomId(ID_PREFIX_FOR_UNIT);
          this.store.dispatch(placeNeutralUnit(closestHex.key, selectedToken, newUnitId));
        } else {
          this.store.dispatch(setHexToken(closestHex.key, selectedToken));
        }
      } else if (sharedGameState.phase === PHASES.SETUP) {
        // Расстановка идет у всех разом (checkIfParallelTurn), поэтому проверка
        // хода выше зрителя не остановила. Своего флага у него нет, и обработчик
        // отвечал бы ему подсказками игроку («выберите ячейку с флагом своего
        // цвета») на каждый клик — зрителю доступен только осмотр ячейки
        if (actingPlayer.id === SPECTATOR_ID) {
          this.handleInspectionClick(closestHex, state, actingPlayer);
        } else {
          this.handleSetupPhaseClick(actingPlayer, hexes, closestHex, startingQuarters);
        }
      } else if (sharedGameState.phase === PHASES.CONSTRUCTION) {
        this.handleConstructionPhaseClick(selectedToken, round, state, sharedGameState, pendingBuildings, nextInitiativePlayerId, closestHex, invalidBuildings);
      } else if (sharedGameState.phase === PHASES.SUPPLY) {
        if (selectIsParallelSupplyPhase(state)) {
          // Одновременная фаза снабжения: клик действует за игрока этого
          // клиента (сам пользователь или бот, которым он управляет как админ),
          // пока тот не закончил свою часть фазы — с его городами, фермами и
          // снабжением. Закончившему игроку и зрителю раздавать нечего — клик
          // только осматривает ячейку
          const supplyPlayer = selectSupplyActingPlayer(state);
          if (!supplyPlayer) {
            this.handleInspectionClick(closestHex, state, actingPlayer);
            return;
          }
          const supplyPlayerCities = citiesCache?.[supplyPlayer.color] || [];
          this.handleSupplyPhaseClick(units, closestHex, supplyPlayer, hexes, specialBuildings, supplyPlayerCities, buildingsDamage, state, players, citiesCache);
        } else {
          this.handleSupplyPhaseClick(units, closestHex, currentPlayer, hexes, specialBuildings, currentPlayerCities, buildingsDamage, state, players, citiesCache);
        }
      } else if (sharedGameState.phase === PHASES.WAR && sharedGameState.warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT) {
        this.handleRecruitmentStageClick(closestHex, currentPlayer, units, pendingRecruitmentActions, selectedToken, state, hexes, specialBuildings, buildingsDamage);
      } else if (sharedGameState.phase === PHASES.WAR && sharedGameState.warPhaseStage === WAR_PHASE_STAGES.MOVEMENT) {
        // Обработка перемещения отрядов в фазе войны на этапе перемещения
        this.handleMovementPhaseClick(closestHex, state, currentPlayer);
      } else if (sharedGameState.phase === PHASES.WAR && sharedGameState.warPhaseStage === WAR_PHASE_STAGES.ABILITIES) {
        // Обработка этапа способностей (соблазнение Суккубов) в фазе войны
        this.handleAbilitiesPhaseClick(closestHex, state, currentPlayer);
      } else if (sharedGameState.phase === PHASES.WAR && sharedGameState.warPhaseStage === WAR_PHASE_STAGES.SHOOTING) {
        // Обработка этапа стрельбы в фазе войны
        this.handleShootingPhaseClick(closestHex, state, currentPlayer);
      } else if (sharedGameState.phase === PHASES.WAR && sharedGameState.warPhaseStage === WAR_PHASE_STAGES.COMBAT) {
        this.handleCombatPhaseClick(closestHex, state, currentPlayer, actingPlayer);
      } else if (sharedGameState.phase === PHASES.WAR && sharedGameState.warPhaseStage === WAR_PHASE_STAGES.MANEUVER) {
        // Обработка перемещения отрядов в фазе войны на этапе манёвра
        this.handleManeuverPhaseClick(closestHex, state, currentPlayer);
      }

      // Клик, ушедший в игровое действие (не в инспекцию), снимает текущий
      // выбор инспектируемой ячейки — панель в футере уступает место фазовому
      // UI. Если клик обработала инспекция, выбор уже либо сменился на другую
      // ячейку, либо снялся повторным кликом — здесь ничего делать не нужно.
      // Отложенный осмотр этой ячейки (см. deferInspectionClick) — тоже
      // инспекция, просто позже: выбор он сменит сам
      const inspectedHexKeyAfterClick = selectInspectedHexKey(this.store.getState());
      const isInspectionDeferred = this.deferredInspectionHexKey === closestHex.key;
      if (inspectedHexKeyBeforeClick && inspectedHexKeyAfterClick === inspectedHexKeyBeforeClick && !isInspectionDeferred) {
        this.store.dispatch(clearInspectedHex());
      }
    }
  }

  // Клик по полю при планировании строительства: чужой ход фазы
  // строительства, а свой ход в ней у игрока еще впереди (см.
  // getConstructionPlanContext). Строение попадает только в план игрока —
  // никаких действий на сервер. Жесты — как в свой ход: одинарный клик ставит
  // строение (или объясняет, почему нельзя), двойной снимает запланированное
  // (tryUndoPlannedConstructionOnHex). false — клик не про план: он уходит в
  // обычную инспекцию ячейки
  handleConstructionPlanningClick(closestHex, state) {
    if (!checkIfConstructionPlanningAvailable(state)) {
      return false;
    }
    const hexKey = closestHex.key;
    const undoGestureFirstClick = this.registerUndoDoubleClick(hexKey, state);
    if (undoGestureFirstClick && this.tryUndoPlannedConstructionOnHex(hexKey, state, undoGestureFirstClick)) {
      return true;
    }

    // На ячейке есть запланированное — клик может оказаться первым в паре
    // двойного клика его снятия: тост и осмотр ячейки откладываются на окно
    // двойного клика (см. deferFirstClickEffect). Постановка поверх — нет, как
    // и в свой ход: ее снимет сама отмена, если второй клик последует
    const isPossibleUndoGestureStart = selectConstructionPlanEntries(state).some((entry) => entry.hexKey === hexKey);
    const decision = decideConstructionPlanningClick(state, hexKey);

    switch (decision.type) {
      // Звук тот же, что у настоящей стройки: игрок размечает будущий ход, и
      // на слух это должно быть строительство, а не выбор жетона в панели. И
      // анимации дохода те же: запланированной гильдии или храма, гильдий и
      // храмов, чей доход изменило запланированное, и роста города со звуком
      // захвата рудников и руин — на поле «с планом»
      case CONSTRUCTION_PLANNING_CLICK_ACTIONS.ADD:
        this.store.dispatch(addConstructionPlanEntry(hexKey, decision.token));
        // Запланированный жетон прилетает сверху так же, как в свой ход, и
        // звук постройки звучит в момент его приземления; тогда же над ячейкой
        // всплывает число монет, на которое уменьшился баланс в шапке
        playPlannedBuildingPlacementEffects(this.store, this.hexGridRenderer, hexKey, decision.token, {
          playPlacementSound: () => this.soundManager.playBuildingPlacement(decision.token, decision.priceCoins),
          price: decision.priceCoins,
        });
        playPlanPlacementIncomeAnimations(
          this.store, this.soundManager, this.hexGridRenderer,
          {hexKey, token: decision.token}, selectConstructionPlanEntries(state)
        );
        break;
      case CONSTRUCTION_PLANNING_CLICK_ACTIONS.TOAST:
        if (isPossibleUndoGestureStart) {
          this.showDeferredClickToast(decision.message, 'error', 5000);
        } else {
          this.store.dispatch(showToast(decision.message, 'warning', 5000));
        }
        break;
      default:
        if (!isPossibleUndoGestureStart) {
          return false;
        }
        this.deferInspectionClick(closestHex, getActingPlayerFromState);
        return true;
    }

    // Как и клик в игровое действие (см. конец handleClick): выбор
    // инспектируемой ячейки снимается, футер уступает место плану
    if (selectInspectedHexKey(state)) {
      this.store.dispatch(clearInspectedHex());
    }
    return true;
  }

  // Двойной клик по ячейке с запланированным — снятие верхнего запланированного
  // строения ячейки, как точечная отмена стройки своего хода
  // (tryUndoConstructionOnHex): снимается только запланированное ДО пары, и не
  // снимается то, без чего не встало бы запланированное позже. Поставленное
  // первым кликом пары поверх снимаемого снимается вместе с ним.
  // Возвращает true, если клик ушел в снятие
  tryUndoPlannedConstructionOnHex(hexKey, state, firstClick) {
    const undo = findConstructionPlanUndo(firstClick.state, hexKey);
    if (!undo || !selectConstructionPlanEntries(state).includes(undo.entry)) {
      return false;
    }
    if (!this.checkIfUndoGestureAvailable(state)) {
      return false;
    }
    this.cancelDeferredFirstClickEffect();

    const entriesBeforePair = selectConstructionPlanEntries(firstClick.state);
    selectConstructionPlanEntries(state)
      .filter((entry) => entry.hexKey === hexKey && !entriesBeforePair.includes(entry))
      .forEach(() => this.store.dispatch(removeConstructionPlanEntry(hexKey)));

    if (undo.conflictEntry) {
      this.store.dispatch(showToast(
        buildTextPlanUndoConflict(getTokenConfig(undo.conflictEntry.token)?.label),
        'warning',
        5000
      ));
      this.soundManager.playToastError();
      return true;
    }

    // Верхнее запланированное ячейки — теперь именно снимаемое
    this.store.dispatch(removeConstructionPlanEntry(hexKey));
    this.soundManager.playUndoClick();
    return true;
  }

  // Клик по полю во время чужого хода: вместо тоста «Дождитесь окончания
  // хода...» — выбор ячейки для просмотра информации о ее сущностях в футере
  // (жетон, владелец, доход, отряд). Подсветка и пути влияния гильдии/храма
  // отрисовываются через updateInspectedHexMarker при перерисовке сцены
  handleInspectionClick(closestHex, state, actingPlayer) {
    const hexKey = closestHex.key;
    const inspectedHexKey = selectInspectedHexKey(state);

    // Повторный клик по выбранной ячейке — деселект
    if (inspectedHexKey === hexKey) {
      this.store.dispatch(clearInspectedHex());
      return;
    }

    const hexes = selectHexes(state);
    const specialBuildings = selectSpecialBuildings(state);
    const units = selectUnits(state);

    const specialBuilding = specialBuildings[hexKey];
    const unit = units.find((u) => u.hexKey === hexKey);

    if (unit) {
      // И свой отряд, и отряд соперника звучат по своему облику, нейтральный —
      // общим звуком чужого (см. SoundManager.playUnitSelect)
      this.soundManager.playUnitSelect(unit.type, unit.ownerId, actingPlayer, unit);
    } else if (specialBuilding) {
      this.soundManager.playSpecialBuildingPlacement(specialBuilding);
    } else {
      this.soundManager.playTokenClickCommon();
    }

    this.store.dispatch(setInspectedHex(hexKey));

    // Осмотр в снабжении — отказ от автовыбора фермы: она не выделится сама, когда панель осмотра закроют
    const supplyPlayer = selectPhase(state) === PHASES.SUPPLY ? selectSupplyActingPlayer(state) : null;
    if (supplyPlayer) {
      this.store.dispatch(declineSupplyAutoFarm(getSupplyTurnKey(selectRound(state), supplyPlayer.id)));
    }

    // Для гильдии/храма — та же разовая анимация путей влияния и дохода, что и
    // при клике на них в свою фазу строительства. Вызывается после диспатча:
    // анимационные маркеры не регистрируются в hex.markers (register: false) и
    // потому переживают перерисовки стора, доигрывая поверх обновленной сцены
    if (specialBuilding === SPECIAL_BUILDING_TOKENS.GUILD || specialBuilding === SPECIAL_BUILDING_TOKENS.TEMPLE) {
      // citiesCache стора пересчитывается только на COMPLETE_PHASE — в течение
      // одного хода строительства он не видит города/кварталы, расширенные
      // более ранними действиями этого же хода (см.
      // playNearbyGuildsTemplesIncomeChangeAnimation). Считаем заново из
      // актуальных hexes/specialBuildings, а не читаем устаревший стор
      this.hexGridRenderer.showSpecialBuildingIncomeAnimation(
        hexKey,
        specialBuilding,
        selectPlayers(state),
        calculateCitiesCache(hexes, specialBuildings),
        units,
        hexes,
        specialBuildings,
        selectBuildingsDamage(state),
        selectCurrentPlayer(state)
      );
    }

    // Для фермы — та же разовая анимация сети связей снабжения, что и при ее
    // постройке. Зовется на осмотр ЛЮБОЙ ячейки: сеть прежней осмотренной
    // фермы гаснет, чтобы ее связи не читались как связи новой ячейки
    playInspectedFarmSupplyNetworkAnimation(this.store, this.hexGridRenderer, hexKey);
  }

  // Двойной клик по одной и той же ячейке в пределах одного этапа — жест
  // точечной отмены действия (снабжение, строительство, найм, перемещение,
  // маневр).
  // Возвращает первый клик пары ({hexKey, time, state} — стор на момент того
  // клика) или null. Пара расходуется целиком: третий быстрый клик начинает
  // новую пару, а не отменяет следующее действие
  registerUndoDoubleClick(hexKey, state) {
    const now = Date.now();
    const stageKey = `${selectPhase(state)}:${selectWarPhaseStage(state)}`;
    const previousClick = this.lastUndoGestureClick;
    const isDoubleClick = Boolean(previousClick)
      && previousClick.hexKey === hexKey
      && previousClick.stageKey === stageKey
      && now - previousClick.time < UNDO_DOUBLE_CLICK_MS;

    this.lastUndoGestureClick = isDoubleClick ? null : {hexKey, stageKey, time: now, state};
    return isDoubleClick ? previousClick : null;
  }

  // Эффект первого клика, который может оказаться половиной двойного клика
  // отмены (тост, модалка ремонта, осмотр ячейки): откладывается на окно
  // двойного клика и снимается, если пара состоялась. Клик по другой ячейке
  // второй половиной пары не бывает — тогда отложенный эффект выполняется
  // сразу, до нового клика (см. handleClick), чтобы эффекты шли в порядке кликов
  deferFirstClickEffect(effect) {
    this.flushDeferredFirstClickEffect();
    // Там, где жест отмены закрыт (обучение), второго клика-отмены не будет —
    // откладывать нечего
    if (!this.checkIfUndoGestureAvailable(this.store.getState())) {
      effect();
      return;
    }
    this.deferredFirstClickEffect = effect;
    this.deferredFirstClickTimer = setTimeout(() => this.flushDeferredFirstClickEffect(), UNDO_DOUBLE_CLICK_MS);
  }

  flushDeferredFirstClickEffect() {
    const effect = this.deferredFirstClickEffect;
    this.cancelDeferredFirstClickEffect();
    effect?.();
  }

  cancelDeferredFirstClickEffect() {
    clearTimeout(this.deferredFirstClickTimer);
    this.deferredFirstClickTimer = null;
    this.deferredFirstClickEffect = null;
    this.deferredInspectionHexKey = null;
  }

  showDeferredClickToast(message, type, duration) {
    this.deferFirstClickEffect(() => this.store.dispatch(showToast(message, type, duration)));
  }

  // Осмотр ячейки по клику, который может оказаться первым в паре двойного
  // клика отмены: иначе в футере мелькнула бы панель ячейки и прозвучал бы звук
  // выбора, а второй клик тут же сменил бы их отменой. Пара состоялась —
  // осмотра нет; нет — ячейка осматривается по свежему стору. resolveActingPlayer
  // — от чьего лица осмотр: в свой ход это ходящий, при планировании и в
  // одновременной фазе снабжения — сам пользователь
  // isUnitDeselected — снять и выбранный отряд: выбор приоритетнее осмотра
  // (оверлей обстрела показал бы зону выбранного, см. resolveShootingZoneUnit)
  deferInspectionClick(closestHex, resolveActingPlayer = selectCurrentPlayer, isUnitDeselected = false) {
    this.deferFirstClickEffect(() => {
      if (isUnitDeselected) {
        this.store.dispatch(deselectUnit());
      }
      const state = this.store.getState();
      this.handleInspectionClick(closestHex, state, resolveActingPlayer(state));
    });
    // Осмотр мог выполниться сразу (в обучении жест отмены закрыт) — тогда
    // метка отложенного осмотра не нужна
    if (this.deferredFirstClickEffect) {
      this.deferredInspectionHexKey = closestHex.key;
    }
  }

  // Модалка ремонта, отложенная на окно двойного клика: к моменту открытия
  // могли смениться ход или фаза, а сама поломка — уйти (например, ремонт
  // отменили кнопкой) — тогда открывать нечего
  deferRepairBuildingModal(hexKey, playerId) {
    this.deferFirstClickEffect(() => {
      const state = this.store.getState();
      const currentPlayer = selectCurrentPlayer(state);
      if (selectPhase(state) !== PHASES.CONSTRUCTION || currentPlayer?.id !== playerId) {
        return;
      }
      const citiesCache = calculateCitiesCache(selectHexes(state), selectSpecialBuildings(state));
      if (!(selectBuildingsDamage(state)[hexKey] > 0) || !checkIfOwner(hexKey, currentPlayer.color, citiesCache)) {
        return;
      }
      this.soundManager.playMenuButtonClick();
      this.store.dispatch(showRepairBuildingModal(hexKey, playerId));
    });
  }

  // Отмена жестом подчиняется тем же правилам обучения, что и кнопка отмены
  // (UndoButton): в обучении — только на шаге, который сам просит откатить
  // действие. Там, где отмена закрыта, жеста нет вовсе: двойной клик — это
  // просто два обычных клика, без подсказки (о ней не просили, в отличие от
  // нажатия кнопки отмены) и без отложенных эффектов первого клика
  checkIfUndoGestureAvailable(state) {
    return !selectIsTutorialActive(state) || checkIfTutorialUndoAllowed(state);
  }

  // Двойной клик по ячейке с постройкой или ремонтом этого хода — отмена
  // последнего действия строительства в этой ячейке без отката всех более
  // поздних. Отменяется только действие, сделанное ДО пары: двойной клик по
  // пустой ячейке по привычке — это постройка, а не постройка с отменой. Если
  // же первый клик пары успел поставить что-то поверх отменяемого (например,
  // при выбранной крепости), это снимается вместе с ним.
  // Возвращает true, если клик ушел в отмену
  tryUndoConstructionOnHex(hexKey, state, firstClick) {
    const pendingBeforePair = selectPendingBuildings(firstClick.state);
    const building = findHexUndoableConstruction(pendingBeforePair, hexKey);
    if (!building || !selectPendingBuildings(state).includes(building)) {
      return false;
    }

    // В обучении, где отмена закрыта, жеста нет — клик обрабатывается как обычный
    if (!this.checkIfUndoGestureAvailable(state)) {
      return false;
    }
    this.cancelDeferredFirstClickEffect();

    // Конфликт — по стору до пары: действий первого клика в нем еще нет
    const conflict = findConstructionUndoConflict(firstClick.state, building);

    // Действия первого клика пары в этой ячейке лежат поверх отменяемого —
    // снимаются первыми, с конца, даже если сама отмена не состоится: двойной
    // клик по ячейке с постройкой этого хода — жест отмены, а не постройка
    selectPendingBuildings(state)
      .filter((pending) => pending.hexKey === hexKey && !pendingBeforePair.includes(pending))
      .reverse()
      .forEach((pending) => this.store.dispatch(createUndoConstructionAction(pending)));

    if (conflict) {
      const laterLabel = getTokenConfig(conflict.building.token)?.label;
      const laterText = laterLabel ? `более позднюю — «${laterLabel}»` : 'более позднюю';
      this.store.dispatch(showToast(
        `Нельзя отменить: без этой постройки нельзя было бы построить ${laterText}.\nСначала отмените ее.`,
        'warning',
        5000
      ));
      this.soundManager.playToastError();
      return true;
    }

    this.store.dispatch(createUndoConstructionAction(building));
    // Как у кнопки отмены: красные молотки прошлой проверки после отмены уже
    // не про текущую расстановку — проверка повторится при завершении хода
    if (selectInvalidPendingBuildings(state)?.length > 0) {
      this.store.dispatch(setInvalidPendingBuildings([]));
    }
    this.soundManager.playUndoClick();
    return true;
  }

  // Двойной клик по своему отряду, уже переместившемуся на этом этапе, —
  // отмена его перемещения вне очереди. Как и в строительстве, отменяется
  // только перемещение, сделанное до первого клика пары: двойной клик по цели
  // перемещения — это само перемещение
  tryUndoUnitMovement(hexKey, state, firstClick, currentPlayer) {
    const pendingMovementActions = selectPendingMovementActions(state);
    const units = selectUnits(state);
    const unit = units.find(u => u.hexKey === hexKey && u.ownerId === currentPlayer?.id);
    // trapTriggered: true - необратимое перемещение (design.md Decision 8
    // add-goblins-faction) - отфильтровано здесь же, а не только в самом
    // reducer'е (findUndoableUnitMove), иначе двойной клик по такому отряду
    // молча "съедал" бы клик со звуком отмены, ничего не отменяя
    const movement = unit && pendingMovementActions.find(action => action.unitId === unit.id && !action.trapTriggered);
    if (!movement || !selectPendingMovementActions(firstClick.state).includes(movement)) {
      return false;
    }

    // В обучении, где отмена закрыта, жеста нет — клик обрабатывается как обычный
    if (!this.checkIfUndoGestureAvailable(state)) {
      return false;
    }
    this.cancelDeferredFirstClickEffect();

    const conflict = findMovementUndoConflict({unitId: unit.id, pendingMovementActions, units});
    if (conflict) {
      this.store.dispatch(showToast(
        'Нельзя отменить перемещение: исходную ячейку отряда уже занял другой отряд.'
        + '\nСначала отмените перемещение того отряда.',
        'warning',
        5000
      ));
      this.soundManager.playToastError();
      return true;
    }

    this.store.dispatch(undoPendingMovementAction(unit.id));
    this.soundManager.playUndoClick();
    return true;
  }

  // То же для маневра, и причина отказа та же единственная: исходную ячейку
  // отряда уже занял другой отряд
  tryUndoUnitManeuver(hexKey, state, firstClick, currentPlayer) {
    const pendingManeuverActions = selectPendingManeuverActions(state);
    const units = selectUnits(state);
    const unit = units.find(u => u.hexKey === hexKey && u.ownerId === currentPlayer?.id);
    // trapTriggered: true - необратимый манёвр, во время которого сработала
    // ловушка Гоблинов (design.md Decision 8, тот же принцип, что и у
    // tryUndoUnitMovement) - отфильтровано здесь же, а не только в
    // UNDO_PENDING_MANEUVER_ACTION (findUndoableUnitMove), иначе двойной клик
    // по такому отряду молча "съедал" бы клик со звуком отмены, ничего не
    // отменяя
    const maneuver = unit && pendingManeuverActions.find(action => action.unitId === unit.id && !action.trapTriggered);
    if (!maneuver || !selectPendingManeuverActions(firstClick.state).includes(maneuver)) {
      return false;
    }

    // В обучении, где отмена закрыта, жеста нет — клик обрабатывается как обычный
    if (!this.checkIfUndoGestureAvailable(state)) {
      return false;
    }
    this.cancelDeferredFirstClickEffect();

    if (units.some(u => u.hexKey === maneuver.fromHexKey && u.id !== unit.id)) {
      this.store.dispatch(showToast(
        'Нельзя отменить маневр: исходную ячейку отряда уже занял другой отряд.\nСначала отмените маневр того отряда.',
        'warning',
        5000
      ));
      this.soundManager.playToastError();
      return true;
    }

    this.store.dispatch(undoPendingManeuverAction(unit.id));
    this.soundManager.playUndoClick();
    return true;
  }

  // Двойной клик по своему отряду на этапе найма — отмена последнего действия
  // найма этого отряда: докупленного размера или улучшения по одному, а в
  // последнюю очередь и самого найма (отряд исчезает). Отменяется только
  // действие, сделанное до первого клика пары: двойной клик по казарме — это
  // сам найм, а не найм с отменой
  tryUndoUnitRecruitment(hexKey, state, firstClick, currentPlayer) {
    const unit = selectUnits(state).find(u => u.hexKey === hexKey && u.ownerId === currentPlayer?.id);
    const recruitmentAction = unit
      && findUnitUndoableRecruitmentAction(selectPendingRecruitmentActions(state), unit.id);
    if (!recruitmentAction || !selectPendingRecruitmentActions(firstClick.state).includes(recruitmentAction)
      || !this.checkIfUndoGestureAvailable(state)) {
      return false;
    }

    this.cancelDeferredFirstClickEffect();

    if (checkIfUnitUpgradeUndoExceedsLimit(recruitmentAction, currentPlayer, selectUnits(state))) {
      this.store.dispatch(showToast(
        'Нельзя отменить улучшение: отрядов прежнего типа стало бы больше, чем разрешено.\nСначала отмените найм более позднего отряда этого типа.',
        'warning',
        5000
      ));
      this.soundManager.playToastError();
      return true;
    }

    this.store.dispatch(undoPendingRecruitmentAction(recruitmentAction));
    this.soundManager.playUndoClick();
    return true;
  }

  // Клик в фазе снабжения за игрока supplyPlayer — того, за кого этот клиент
  // раздает снабжение (см. selectSupplyActingPlayer): в поочередной фазе это
  // текущий игрок, в одновременной — сам пользователь, еще не закончивший свою
  // часть фазы. supplyPlayerCities — его города. В одновременной фазе каждое
  // действие несет playerId этого игрока: текущий игрок там лишь якорь хода, и
  // без playerId редьюсер отдал бы действие ему. В поочередной playerId не
  // передается — действия остаются прежними
  handleSupplyPhaseClick(units, closestHex, supplyPlayer, hexes, specialBuildings, supplyPlayerCities, buildingsDamage, state, players, citiesCache) {
    const isParallelSupplyPhase = selectIsParallelSupplyPhase(state);
    const actorPlayerId = isParallelSupplyPhase ? supplyPlayer?.id : undefined;
    // Осмотр ячейки, отложенный на окно двойного клика, выполняется по свежему
    // стору: в одновременной фазе — от лица игрока этого клиента (даже если
    // он успел закончить фазу), а не текущего игрока-якоря
    const resolveInspectionActingPlayer = isParallelSupplyPhase ? getActingPlayerFromState : undefined;
    const pendingSupply = selectSupplyPlayerPendingSupply(state, supplyPlayer);
    const selectedFarmHexKey = selectSelectedFarmHexKey(state);
    // Обработка фазы снабжения
    const isAgrarianismConversionOn = selectIsAgrarianismConversionOn(state, supplyPlayer?.id);
    const selectedFarmsForConversion = selectSelectedFarmsForConversion(state, supplyPlayer?.id);

    // Двойной клик по своему отряду — точечная отмена его снабжения: первым
    // двойным кликом снимается дополнительное снабжение, следующим — базовое
    // (для нанятого за снабжение d6 крысолюдов отмена базового убирает отряд).
    // Одиночный клик при этом отрабатывает как обычно (раздача/инспекция) —
    // второй клик пары лишь откатывает его эффект, если тот что-то раздал
    const isDoubleClick = Boolean(this.registerUndoDoubleClick(closestHex.key, state));

    // В обучении, где отмена закрыта, жеста нет — клик обрабатывается как обычный
    if (isDoubleClick && this.checkIfUndoGestureAvailable(state)) {
      const doubleClickedUnit = units.find(
        u => u.hexKey === closestHex.key && u.ownerId === supplyPlayer?.id
      );
      const hasUndoableSupply = doubleClickedUnit && pendingSupply.some(
        supply => supply.unitId === doubleClickedUnit.id && !supply.isNotUndoable
      );
      if (hasUndoableSupply) {
        // Первый клик пары мог отложить тост «ферма пуста» (отряд стоит на
        // ферме без остатка снабжения) — двойной клик состоялся, тост не нужен
        this.cancelDeferredFirstClickEffect();
        this.store.dispatch(undoUnitPendingSupply(doubleClickedUnit.id, actorPlayerId));
        this.soundManager.playUndoClick();
        return;
      }

      // Двойной клик по СВОЕЙ ферме — возврат последнего отданного с нее
      // снабжения. Проверяется после отряда: если на ферме стоит свой отряд с
      // отменяемым снабжением, двойной клик по нему отменяет именно его
      // снабжение (клик по отряду — более точное указание цели)
      const isOwnFarm = hexes[closestHex.key] === TOKENS.FARM
        && supplyPlayerCities?.some(
          city => (city[COMMON_BUILDING_TOKENS.FARM] || []).includes(closestHex.key)
        );
      const hasUndoableFarmSupply = isOwnFarm && checkIfFarmHasUndoableSupply(pendingSupply, closestHex.key);
      if (hasUndoableFarmSupply) {
        this.cancelDeferredFirstClickEffect();
        this.store.dispatch(undoFarmPendingSupply(closestHex.key, actorPlayerId));
        this.soundManager.playUndoClick();
        return;
      }
    }

    const token = hexes[closestHex.key];
    const clickedOwnUnit = units.find(
      u => u.hexKey === closestHex.key && u.ownerId === supplyPlayer?.id
    );

    // Свой отряд с отменяемым снабжением — цель двойного клика отмены: осмотр
    // его ячейки по первому клику пары откладывается (см. deferInspectionClick)
    const isUndoableSupplyUnitClick = Boolean(clickedOwnUnit) && pendingSupply.some(
      supply => supply.unitId === clickedOwnUnit.id && !supply.isNotUndoable
    );
    const inspectClickedCell = () => {
      if (isUndoableSupplyUnitClick) {
        this.deferInspectionClick(closestHex, resolveInspectionActingPlayer);
      } else {
        this.handleInspectionClick(closestHex, state, supplyPlayer);
      }
    };

    // Великаны: клик на своего великана без выбранной фермы выбирает его для
    // показа в GiantsSupplyToolbar (счётчик снабжения + кнопка конвертации) -
    // чисто отображательное действие, на раздачу снабжения кликами по фермам/
    // отрядам ниже никак не влияет. Проверка идет по фракции ЮНИТА, а не игрока -
    // сохраняет способность и у Великана, захваченного Суккубом (см.
    // unit-scoped-faction-abilities).
    // Исключение — великан стоит на своей ферме, с которой еще есть что
    // раздать: клик уходит в ветку фермы ниже, как с отрядом любой фракции.
    // Иначе ферму под великаном было не выбрать вовсе — ни раздать с нее
    // снабжение (и подсветить отряды, до которых она дотягивается), ни отметить
    // для обмена по аграризму. Повторный клик по ячейке при выбранной ферме
    // дает снабжение самому великану. Когда на ферме раздавать уже нечего (или
    // ее заблокировал противник), клик снова выбирает великана для тулбара
    const clickedOwnGiant = clickedOwnUnit && isUnitFaction(clickedOwnUnit, FACTIONS.GIANTS)
      ? clickedOwnUnit
      : null;
    const checkIfClickedOwnFarmHasSupply = () => token === TOKENS.FARM
      && getPlayerFarms(state, supplyPlayer).includes(closestHex.key)
      // > 0 отсекает и розданную до конца ферму, и заблокированную
      // (FARM_SUPPLY_BLOCKED_COUNT) — ровно те случаи, где ветка фермы ее не
      // выбрала бы
      && calcFarmRemainingSupply(closestHex.key, selectedFarmsForConversion, hexes, specialBuildings, players, units, citiesCache, supplyPlayer, pendingSupply, buildingsDamage) > 0;
    const isGiantOnSelectableOwnFarm = Boolean(clickedOwnGiant) && !selectedFarmHexKey
      && checkIfClickedOwnFarmHasSupply();
    const isGiantSelectionClick = Boolean(clickedOwnGiant) && !selectedFarmHexKey
      && !isGiantOnSelectableOwnFarm;

    if (isGiantSelectionClick) {
      this.store.dispatch(giantsSelectUnitForAdditionalSupply(clickedOwnUnit.id, actorPlayerId));
      this.soundManager.playSelectFarm();
      return;
    }

    // Выбрана (или этим кликом выбирается) ячейка, где великан стоит на
    // ферме: панель великана видна вместе с выбранной фермой — и когда клик
    // выбирает ферму под ним, и когда повторный клик отдает ему с нее
    // снабжение (счетчик «Получено» растет на глазах). Опустеет ферма — ее
    // выбор снимется сам, а великан останется выбранным. В режиме обмена по
    // аграризму панель скрыта, клик только отмечает ферму — выбор не нужен
    const giantToKeepSelected = clickedOwnGiant && !isAgrarianismConversionOn
      && (isGiantOnSelectableOwnFarm || closestHex.key === selectedFarmHexKey)
      ? clickedOwnGiant
      : null;
    const selectedGiantId = selectGiantsSelectedUnitId(state, supplyPlayer?.id);

    if (giantToKeepSelected) {
      // Выбор великана — общее состояние партии, которое уходит по сети:
      // повторно того же великана не выбираем
      if (selectedGiantId !== giantToKeepSelected.id) {
        this.store.dispatch(giantsSelectUnitForAdditionalSupply(giantToKeepSelected.id, actorPlayerId));
      }
    } else if (selectedGiantId) {
      // Любой другой клик снимает выбор великана - как и у обычной выбранной
      // фермы/отряда, клик по чему-то ещё сбрасывает предыдущий выбор.
      // selectGiantsSelectedUnitId устанавливается только для юнита-Великана
      // (см. isGiantSelectionClick выше), доп. проверка факции игрока не нужна
      this.store.dispatch(giantsResetAdditionalSupplySelection(actorPlayerId));
    }

    // Проверяем, есть ли уже выбранная ферма
    if (selectedFarmHexKey) {
      // Проверяем, кликнули ли на отряд игрока
      const clickedUnit = clickedOwnUnit;

      if (clickedUnit) {
        // Великаны могут получать базовое снабжение неограниченное число раз за отряд -
        // флаг "уже получил доп. снабжение" не должен их блокировать. Проверка по факции
        // ЮНИТА-получателя, а не игрока - сохраняет способность и у Великана, захваченного
        // Суккубом (см. unit-scoped-faction-abilities). Отряду, который больше не возьмет,
        // снабжать нечего: клик уходит в инспекцию, а двойной клик снимет его снабжение
        if (!isUnitFaction(clickedUnit, FACTIONS.GIANTS) && checkIfUnitHasPendingAdditionalSupply(clickedUnit.id, pendingSupply)) {
          inspectClickedCell();
          return;
        }

        // Вычисляем доступные для снабжения ячейки
        const availableSupplyCells = calculateAvailableSupplyCells(
          this.hexGridRenderer,
          hexes,
          specialBuildings,
          units,
          selectedFarmHexKey,
          supplyPlayer,
          supplyPlayerCities,
          buildingsDamage,
        );

        // Ферма не дотягивается до отряда, которому снабжение еще нужно. Выделенную
        // панелью ферму меняем на ту, что дотягивается (как жетон отряда в панели). Выбор
        // игрока не меняем: показываем, где снабжение обрывается
        if (!availableSupplyCells.includes(closestHex.key)) {
          const otherFarmHexKey = selectIsFarmAutoSelected(state)
            ? this.pickFarmReachingUnit(clickedUnit, state, supplyPlayer)
            : null;
          if (otherFarmHexKey) {
            this.store.dispatch(selectFarm(otherFarmHexKey, {isAuto: true}));
            this.distributeSupplyFromFarm(otherFarmHexKey, clickedUnit, closestHex.key, state, supplyPlayer, supplyPlayerCities, actorPlayerId);
          } else if (!this.showBlockedSupplyPath(selectedFarmHexKey, closestHex.key, state, supplyPlayer, supplyPlayerCities)) {
            inspectClickedCell();
          }
          return;
        }

        this.distributeSupplyFromFarm(selectedFarmHexKey, clickedUnit, closestHex.key, state, supplyPlayer, supplyPlayerCities, actorPlayerId);
        return;
      }
    }

    // Проверяем, кликнули ли на ферму
    if (token === TOKENS.FARM) {
      const playerFarms = getPlayerFarms(state, supplyPlayer)
      const isPlayerFarm = playerFarms.some(s => s === closestHex.key);

      // Чужая или заблокированная противником ферма не участвует в действиях
      // фазы (не подсвечена) — клик по ней уходит в инспекцию, панель которой
      // сама покажет владельца и пометку «Заблокировано»
      if (!isPlayerFarm) {
        this.handleInspectionClick(closestHex, state, supplyPlayer);
        return;
      }

      const farmRemainingSupply = calcFarmRemainingSupply(closestHex.key, selectedFarmsForConversion, hexes, specialBuildings, players, units, citiesCache, supplyPlayer, pendingSupply, buildingsDamage);
      if (farmRemainingSupply === FARM_SUPPLY_BLOCKED_COUNT) {
        this.handleInspectionClick(closestHex, state, supplyPlayer);
        return;
      }
      if (farmRemainingSupply <= 0) {
        // Клик может оказаться первым в паре двойного клика отмены — по своему
        // отряду с отменяемым снабжением, стоящему на ферме, или по самой
        // ферме, с которой отдано отменяемое снабжение: тогда тост
        // откладывается на окно двойного клика и гасится, если пара состоялась
        const unitOnFarm = units.find(
          u => u.hexKey === closestHex.key && u.ownerId === supplyPlayer?.id
        );
        const unitHasUndoableSupply = unitOnFarm && pendingSupply.some(
          supply => supply.unitId === unitOnFarm.id && !supply.isNotUndoable
        );
        if (unitHasUndoableSupply || checkIfFarmHasUndoableSupply(pendingSupply, closestHex.key)) {
          this.showDeferredClickToast('С этой фермы уже распределено все снабжение!', 'error', 3000);
        } else {
          this.store.dispatch(showToast('С этой фермы уже распределено все снабжение!', 'warning', 3000));
        }
        return;
      }

      if (isAgrarianismConversionOn) {
        const selectFarmAction = agrarianismSelectFarmForConversion(closestHex.key, actorPlayerId);
        if (!checkTutorialActionWouldApply(state, selectFarmAction)) {
          this.store.dispatch(selectFarmAction);
          return;
        }
        this.store.dispatch(selectFarmAction);
        this.soundManager.playDistributeSupply();
      } else {
        // Выбираем ферму
        this.store.dispatch(selectFarm(closestHex.key));
        this.soundManager.playSelectFarm();
      }
      return;
    }


    // Кликнули не на ферму и не на снабжаемый отряд — деселектируем ферму,
    // клик уходит в инспекцию ячейки
    this.store.dispatch(deselectFarm());
    inspectClickedCell();
  }

  // Снабжение отряду с фермы: действие, звук и анимация пути. Общее у клика по
  // полю и жетона панели снабжения
  distributeSupplyFromFarm(farmHexKey, unit, unitHexKey, state, supplyPlayer, supplyPlayerCities, actorPlayerId) {
    const supplyAction = distributeSupply(farmHexKey, unit.id, unitHexKey, undefined, undefined, undefined, actorPlayerId);
    // В обучении неразрешенное шагом действие блокируется гейтом (с
    // подсказкой) — звук и анимацию пути в этом случае не проигрываем
    if (!checkTutorialActionWouldApply(state, supplyAction)) {
      this.store.dispatch(supplyAction);
      return;
    }
    this.store.dispatch(supplyAction);
    this.soundManager.playDistributeSupply();
    this.showSupplyPathFromFarm(farmHexKey, unitHexKey, state, supplyPlayer, supplyPlayerCities, true);
  }

  // Анимация пути передачи снабжения от фермы к отряду — как пути влияния
  // гильдий/храмов, но цветом снабжения. Путь ищется по тем же правилам, что и
  // достижимость (включая цепочки ферм). С isTransfer по нему летит и снабжение
  showSupplyPathFromFarm(farmHexKey, unitHexKey, state, supplyPlayer, supplyPlayerCities, isTransfer) {
    // Экоанархизм: снабжение раздается без построения путей (любой отряд
    // достижим от любой фермы) — анимация пути не имеет смысла
    if (isPlayerPolicy(supplyPlayer, POLICIES.ECOANARCHISM)) {
      return;
    }

    const supplyPathContext = buildSupplyPathContext({
      fieldHexes: this.hexGridRenderer.hexes,
      hexes: selectHexes(state),
      units: selectUnits(state),
      currentPlayer: supplyPlayer,
      currentPlayerCities: supplyPlayerCities,
      specialBuildings: selectSpecialBuildings(state),
      buildingsDamage: selectBuildingsDamage(state),
    });
    const supplyPath = findSupplyPathFromFarm(farmHexKey, unitHexKey, supplyPathContext);
    if (supplyPath) {
      // Промежуточные фермы цепочки (без фермы-источника и ячейки
      // отряда) отмечаются на пути белыми кругами — только те, что
      // цепочку продлевают: чужая или заблокированная ферма на пути
      // лишь проводит снабжение, как любая застроенная ячейка
      const intermediateFarmHexKeys = findSupplyPathRelayFarms(supplyPath, supplyPathContext);
      this.hexGridRenderer.showSupplyPathAnimation(supplyPath, supplyPath.length, {intermediateFarmHexKeys});
      if (isTransfer) {
        this.hexGridRenderer.showSupplyTransfer?.(supplyPath);
      }
    }
  }

  // Другая ферма с остатком, которая дотягивается до отряда
  pickFarmReachingUnit(unit, state, supplyPlayer) {
    const model = buildSupplyPanelModel(state, supplyPlayer, this.hexGridRenderer);
    const entry = model.units.find((candidate) => candidate.unit.id === unit.id);
    return entry ? pickSupplyFarmForUnit(model, entry) : null;
  }

  /**
   * Попытка снабдить отряд с фермы, которая до него не дотягивается: перекрытый путь, как в
   * подсказке этапа перемещения
   * @returns {boolean} показан ли путь: его может не быть вовсе (горы вокруг отряда)
   */
  showBlockedSupplyPath(farmHexKey, unitHexKey, state, supplyPlayer, supplyPlayerCities) {
    const supplyPathContext = buildSupplyPathContext({
      fieldHexes: this.hexGridRenderer.hexes,
      hexes: selectHexes(state),
      units: selectUnits(state),
      currentPlayer: supplyPlayer,
      currentPlayerCities: supplyPlayerCities,
      specialBuildings: selectSpecialBuildings(state),
      buildingsDamage: selectBuildingsDamage(state),
    });
    const blockedPath = findBlockedSupplyPathFromFarm(farmHexKey, unitHexKey, supplyPathContext);
    if (!blockedPath) {
      return false;
    }
    const {path, validCellsCount} = blockedPath;
    this.hexGridRenderer.showSupplyPathAnimation(path, validCellsCount, {
      intermediateFarmHexKeys: findSupplyPathRelayFarms(path.slice(0, validCellsCount), supplyPathContext),
    });
    this.soundManager.playToastError();
    return true;
  }

  /** Жетон отряда в панели: выбранная ферма до него не дотягивается */
  showBlockedSupplyPathFromPanel(farmHexKey, unitHexKey) {
    const state = this.store.getState();
    const supplyPlayer = selectSupplyActingPlayer(state);
    if (supplyPlayer && !isFieldInputLocked(this)) {
      this.showBlockedSupplyPath(farmHexKey, unitHexKey, state, supplyPlayer, selectSupplyActingPlayerCities(state));
    }
  }

  /**
   * Жетон отряда в панели снабжения: то же, что клик по отряду на поле при выбранной ферме.
   * Выбор великана ведет себя как на поле: его панель видна, пока ему раздают
   *
   * @param {string} farmHexKey - с какой фермы (см. pickSupplyFarmForUnit)
   * @param {string} unitId
   */
  giveSupplyFromPanel(farmHexKey, unitId) {
    const state = this.store.getState();
    const supplyPlayer = selectSupplyActingPlayer(state);
    const unit = selectUnits(state).find((candidate) => candidate.id === unitId);
    if (!supplyPlayer || !unit || isFieldInputLocked(this)) {
      return;
    }
    const actorPlayerId = selectIsParallelSupplyPhase(state) ? supplyPlayer.id : undefined;
    const selectedGiantId = selectGiantsSelectedUnitId(state, supplyPlayer.id);
    if (isUnitFaction(unit, FACTIONS.GIANTS)) {
      if (selectedGiantId !== unit.id) {
        this.store.dispatch(giantsSelectUnitForAdditionalSupply(unit.id, actorPlayerId));
      }
    } else if (selectedGiantId) {
      this.store.dispatch(giantsResetAdditionalSupplySelection(actorPlayerId));
    }
    this.distributeSupplyFromFarm(farmHexKey, unit, unit.hexKey, state, supplyPlayer, selectSupplyActingPlayerCities(state), actorPlayerId);
  }

  /** Наведение на жетон отряда в панели снабжения: откуда придет снабжение */
  previewSupplyPathFromPanel(farmHexKey, unitHexKey) {
    const state = this.store.getState();
    const supplyPlayer = selectSupplyActingPlayer(state);
    if (!supplyPlayer || isFieldInputLocked(this)) {
      return;
    }
    this.showSupplyPathFromFarm(farmHexKey, unitHexKey, state, supplyPlayer, selectSupplyActingPlayerCities(state), false);
  }

  /** Панель снабжения выбрала ферму или отряд: подводим кадр, только если их не видно */
  focusCameraOnSupplyHex(hexKey) {
    if (!this.isDemoMode && !isFieldInputLocked(this)) {
      this.frameAutoSelectedUnit(hexKey);
    }
  }

  /**
   * Отряд оказался там, куда ни одна ферма игрока не дотягивается путем
   * снабжения (≤ 5 непустых ячеек от фермы; обратный поиск от ячейки отряда
   * эквивалентен — последний сегмент любой цепочки ферм это прямой путь ≤ 5
   * от какой-то фермы): показываем путь от ближайшей фермы — валидную часть
   * (докуда ферма реально дотянулась бы) цветом снабжения, остаток серым — и
   * играем звук ошибки.
   *
   * Зовется в трех местах и только у самого ходящего игрока (все вызовы живут
   * в коде, который исполняет клиент, обработавший клик): по завершении
   * анимации перемещения отряда, по завершении анимации маневра и сразу
   * после найма нового отряда на казарму (см. recruitNewUnitOnBarracks).
   *
   * В финальном раунде молчит: штраф за отсутствие снабжения списывается в
   * конце фазы снабжения, а ее в этом раунде уже не будет (см.
   * checkIfOutOfSupplyPenaltyAhead).
   *
   * @param {string} unitHexKey - ячейка, где отряд оказался
   * @param {Object} state - состояние ПОСЛЕ перемещения/маневра/найма
   * @param {number} [delayMs] - задержка перед показом: найм звучит своим
   *   звуком, и подсказке нужно дать ему отыграть, иначе два звука сходятся
   *   в кашу (у перемещения и маневра такой задержки нет — там подсказку и
   *   так отделяет анимация хода)
   */
  showSupplyOutOfRangeHintIfNeeded(unitHexKey, state, {delayMs = 0} = {}) {
    const currentPlayer = selectCurrentPlayer(state);
    if (!currentPlayer) return;

    // Инсектоиды не голодают вовсе: каждый их отряд снабжается сам, и путь до
    // ближайшей фермы им не нужен (см. checkIfPlayerUnitsNeverStarve)
    if (checkIfPlayerUnitsNeverStarve(currentPlayer)) {
      return;
    }

    // Финальный раунд: следующей фазы снабжения не будет, голодать отряду уже
    // не придется — подсказка была бы ложной тревогой
    if (!checkIfOutOfSupplyPenaltyAhead(selectRound(state), selectFinalRound(state))) {
      return;
    }

    // Экоанархизм: пути снабжения не нужны вовсе — отряд снабжаем из любой
    // точки, подсказка неуместна. Исключение: куплена ДРУГАЯ политика (со
    // следующего раунда экоанархизм перестанет действовать) — в ближайшей
    // фазе снабжения пути уже понадобятся, подсказку показываем
    if (isPlayerPolicy(currentPlayer, POLICIES.ECOANARCHISM) && !getPlayerNextPolicy(currentPlayer)) {
      return;
    }

    const supplyPathContext = buildSupplyPathContext({
      fieldHexes: this.hexGridRenderer.hexes,
      hexes: selectHexes(state),
      units: selectUnits(state),
      currentPlayer,
      currentPlayerCities: selectCurrentPlayerCities(state) || [],
      specialBuildings: selectSpecialBuildings(state),
      buildingsDamage: selectBuildingsDamage(state),
    });

    const nearestFarmInfo = findNearestFarmSupplyInfo(unitHexKey, supplyPathContext);
    // Пути к фермам нет вовсе (отряд среди пустых ячеек) — рисовать нечего;
    // ферма дотягивается — подсказка не нужна. Пути, перекрытые только
    // вражескими отрядами, findNearestFarmSupplyInfo уже вернул с ослабленным
    // поиском — подсказка в этом случае обязана показаться
    if (!nearestFarmInfo || nearestFarmInfo.isWithinSupplyRange) {
      return;
    }

    // Валидная часть — докуда ферма реально дотянулась бы по строгим правилам
    // (дальность и вражеские блокировки)
    const showHint = () => {
      this.hexGridRenderer.showSupplyPathAnimation(nearestFarmInfo.path, nearestFarmInfo.validCellsCount);
      this.soundManager.playToastError();
    };

    if (delayMs > 0) {
      // Таймер сцены, а не setTimeout: с уничтожением сцены (выход из матча
      // сразу после найма) он снимается сам
      this.time.delayedCall(delayMs, showHint);
      return;
    }
    showHint();
  }

  handleConstructionPhaseClick(selectedToken, round, state, sharedGameState, pendingBuildings, nextInitiativePlayerId, closestHex, invalidBuildings) {
    const undoGestureFirstClick = this.registerUndoDoubleClick(closestHex.key, state);
    if (undoGestureFirstClick && this.tryUndoConstructionOnHex(closestHex.key, state, undoGestureFirstClick)) {
      return;
    }

    // В ячейке уже есть действие этого хода — клик может оказаться первым в
    // паре двойного клика его отмены. Осмотр ячейки, тост ошибки и модалка
    // ремонта (ее подложка забрала бы себе второй клик пары) откладываются на
    // окно двойного клика (см. deferFirstClickEffect). Постройка поверх — нет:
    // отложенная, она терялась бы при завершении хода в это окно, а так ее
    // снимет сама отмена, если второй клик последует
    const isPossibleUndoGestureStart = Boolean(findHexUndoableConstruction(pendingBuildings, closestHex.key));

    const hexes = selectHexes(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const currentPlayer = selectCurrentPlayer(state);
    const players = selectPlayers(state);
    // Store-кэш городов пересчитывается только на COMPLETE_PHASE, поэтому в
    // течение своего хода строительства он не видит кварталы, поставленные
    // этим же ходом — строение, только что вошедшее в город, числилось бы
    // «чужим» и клик по нему не открывал бы ремонт, хотя поле уже рисует его
    // своим (HexGridRenderer.updateHexes считает кэш заново). Считаем так же
    const citiesCache = calculateCitiesCache(hexes, selectSpecialBuildings(state));

    // Если кликнули на повреждённое строение своего города — предлагаем
    // починить. Если в ячейке уже есть ремонт этого хода, модалка откроется с
    // задержкой: иначе ее подложка перехватила бы второй клик пары отмены
    if (buildingsDamage[closestHex.key] > 0 && checkIfOwner(closestHex.key, currentPlayer.color, citiesCache)) {
      if (isPossibleUndoGestureStart) {
        this.deferRepairBuildingModal(closestHex.key, currentPlayer.id);
        return;
      }
      this.soundManager.playMenuButtonClick();
      this.store.dispatch(showRepairBuildingModal(closestHex.key, currentPlayer.id));
      return;
    }

    // Все проверки размещения — в resolveConstructionPlacement: по тем же
    // правилам строения размечаются в план в чужой ход и применяются из плана в
    // начале своего хода (см. handleConstructionPlanningClick)
    const placement = resolveConstructionPlacement(state, closestHex.key);

    // Клик мимо подсвеченных ячеек строительства (белый и красный контуры) —
    // инспекция ячейки вместо ошибки. Существующие гильдии/храмы попадают
    // сюда же: разовая анимация их дохода и путей влияния проигрывается самим
    // выбором ячейки (см. handleInspectionClick)
    if (placement.kind === CONSTRUCTION_PLACEMENT_KINDS.OUTSIDE) {
      if (isPossibleUndoGestureStart) {
        this.deferInspectionClick(closestHex);
      } else {
        this.handleInspectionClick(closestHex, state, currentPlayer);
      }
      return;
    }

    if (placement.kind === CONSTRUCTION_PLACEMENT_KINDS.ERROR) {
      if (isPossibleUndoGestureStart) {
        this.showDeferredClickToast(placement.errorMessage, 'error', 5000);
      } else {
        this.store.dispatch(showToast(placement.errorMessage, 'warning', 5000));
      }
      return;
    }

    const {priceCoins, isFreeAction, elementIndex} = placement;

    if (isPlayerFaction(currentPlayer, FACTIONS.ELEMENTALS) && elementIndex !== null) {
      this.store.dispatch(elementalsSpendElement(elementIndex))
    }

    // Если выбрано специальное строение - размещаем его через setSpecialBuilding
    if (placement.kind === CONSTRUCTION_PLACEMENT_KINDS.SPECIAL) {
      const specialBuildingAction = setSpecialBuilding(closestHex.key, selectedToken);
      // Гейт обучения: заблокированное размещение не должно звучать
      if (!checkTutorialActionWouldApply(state, specialBuildingAction)) {
        this.store.dispatch(specialBuildingAction);
        return;
      }
      const specialBuildingsBefore = selectSpecialBuildings(state);
      this.store.dispatch(specialBuildingAction);
      // Прилет жетона, звук постройки, пыль и число потраченных монет: у
      // спецстроений свой путь размещения, общая воронка эффектов их не видит
      playSpecialBuildingPlacementEffects(this.store, this.hexGridRenderer, closestHex.key, selectedToken, {
        specialBuildingsBefore,
        playPlacementSound: () => this.soundManager.playBuildingPlacement(selectedToken, priceCoins),
        price: priceCoins,
      });

      if (selectedToken === SPECIAL_BUILDING_TOKENS.GUILD || selectedToken === SPECIAL_BUILDING_TOKENS.TEMPLE) {
        setTimeout(() => {
          const newState = this.store.getState();
          // Постройку могли снять за эту секунду: двойной клик по ячейке с
          // постройкой этого хода снимает и то, что поставил его первый клик
          if (selectSpecialBuildings(newState)[closestHex.key] !== selectedToken) {
            return;
          }
          const newCurrentPlayer = selectCurrentPlayer(newState);
          const newPlayers = selectPlayers(newState);
          const newUnits = selectUnits(newState);
          const newHexes = selectHexes(newState);
          const newSpecialBuildings = selectSpecialBuildings(newState);
          const newBuildingsDamage = selectBuildingsDamage(newState);
          // Размещение специального строения не трогает citiesCache стора
          // (он пересчитывается только на COMPLETE_PHASE) — считаем заново,
          // иначе только что построенная гильдия/храм не найдется в чужом
          // (устаревшем) кэше городов вовсе
          const newCitiesCache = calculateCitiesCache(newHexes, newSpecialBuildings);
          this.hexGridRenderer.showSpecialBuildingIncomeAnimation(
            closestHex.key, selectedToken, newPlayers, newCitiesCache, newUnits, newHexes, newSpecialBuildings, newBuildingsDamage, newCurrentPlayer
          );
        }, 1000);
      }

      return;
    } else if (placement.kind === CONSTRUCTION_PLACEMENT_KINDS.MINE_EXPLORATION) {
      const validationResult = validatePendingBuildings(pendingBuildings, hexes, currentPlayer, players, citiesCache, buildingsDamage, selectSpecialBuildings(state));

      if (!validationResult.isValid) {
        // Есть невалидные строения - показываем ошибку и сохраняем их для отображения красных молотков
        const invalidHexKeys = validationResult.invalidBuildings.map(b => b.hexKey);
        this.store.dispatch(setInvalidPendingBuildings(invalidHexKeys));

        const errorMessages = [
          `Новые строения размещены некорректно:`,
        ];
        validationResult.invalidBuildings.forEach(invalidBuilding => {
          invalidBuilding.validationErrors.forEach((error) => {
            if (!errorMessages.includes(error)) {
              errorMessages.push(error)
            }
          })
        });

        const invalidDescriptions = errorMessages.join('\n• ');

        this.store.dispatch(showToast(invalidDescriptions, 'warning', 5000))
        return;
      }
      // Кликает живой игрок: у него подкруток нет (getBotD6MinRoll и
      // getBotD6RollBonus дают их только явному и локальному боту). Бот сюда попадает, только когда
      // им управляет админ (см. getActingPlayer), — тогда кости бота остаются
      // при нем, как и во всех остальных точках броска d6
      const diceResult = getD6Roll(getBotD6MinRoll(currentPlayer), getBotD6RollBonus(currentPlayer));
      let mineToken = diceResult > 4 ? KEY_POINT_TOKENS.SUPER_MINE : KEY_POINT_TOKENS.MINE;

      const mineTokenAction = setHexToken(closestHex.key, mineToken, priceCoins, true);
      if (!checkTutorialActionWouldApply(state, mineTokenAction)) {
        this.store.dispatch(mineTokenAction);
        return;
      }
      this.store.dispatch(mineTokenAction);
      // Звук постройки зазвучит, когда жетон коснется земли; тогда же над
      // ячейкой всплывет число потраченных монет
      playBuildingPlacementEffects(this.store, this.soundManager, this.hexGridRenderer, closestHex.key, hexes, undefined, {
        playPlacementSound: () => this.soundManager.playBuildingPlacement(mineToken, priceCoins),
        price: priceCoins,
      });
      return;
    }

    // Размещение токена
    const setTokenAction = setHexToken(closestHex.key, selectedToken, priceCoins, false, isFreeAction);
    if (!checkTutorialActionWouldApply(state, setTokenAction)) {
      this.store.dispatch(setTokenAction);
      return;
    }
    this.store.dispatch(setTokenAction);
    // Звук постройки зазвучит, когда жетон коснется земли; тогда же над
    // ячейкой всплывет число потраченных монет
    playBuildingPlacementEffects(this.store, this.soundManager, this.hexGridRenderer, closestHex.key, hexes, undefined, {
      playPlacementSound: () => this.soundManager.playBuildingPlacement(selectedToken, priceCoins),
      price: priceCoins,
    });
    if (invalidBuildings && invalidBuildings.length > 0) {
      this.store.dispatch(setInvalidPendingBuildings([]));
    }
  }

  handleSetupPhaseClick(actingPlayer, hexes, closestHex, startingQuarters) {
    const flagToken = `flag_${actingPlayer.color}`;

    // Проверяем, есть ли на клетке флаг того же цвета
    if (hexes[closestHex.key] !== flagToken) {
      if (startingQuarters[actingPlayer.id]) {
        this.store.dispatch(showToast(
          `Вы уже разместили стартовый квартал на поле.\n\nГотовы подтвердить ${MINUS_SIGN} нажмите на кнопку завершения хода.\n\nХотите измените выбор ${MINUS_SIGN} нажмите на пустую ячейку с флагом своего цвета.`,
          'info',
          5000));
        return;
      }

      this.store.dispatch(showToast(
        'Выберите ячейку с флагом своего цвета',
        'info',
        3000));
      return;
    }

    const startingQuarterAction = setStartingQuarter(actingPlayer.id, closestHex.key);
    if (!checkTutorialActionWouldApply(this.store.getState(), startingQuarterAction)) {
      this.store.dispatch(startingQuarterAction);
      return;
    }
    this.store.dispatch(startingQuarterAction);
    // Квартал прилетает на ячейку сверху, как и строения: звук и пыль — в
    // момент касания земли. Затем город, как после постройки центрального
    // квартала, показывает доход своих рудников и руин со звуком их захвата
    playStartingQuarterPlacementEffects(this.store, this.hexGridRenderer, closestHex.key, {
      playerId: actingPlayer.id,
      color: actingPlayer.color,
      playPlacementSound: () => this.soundManager.playStartingQuarterPlacement(),
      soundManager: this.soundManager,
    });
  }

  // Клик по отряду на этапе найма: свой отряд на работающей казарме
  // выбирается под докупку размера, прочие — осматриваются
  handleRecruitmentStageUnitClick(closestHex, unitOnHex, state, currentPlayer, units, hexes, specialBuildings, buildingsDamage) {
    this.store.dispatch(deselectUnit());
    // Ячейка с отрядом под найм НОВОГО отряда не годится — выбранная ранее
    // казарма снимается, чем бы клик ни закончился (инспекцией или выбором
    // своего отряда под докупку размера)
    this.store.dispatch(deselectBarracksForRecruitment());

    // Действие этапа — только свой отряд на РАБОТАЮЩЕЙ казарме (докупка
    // размера); клики по прочим отрядам уходят в инспекцию ячейки
    const isOwnUnitOnWorkingBarracks = unitOnHex.ownerId === currentPlayer.id
      && hexes[closestHex.key] === TOKENS.BARRACKS
      && !checkIfHexBlockedByEnemyUnit(closestHex.key, TOKENS.BARRACKS, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage);
    if (!isOwnUnitOnWorkingBarracks) {
      this.handleInspectionClick(closestHex, state, currentPlayer);
      return;
    }

    // Выбираем отряд на казарме для найма размера с кварталов города, в котором расположена эта казарма
    // Сбрасываем выбранный тип отряда из тулбара
    this.store.dispatch(setSelectedToken(null));
    this.store.dispatch(selectUnit(unitOnHex.id));
    this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, currentPlayer, unitOnHex)
  }

  // Тот же клик по отряду, отложенный на окно двойного клика: у отряда есть
  // действия найма этого хода, и клик может оказаться первым в паре их
  // отмены — выбор отряда со звуком тогда не нужен. Выполняется по свежему
  // стору, если за окно не сменились этап и ход, а отряд остался на месте
  deferRecruitmentStageUnitClick(closestHex, playerId) {
    // Бесшумная часть клика — сразу, как и раньше: иначе тип отряда,
    // выбранный в футере за окно, лег бы на еще выбранную казарму
    this.store.dispatch(deselectBarracksForRecruitment());
    this.store.dispatch(setSelectedToken(null));
    const tokenAtClick = selectSelectedToken(this.store.getState());
    const selectedUnitIdAtClick = selectSelectedUnitId(this.store.getState());

    this.deferFirstClickEffect(() => {
      const state = this.store.getState();
      const currentPlayer = selectCurrentPlayer(state);
      if (selectPhase(state) !== PHASES.WAR || selectWarPhaseStage(state) !== WAR_PHASE_STAGES.RECRUITMENT
        || currentPlayer?.id !== playerId) {
        return;
      }
      // За окно игрок уже что-то выбрал в футере (тип отряда, другой отряд) —
      // отложенный выбор этого отряда такой выбор не затирает
      if (selectSelectedToken(state) !== tokenAtClick || selectSelectedUnitId(state) !== selectedUnitIdAtClick) {
        return;
      }
      const units = selectUnits(state);
      const unitOnHex = units.find(u => u.hexKey === closestHex.key);
      if (!unitOnHex) {
        return;
      }
      this.handleRecruitmentStageUnitClick(
        closestHex, unitOnHex, state, currentPlayer, units, selectHexes(state), selectSpecialBuildings(state), selectBuildingsDamage(state)
      );
    });
  }

  handleRecruitmentStageClick(closestHex, currentPlayer, units, pendingRecruitmentActions, selectedToken, state, hexes, specialBuildings, buildingsDamage) {
    const undoGestureFirstClick = this.registerUndoDoubleClick(closestHex.key, state);
    if (undoGestureFirstClick && this.tryUndoUnitRecruitment(closestHex.key, state, undoGestureFirstClick, currentPlayer)) {
      return;
    }

    // Проверяем, что кликнули на квартал с маркером улучшения
    const hex = this.hexGridRenderer.hexes[closestHex.key];
    if (hex && hex.unitSizeRecruitmentMarkers) {
      const unitSizeRecruitmentMarker = hex.unitSizeRecruitmentMarkers.find(m => m.quarterHexKey === closestHex.key);
      if (unitSizeRecruitmentMarker) {
        const selectedUnit = selectSelectedUnit(state);
        const unitConfig = getUnitTypeConfig(selectedUnit.type);

        // Находим город, в котором стоит отряд, для проверки arsenal
        const citiesCache = selectCitiesCache(this.store.getState());
        const playerCities = citiesCache[currentPlayer.color] || [];
        const unitHexKey = selectedUnit.hexKey;
        let cityWhereUnitStands = null;

        for (const city of playerCities) {
          const cityBarracks = city.barracks;
          if (cityBarracks && Array.isArray(cityBarracks) && cityBarracks.includes(unitHexKey)) {
            cityWhereUnitStands = city;
            break;
          }
        }


        const coinsRecruitmentSize = calcCoinsRecruitmentSize(cityWhereUnitStands, currentPlayer.id, units, selectedUnit, pendingRecruitmentActions, hexes, specialBuildings, buildingsDamage)
        if (coinsRecruitmentSize === 0) {
          this.store.dispatch(showToast(`У отряда уже максимальный размер: ${unitConfig.maxSize}`, 'warning', 3000));
          return;
        }

        const {sizePrice} = getSizeRecruitmentPrice(unitConfig, currentPlayer);

        if (currentPlayer.coins < sizePrice) {
          this.store.dispatch(showToast(`Недостаточно монет для увеличения размера отряда!`, 'warning', 3000));
          return;
        }

        // Выполняем найм размера для выбранного отряда
        const sizeRecruitmentAction = addPendingRecruitmentAction(selectedUnit.id, 'SIZE_RECRUITMENT', closestHex.key, sizePrice, coinsRecruitmentSize);
        if (!checkTutorialActionWouldApply(state, sizeRecruitmentAction)) {
          this.store.dispatch(sizeRecruitmentAction);
          return;
        }
        this.store.dispatch(sizeRecruitmentAction);
        this.soundManager.playCoins(sizePrice)
        // От квартала к отряду летит шар, а когда долетит — подсветка ячейки под
        // отрядом и число потраченных монет над ним, как и при найме нового отряда
        playUnitSizeRecruitmentEffects(this.store, this.hexGridRenderer, {
          unitId: selectedUnit.id,
          quarterHexKey: closestHex.key,
          amount: coinsRecruitmentSize,
          price: sizePrice,
          playTransferSound: () => this.soundManager.playUnitSizeTransfer?.(),
        });
        return;
      }
    }

    // Проверяем, есть ли отряд на этой клетке. У своего отряда с действиями
    // найма этого хода клик может оказаться первым в паре двойного клика их
    // отмены — выбор отряда откладывается (см. deferRecruitmentStageUnitClick)
    const unitOnHex = units.find(u => u.hexKey === closestHex.key);
    if (unitOnHex) {
      const hasUndoableRecruitment = unitOnHex.ownerId === currentPlayer.id
        && Boolean(findUnitUndoableRecruitmentAction(pendingRecruitmentActions, unitOnHex.id));
      if (hasUndoableRecruitment) {
        this.deferRecruitmentStageUnitClick(closestHex, currentPlayer.id);
      } else {
        this.handleRecruitmentStageUnitClick(closestHex, unitOnHex, state, currentPlayer, units, hexes, specialBuildings, buildingsDamage);
      }
      return;
    }

    this.store.dispatch(deselectUnit());

    const isNewUnitSelected = checkIfNewUnitSelected(selectedToken);
    const availableRecruitmentCells = calculateAvailableRecruitmentCells(state);
    const isAvailableBarracks = availableRecruitmentCells.includes(closestHex.key);

    // Обратный порядок действий: сначала казарма, потом тип отряда в футере.
    // Инспекция ячейки при этом снимается — футер занимает выбор типа отряда
    if (!isNewUnitSelected) {
      if (!isAvailableBarracks || checkIfFinalRound(state)) {
        // Не пригодная под найм ячейка (и любая ячейка в последнем раунде,
        // где найм запрещен) — обычная инспекция, выбранная ранее казарма
        // снимается вместе со своей подсветкой
        this.store.dispatch(deselectBarracksForRecruitment());
        this.handleInspectionClick(closestHex, state, currentPlayer);
        return;
      }

      this.store.dispatch(clearInspectedHex());
      this.store.dispatch(selectBarracksForRecruitment(closestHex.key));
      this.store.dispatch(showToast('Выберите тип отряда для найма на этой казарме', 'info', 5000));
      this.soundManager.playTokenClick();
      return;
    }

    // Клик мимо подсвеченных пустых работающих казарм — инспекция ячейки
    if (!isAvailableBarracks) {
      this.store.dispatch(deselectBarracksForRecruitment());
      this.handleInspectionClick(closestHex, state, currentPlayer);
      return;
    }

    // Выбор казармы кликом по полю отменяет предыдущий выбор казармы из
    // футерного порядка действий — найм идет на ту, по которой кликнули
    this.store.dispatch(deselectBarracksForRecruitment());
    recruitNewUnitOnBarracks(this.store, this.soundManager, closestHex.key, selectedToken);
  }

  /**
   * Обработка клика в фазе перемещения (war -> movement)
   * Включает:
   * - Выбор отряда для перемещения
   * - Перемещение отряда на доступные ячейки
   */
  handleMovementPhaseClick(closestHex, state, currentPlayer) {
    const undoGestureFirstClick = this.registerUndoDoubleClick(closestHex.key, state);
    if (undoGestureFirstClick && this.tryUndoUnitMovement(closestHex.key, state, undoGestureFirstClick, currentPlayer)) {
      return;
    }

    const hexes = selectHexes(state);
    const pendingMovementActions = selectPendingMovementActions(state);
    const units = selectUnits(state);

    // Проверяем, есть ли отряд на этой клетке
    const unitOnHex = units.find(u => u.hexKey === closestHex.key);

    // Вражеский отряд не участвует в действиях этапа — инспекция ячейки
    if (unitOnHex && unitOnHex.ownerId !== currentPlayer.id) {
      this.handleInspectionClick(closestHex, state, currentPlayer);
      return;
    }

    if (unitOnHex) {
      const movementAction = pendingMovementActions.find(action => action.unitId === unitOnHex.id);
      // Отряд, уже перемещавшийся в текущей фазе (в т.ч. необратимо, из-за
      // срабатывания ловушки Гоблинов — trapTriggered: true, design.md
      // Decision 8 add-goblins-faction), второй раз не выбирается — иначе его
      // можно было бы переместить ещё раз. Но осмотреть его можно: у стрелка
      // оверлей покажет, куда он достанет с новой позиции. Клик может
      // оказаться первым в паре двойного клика отмены — осмотр откладывается
      // и снимается, если пара состоялась
      if (movementAction) {
        this.deferInspectionClick(closestHex, selectCurrentPlayer, true);
        return;
      }

      // Кража Лепреконов — вместо перемещения: укравший отряд тоже только
      // осматривается
      if (isUnitFaction(unitOnHex, FACTIONS.LEPRECHAUNS)) {
        const leprechaunsStealCoinsActions = selectLeprechaunsStealCoinsActions(state);

        const hasStolen = leprechaunsStealCoinsActions.some(action => action.unitId === unitOnHex.id);
        if (hasStolen) {
          this.store.dispatch(deselectUnit());
          this.handleInspectionClick(closestHex, state, currentPlayer);
          return;
        }
      }

      this.store.dispatch(selectUnit(unitOnHex.id));
      this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, currentPlayer, unitOnHex)
      return;
    }

    // Проверяем, что кликнули на доступную ячейку для перемещения ранее выбранного отряда
    const unitToMove = selectSelectedUnit(state);
    if (unitToMove) {
      const players = selectPlayers(state);
      const {cells: availableMovementCells, additionalSupplyCells, paths} = calculateAvailableMovementCells(
        this.hexGridRenderer, hexes, units, unitToMove, players, selectGoblinsTrapDeathHexKeys(state)
      )

      if (availableMovementCells.includes(closestHex.key)) {
        // Проверяем, используется ли дополнительное снабжение для достижения этой ячейки
        const usesAdditionalSupply = additionalSupplyCells.includes(closestHex.key);

        // Получаем путь для анимации
        const movementPath = paths[closestHex.key];

        // Гейт обучения: заблокированное перемещение не должно звучать и
        // проигрывать анимацию — диспатчим сразу (гейт покажет подсказку)
        const probeMoveAction = moveUnit(unitToMove.id, unitToMove.type, closestHex.key);
        if (!checkTutorialActionWouldApply(state, probeMoveAction)) {
          this.store.dispatch(probeMoveAction);
          return;
        }

        // Ловушки Гоблинов: если на поле есть хотя бы одна, прогоняем путь
        // через резолвер ДО начала анимации (см. add-goblins-faction
        // design.md Decision 4) — единственная точка правды, синхронная и
        // детерминированная. Если ловушек на карте нет вовсе — быстрый ранний
        // выход, обычное перемещение ниже не меняется вообще
        const goblinsTraps = selectGoblinsTraps(state);
        if (goblinsTraps.length > 0) {
          const turnOrder = selectTurnOrder(state);
          const resolution = resolveMovementWithTraps(movementPath, unitToMove, goblinsTraps, turnOrder);

          if (resolution.triggeredTrapEvents.length > 0) {
            const truncatedPath = movementPath.slice(0, resolution.finalStepIndex + 1);
            // Реальная потеря численности, а не сумма сырого урона по
            // ловушкам - у Орков это разные числа (calculateOrcLoss, см.
            // resolveMovementWithTraps.js), а показанный здесь "-N" должен
            // совпадать с тем, что реально произошло с отрядом
            const totalDamageTaken = unitToMove.size - resolution.remainingSize;

            this.soundManager.playUnitMovement(unitToMove.type, unitToMove);
            this.hexGridRenderer.animateUnitMovement(unitToMove.id, truncatedPath, () => {
              // Необратимо (design.md Decision 8): applyGoblinsTrapMovement
              // не создаёт запись в pendingMovementActions, поэтому
              // недоступно для Undo
              this.store.dispatch(applyGoblinsTrapMovement(unitToMove.id, {
                ...resolution,
                usesAdditionalSupply,
                ownerId: unitToMove.ownerId,
                // Для отображения на ДРУГИХ клиентах (playEffectForServerAction.js)
                // - юнит к этому моменту там уже может быть удалён из units,
                // если погиб, а тип/размер до урона нужны для
                // призрака/индикатора (см. design.md Decision 21). factionId/
                // capturedFromOwnerId - для правильного фракционного скина
                // призрака (resolveUnitImageKey) - без них наблюдатели видели
                // бы общий силуэт вместо купленного скина (найдено
                // пользователем в ручном тестировании)
                unitType: unitToMove.type,
                unitSizeBeforeDamage: unitToMove.size,
                unitFactionId: unitToMove.factionId,
                unitCapturedFromOwnerId: unitToMove.capturedFromOwnerId ?? null,
              }));

              // Показывается при ЛЮБОМ срабатывании (выжил или погиб),
              // blocking: true — только на клиенте самого ходившего игрока
              // (см. design.md Decision 21). Индикаторы на поле (плашки урона
              // по ловушкам, "-N" под отрядом, мигание отряда/призрака)
              // рисуются КАЖДЫЙ рендер-такт из этого же состояния, пока
              // show === true (HexGridRenderer.updateGoblinsTrapTriggerDisplay)
              // — не одноразовым эффектом здесь, как было раньше (тот баг,
              // из-за которого анимация иногда не отображалась вовсе или
              // накладывалась на неактуальный гекс, см. Decision 20/21).
              // unit — снимок ДО гибели (полная копия), нужен для отрисовки
              // призрака/мигания
              this.store.dispatch(showGoblinsTrapKillNotice({
                unit: {...unitToMove},
                died: resolution.unitDied,
                finalHexKey: resolution.finalHexKey,
                totalDamageTaken,
                triggeredTrapEvents: resolution.triggeredTrapEvents,
                blocking: true,
              }));
              this.soundManager.playGoblinsTrapTrigger(resolution.triggeredTrapEvents);

              const newState = this.store.getState();
              const selectedUnitId = selectSelectedUnitId(newState);
              if (selectedUnitId === unitToMove.id) {
                this.store.dispatch(deselectUnit());
              }

              if (!resolution.unitDied) {
                this.showSupplyOutOfRangeHintIfNeeded(resolution.finalHexKey, newState);
              }
            });
            return;
          }
        }

        this.soundManager.playUnitMovement(unitToMove.type, unitToMove);
        // Состояние меняется в колбэке анимации, а занятость целевой ячейки
        // проверена выше — по состоянию на момент клика. Чтобы за время полета
        // отряда состояние не успело измениться (и двое не оказались на одной
        // ячейке), анимацию добивает первым же игровым экшеном
        // unitMovementAnimationMiddleware — клики по канве делают это сами
        this.hexGridRenderer.animateUnitMovement(unitToMove.id, movementPath, () => {
          // После завершения анимации обновляем состояние
          this.store.dispatch(moveUnit(unitToMove.id, unitToMove.type, closestHex.key));
          this.store.dispatch(addPendingMovementAction(
            unitToMove.id,
            unitToMove.hexKey,
            closestHex.key,
            usesAdditionalSupply
          ));

          const newState = this.store.getState();
          const selectedUnitId = selectSelectedUnitId(newState);
          if (selectedUnitId === unitToMove.id) {
            this.store.dispatch(deselectUnit());
          }

          // Подсказка «отряд вышел из зоны снабжения» — только у самого
          // ходящего игрока (этот колбэк выполняется лишь на клиенте,
          // обработавшем клик)
          this.showSupplyOutOfRangeHintIfNeeded(closestHex.key, newState);
        });
        return;
      }

      this.store.dispatch(deselectUnit());
      this.handleInspectionClick(closestHex, state, currentPlayer);
      return;
    }

    // Пустая ячейка без выбранного отряда — инспекция ячейки
    this.handleInspectionClick(closestHex, state, currentPlayer);
  }

  handleCombatPhaseClick(closestHex, state, currentPlayer, actingPlayer) {
    const units = selectUnits(state);
    const pendingMeleeDamage = selectPendingMeleeDamage(state);
    const showMeleeTargets = selectShowMeleeTargets(state);
    const selectedUnit = selectSelectedUnit(state);

    if (showMeleeTargets) {
      const neighborEnemyUnits = getNeighborEnemyUnits(selectedUnit.ownerId, selectedUnit.hexKey, units);
      const clickedNeighborEnemyUnit = neighborEnemyUnits.find(neighbor => {
        return neighbor.hexKey === closestHex.key;
      });

      if (clickedNeighborEnemyUnit) {
        const priorityTargetForUnit = {[selectedUnit.id]: clickedNeighborEnemyUnit.id};

        // Передаем для расчета именно currentPlayer, так как ближний бой между игроком, чья сейчас фза войны, и всеми остальными
        // (actingPlayer было бы передавать некорректно - были бы иные результаты расчетов).
        // players — для подкрутки d6 отрядов ботов уровнем бонуса и давлением на лидера
        const meleeCombatInput = calcMeleeCombatInput(
          units, currentPlayer.id, pendingMeleeDamage, {}, selectPlayers(state),
        );
        this.store.dispatch(calculateMeleeCombatDamage(meleeCombatInput, undefined, priorityTargetForUnit));
        this.soundManager.playPriorityTargetSelect();
      }

      this.store.dispatch(hideMeleeTargetsForPrioritySelect());
      return;
    }

    const unitOnHex = units.find(u => u.hexKey === closestHex.key);

    if (unitOnHex) {
      const meleeAction = pendingMeleeDamage.find(action => action.unitId === unitOnHex.id);
      if (meleeAction) {
        this.store.dispatch(selectUnit(unitOnHex.id));
        this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, actingPlayer, unitOnHex);
        return;
      }
    }

    // Клик мимо участников ближнего боя — инспекция ячейки
    this.store.dispatch(deselectUnit());
    this.handleInspectionClick(closestHex, state, actingPlayer);
  }

  handleManeuverPhaseClick(closestHex, state, currentPlayer) {
    const undoGestureFirstClick = this.registerUndoDoubleClick(closestHex.key, state);
    if (undoGestureFirstClick && this.tryUndoUnitManeuver(closestHex.key, state, undoGestureFirstClick, currentPlayer)) {
      return;
    }

    const hexes = selectHexes(state);
    const units = selectUnits(state);
    const pendingManeuverActions = selectPendingManeuverActions(state);

    // Проверяем, есть ли отряд на этой клетке
    const unitOnHex = units.find(u => u.hexKey === closestHex.key && u.ownerId === currentPlayer?.id);

    if (unitOnHex) {
      // Проверяем, совершил ли отряд уже маневр — первым делом: у отряда после
      // маневра проверки ниже (снабжение, ячейка) назвали бы не ту причину
      // отказа. Клик может оказаться первым в паре двойного клика отмены —
      // тост откладывается и гасится, если пара состоялась
      const existingManeuverAction = pendingManeuverActions.find(action => action.unitId === unitOnHex.id);
      if (existingManeuverAction) {
        // Подсказку про двойной клик показываем, только если манёвр реально
        // можно отменить - для trapTriggered (сработала ловушка Гоблинов,
        // design.md Decision 8) двойной клик ничего не отменит (см.
        // tryUndoUnitManeuver), упоминать его было бы обманом (тот же принцип,
        // что и у аналогичного тоста перемещения)
        const undoHint = !existingManeuverAction.trapTriggered && this.checkIfUndoGestureAvailable(state)
          ? '\nДвойной клик по нему отменит маневр.'
          : '';
        this.showDeferredClickToast(`Этот отряд уже совершил маневр!${undoHint}`, 'error', 4000);
        return;
      }

      // Обработка клика на отряд на этапе маневра
      if (!unitOnHex.additionalSupply) {
        this.store.dispatch(showToast('Отряд без дополнительного снабжения не может маневрировать!', 'warning', 4000));
        return;
      }

      if (!checkIfCouldManeuverFromHex(currentPlayer, hexes, unitOnHex.hexKey)) {
        this.store.dispatch(showToast('Отряд не может маневрировать с пустой ячейки!', 'warning', 4000));
        return;
      }

      // Проверяем, участвовал ли отряд в этой фазе войны (стрельба/ближний
      // бой — для всех фракций, hasUsedAbilityThisWarPhase — общий флаг
      // способности любой фракции, соблазнение Суккубов и способности
      // Гоблинов). Тот же набор условий, что и в checkIfUnitCouldManeuverFromHex
      // (единственное отличие — здесь отдельным тостом выше уже отсеяны
      // дополнительное снабжение и проходимость гекса, поэтому эта функция не
      // вызывается целиком: иначе отказ без доп. снабжения показал бы то же
      // сообщение, что и отказ без участия в бою)
      if (!unitOnHex.hasShotThisWarPhase && !unitOnHex.hasFoughtThisWarPhase
        && !unitOnHex.hasUsedAbilityThisWarPhase) {
        this.store.dispatch(showToast('Только отряды, которые стреляли, сражались или применяли способность, могут маневрировать!\nЕсли у них осталось дополнительное снабжение.', 'warning', 5000));
        return;
      }

      // Выбираем отряд для маневра
      this.store.dispatch(selectUnit(unitOnHex.id));
      this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, currentPlayer, unitOnHex)
      return;
    }

    // Проверяем, что кликнули на доступную ячейку для перемещения.
    // Выбор отряда мог «протечь» с прошлых этапов (смена этапа войны не
    // сбрасывает selectedUnitId — только смена игрока): например, свой отряд,
    // выбранный на этапе перемещения и не ходивший, или чужой, выбранный для
    // просмотра результатов стрельбы/боя. Без повторной проверки права на
    // маневр такой отряд можно было бы маневрировать в обход всех условий,
    // которые проверяются при выборе отряда кликом выше
    let unitToMove = selectSelectedUnit(state);
    if (unitToMove && (
      unitToMove.ownerId !== currentPlayer?.id
      || pendingManeuverActions.some(action => action.unitId === unitToMove.id)
      || !checkIfUnitCouldManeuverFromHex(currentPlayer, unitToMove, hexes)
    )) {
      this.store.dispatch(deselectUnit());
      unitToMove = null;
    }
    if (unitToMove) {
      const availableManeuverCells = calculateAvailableManeuverCells(
        this.hexGridRenderer, hexes, units, unitToMove, selectGoblinsTrapDeathHexKeys(state)
      )

      if (availableManeuverCells.includes(closestHex.key)) {
        const fromHexKey = unitToMove.hexKey;

        // Получаем путь для анимации
        const [startQ, startR] = keyToCoords(fromHexKey);
        const [endQ, endR] = keyToCoords(closestHex.key);

        const movementPath = [{q: startQ, r: startR}, {q: endQ, r: endR}];

        // Гейт обучения: заблокированный маневр не должен звучать и
        // проигрывать анимацию — диспатчим сразу (гейт покажет подсказку)
        const probeManeuverAction = moveUnit(unitToMove.id, unitToMove.type, closestHex.key);
        if (!checkTutorialActionWouldApply(this.store.getState(), probeManeuverAction)) {
          this.store.dispatch(probeManeuverAction);
          return;
        }

        // Ловушки Гоблинов: манёвр — тоже перемещение, ловушка должна
        // срабатывать так же, как на этапе перемещения (найдено при ревью —
        // манёвр раньше вообще не прогонялся через резолвер, см.
        // add-goblins-faction design.md). Тот же resolveMovementWithTraps,
        // что и в handleMovementPhaseClick, единственная точка правды
        const goblinsTraps = selectGoblinsTraps(state);
        if (goblinsTraps.length > 0) {
          const turnOrder = selectTurnOrder(state);
          const resolution = resolveMovementWithTraps(movementPath, unitToMove, goblinsTraps, turnOrder);

          if (resolution.triggeredTrapEvents.length > 0) {
            const truncatedPath = movementPath.slice(0, resolution.finalStepIndex + 1);
            const totalDamageTaken = unitToMove.size - resolution.remainingSize;

            this.soundManager.playUnitMovement(unitToMove.type, unitToMove);
            this.hexGridRenderer.animateUnitMovement(unitToMove.id, truncatedPath, () => {
              // isManeuver: true — reducer пишет в pendingManeuverActions, а
              // не в pendingMovementActions (design.md), необратимо
              // (trapTriggered: true, та же необратимость, что и у
              // перемещения, Decision 8)
              this.store.dispatch(applyGoblinsTrapMovement(unitToMove.id, {
                ...resolution,
                isManeuver: true,
                ownerId: unitToMove.ownerId,
                unitType: unitToMove.type,
                unitSizeBeforeDamage: unitToMove.size,
                unitFactionId: unitToMove.factionId,
                unitCapturedFromOwnerId: unitToMove.capturedFromOwnerId ?? null,
              }));

              this.store.dispatch(showGoblinsTrapKillNotice({
                unit: {...unitToMove},
                died: resolution.unitDied,
                finalHexKey: resolution.finalHexKey,
                totalDamageTaken,
                triggeredTrapEvents: resolution.triggeredTrapEvents,
                blocking: true,
              }));
              this.soundManager.playGoblinsTrapTrigger(resolution.triggeredTrapEvents);

              const newState = this.store.getState();
              const selectedUnitId = selectSelectedUnitId(newState);
              if (selectedUnitId === unitToMove.id) {
                this.store.dispatch(deselectUnit());
              }

              if (!resolution.unitDied) {
                this.showSupplyOutOfRangeHintIfNeeded(resolution.finalHexKey, newState);
              }
            });
            return;
          }
        }

        this.soundManager.playUnitMovement(unitToMove.type, unitToMove);
        // Как и на этапе перемещения: состояние меняется в колбэке анимации,
        // от изменений за время полета отряда защищает
        // unitMovementAnimationMiddleware
        this.hexGridRenderer.animateUnitMovement(unitToMove.id, movementPath, () => {
          // После завершения анимации обновляем состояние
          this.store.dispatch(moveUnit(unitToMove.id, unitToMove.type, closestHex.key));
          this.store.dispatch(addPendingManeuverAction(
            unitToMove.id,
            fromHexKey,
            closestHex.key,
          ));

          const newState = this.store.getState();
          const selectedUnitId = selectSelectedUnitId(newState);
          if (selectedUnitId === unitToMove.id) {
            this.store.dispatch(deselectUnit());
          }

          // Подсказка «отряд вышел из зоны снабжения» — та же, что после
          // перемещения и найма, и только у самого ходящего игрока (колбэк
          // выполняется лишь на клиенте, обработавшем клик). После маневра
          // она нужнее всего: маневр — последний этап последней фазы раунда,
          // и до следующей фазы снабжения отряд подвинуть уже нечем
          this.showSupplyOutOfRangeHintIfNeeded(closestHex.key, newState);
        });
        return;
      }

      this.store.dispatch(deselectUnit());
    }

    // Клик мимо своих отрядов и целей маневра — инспекция ячейки
    this.handleInspectionClick(closestHex, state, currentPlayer);
  }

  handleAbilitiesPhaseClick(closestHex, state, currentPlayer) {
    const units = selectUnits(state);
    const selectedUnitId = selectSelectedUnitId(state);
    const hexes = selectHexes(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);
    const fortressesUnderConstruction = selectFortressesUnderConstruction(state);
    const citiesCache = selectCitiesCache(state);
    const goblinsTraps = selectGoblinsTraps(state);

    const unitOnHex = units.find(u => u.hexKey === closestHex.key);

    if (selectedUnitId) {
      const selectedUnit = selectSelectedUnit(state);

      // Разветвление по фракции ВЫБРАННОГО юнита — важно делать это ДО вызова
      // способность-специфичных calculateAvailable*Cells: они у каждой
      // фракции сами возвращают [] для чужого юнита, и раньше (до появления
      // Гоблинов) единственная ветка (Суккубы) безусловно делала
      // dispatch(deselectUnit()) на любой клик не по цели — для юнита ЛЮБОЙ
      // другой фракции это означало мгновенный сброс выбора при каждом
      // клике, что сломало бы выбор гоблинского юнита ещё до открытия
      // тулбара выбора режима
      if (isUnitFaction(selectedUnit, FACTIONS.SUCCUBES)) {
        const availableSeductionCells = calculateAvailableSeductionCells(
          hexes, units, selectedUnit, specialBuildings, buildingsDamage, fortressesUnderConstruction
        );

        // Если кликнули на валидную цель
        if (availableSeductionCells.includes(closestHex.key)) {
          const targetUnit = units.find(u => u.hexKey === closestHex.key);
          const previousOwnerId = targetUnit.ownerId;

          const roll = getD20Roll();
          const attackerSize = selectedUnit.size;
          const targetSize = targetUnit.size;
          const success = calculateSeductionSuccess(roll, attackerSize, targetSize);

          const prevState = this.store.getState();
          this.store.dispatch(succubesSeduceUnit(
            selectedUnit.id, targetUnit.id, previousOwnerId, roll, attackerSize, targetSize, success
          ));

          // Сердце летит к цели, итог — тост и звук — в момент попадания (см.
          // playAbilityCastEffects). Отвергнутая попытка (обучение, правила)
          // эффектов не дает
          if (checkIfUnitAbilityApplied(prevState, this.store.getState(), selectedUnit.id)) {
            playSuccubesSeductionEffects(this.store, this.hexGridRenderer, this.soundManager, {
              unitId: selectedUnit.id,
              targetUnitId: targetUnit.id,
              success,
              cost: SUCCUBES_SEDUCTION_COST,
              prevState,
              onImpact: () => {
                const resultText = success
                  ? 'Соблазнение удалось! Отряд перешел под Ваш контроль.'
                  : 'Соблазнение не удалось.';
                this.store.dispatch(showToast(resultText, success ? 'success' : 'warning', 4000, true, true));
              },
            });
          }
          // Отряд-суккуб остаётся выбранным (не deselectUnit()) — независимо от
          // исхода, чтобы футер сразу показал формулу расчёта (SelectedUnitSeduction,
          // см. design.md Decision 13). selectedUnitId и так уже указывает на этот
          // юнит, дополнительный dispatch(selectUnit(...)) не нужен. Это остаётся
          // только на экране клиента, который реально совершил попытку: этот
          // обработчик вызывается лишь на ходу текущего пользователя
          // (checkIfCurrentUserTurn выше по стеку), а selectUnit/deselectUnit несут
          // noServerSync и никогда не синхронизируются на экраны других клиентов
          return;
        } else {
          // Сброс выбора при клике не в цель
          this.store.dispatch(deselectUnit());
        }
      } else if (isUnitFaction(selectedUnit, FACTIONS.GOBLINS)) {
        const mode = selectGoblinsAbilityMode(state);

        let availableCells = [];
        if (mode === GOBLINS_ABILITY_MODES.SET_TRAP) {
          availableCells = calculateAvailableTrapSetupCells(
            hexes, units, goblinsTraps, selectedUnit, specialBuildings, buildingsDamage, fortressesUnderConstruction
          );
        } else if (mode === GOBLINS_ABILITY_MODES.REMOVE_TRAP) {
          availableCells = calculateAvailableTrapRemovalCells(
            units, goblinsTraps, selectedUnit, hexes, specialBuildings, buildingsDamage, fortressesUnderConstruction
          );
        } else if (mode === GOBLINS_ABILITY_MODES.DAMAGE_BUILDING) {
          availableCells = calculateAvailableBuildingDamageCells(
            hexes, citiesCache, specialBuildings, buildingsDamage, units, selectedUnit, currentPlayer,
            fortressesUnderConstruction
          );
        }

        if (mode && availableCells.includes(closestHex.key)) {
          if (mode === GOBLINS_ABILITY_MODES.SET_TRAP) {
            const roll = getD6Roll();
            const success = roll >= GOBLINS_ABILITY_SUCCESS_THRESHOLD;
            const trapId = createRandomId(ID_PREFIX_FOR_GOBLINS_TRAP);
            const prevState = this.store.getState();
            this.store.dispatch(goblinsSetTrap(selectedUnit.id, trapId, closestHex.key, roll, success));
            if (checkIfUnitAbilityApplied(prevState, this.store.getState(), selectedUnit.id)) {
              playGoblinsTrapSetupEffects(this.store, this.hexGridRenderer, this.soundManager, {
                unitId: selectedUnit.id,
                trapId,
                hexKey: closestHex.key,
                ownerId: currentPlayer.id,
                success,
                cost: GOBLINS_TRAP_SETUP_COST,
                prevState,
                onImpact: () => this.store.dispatch(withToastIcon(showToast(
                  success ? 'Ловушка успешно установлена!' : 'Установить ловушку не удалось.',
                  success ? 'success' : 'warning', 3500, true, true
                ), TOAST_ICONS.TRAP)),
              });
            }
          } else if (mode === GOBLINS_ABILITY_MODES.REMOVE_TRAP) {
            const trap = goblinsTraps.find(t => t.hexKey === closestHex.key && t.ownerId === currentPlayer.id);
            this.store.dispatch(goblinsRemoveTrap(selectedUnit.id, trap.id));
            this.store.dispatch(withToastIcon(showToast('Ловушка снята, получена 1 монета.', 'success', 3000, true, true), TOAST_ICONS.TRAP));
            // Звук монет и награда «+1» над гоблином
            playGoblinsTrapRemovalEffects(this.store, this.hexGridRenderer, this.soundManager, {
              unitId: selectedUnit.id,
              reward: GOBLINS_TRAP_REMOVAL_REWARD,
            });
          } else if (mode === GOBLINS_ABILITY_MODES.DAMAGE_BUILDING) {
            const roll = getD6Roll();
            const success = roll >= GOBLINS_ABILITY_SUCCESS_THRESHOLD;
            // Считаем результат ДО диспатча (reducer пересчитает его же сам,
            // см. calculateGoblinsBuildingDamageOutcome) - иначе не показать
            // верный тост ни себе, ни другим клиентам: после диспатча
            // specialBuildings/hexes по этому гексу уже могут быть изменены
            const outcome = success
              ? calculateGoblinsBuildingDamageOutcome(hexes, specialBuildings, buildingsDamage, closestHex.key, roll)
              : null;
            const victimPlayer = findOwner(closestHex.key, selectPlayers(state), citiesCache);
            const prevState = this.store.getState();
            this.store.dispatch(goblinsDamageBuilding(
              selectedUnit.id, closestHex.key, roll, success, outcome, victimPlayer?.id || null
            ));
            // Бомба летит к строению, итог — в момент попадания
            if (checkIfUnitAbilityApplied(prevState, this.store.getState(), selectedUnit.id)) {
              playGoblinsBuildingDamageEffects(this.store, this.hexGridRenderer, this.soundManager, {
                unitId: selectedUnit.id,
                hexKey: closestHex.key,
                success,
                destroyed: Boolean(outcome?.destroyed),
                damageIncrement: outcome?.damageIncrement,
                prevState,
                onImpact: () => this.store.dispatch(showToast(
                  !success
                    ? 'Повредить строение не удалось.'
                    : outcome.destroyed
                      ? `Строение разрушено: ${outcome.buildingLabel}!`
                      : `Строение повреждено: ${outcome.buildingLabel}!`,
                  success ? 'success' : 'warning', 3500, true, true
                )),
              });
            }
          }

          // Отряд остаётся выбранным (не deselectUnit()) — по тому же образцу,
          // что и у Суккубов выше (design.md Decision 13/25): чтобы футер
          // (SelectedUnitGoblinsAbility) сразу показал формулу применённой
          // способности, без дополнительного клика. selectedUnitId и так уже
          // указывает на этот юнит
          this.store.dispatch(setGoblinsAbilityMode(null));
          return;
        } else if (mode) {
          // Невалидная цель при АКТИВНОМ режиме способности (пункт 12 ручного
          // тестирования): раньше клик по невалидной ячейке снимал выбор с
          // отряда и проваливался дальше в обычную инспекцию кликнутой ячейки
          // (см. блок if (unitOnHex) ниже) без единого объяснения - игроку
          // приходилось заново кликать по своему отряду, потом по кнопке
          // способности. Теперь: тост с тем, что именно нужно было выбрать,
          // выход из режима (как повторный клик по кнопке способности), но
          // отряд ОСТАЁТСЯ выбранным и клик НЕ передаётся дальше в инспекцию
          const invalidTargetMessages = {
            [GOBLINS_ABILITY_MODES.SET_TRAP]:
              'Нужно выбрать ячейку в радиусе способности — без гор, без ловушки и не смежную вражескому отряду.',
            [GOBLINS_ABILITY_MODES.REMOVE_TRAP]:
              'Нужно выбрать свою ловушку в радиусе способности.',
            [GOBLINS_ABILITY_MODES.DAMAGE_BUILDING]:
              'Нужно выбрать вражеское строение в радиусе способности.',
          };
          this.store.dispatch(showToast(invalidTargetMessages[mode], 'warning', 3500));
          this.store.dispatch(setGoblinsAbilityMode(null));
          return;
        } else {
          // Режим способности не был выбран - обычное поведение "клик не по
          // цели снимает выбор с отряда"
          this.store.dispatch(deselectUnit());
        }
      }
    }

    // Обработка выбора отряда, применившего способность
    if (unitOnHex) {
      // Отряд, уже применивший способность в этом этапе, — показываем формулу
      // расчёта ЛЮБОМУ игроку, независимо от владельца (по образцу ближнего
      // боя, см. design.md Decision 13/25), поэтому эта проверка идёт ДО
      // проверки владельца ниже. Без неё клик по такому отряду проваливался
      // бы в checkIfSuccubesUnitCouldSeduce/checkIfGoblinsUnitCouldUseAbility
      // (false, способность уже использована) и дальше в обычную инспекцию
      // ячейки. Какой футер показать - решает TokensToolbar.js по factionId
      if (unitOnHex.hasUsedAbilityThisWarPhase) {
        this.store.dispatch(selectUnit(unitOnHex.id));
        this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, currentPlayer, unitOnHex);
        return;
      }

      // Вражеский/нейтральный/чужой захваченный отряд вне подсвеченных целей — инспекция ячейки
      if (unitOnHex.ownerId !== currentPlayer.id) {
        this.handleInspectionClick(closestHex, state, currentPlayer);
        return;
      }

      if (checkIfSuccubesUnitCouldSeduce(
        unitOnHex, units, currentPlayer.coins, hexes, specialBuildings, buildingsDamage, fortressesUnderConstruction
      )) {
        this.store.dispatch(selectUnit(unitOnHex.id));
        this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, currentPlayer, unitOnHex);
        return;
      }

      if (checkIfGoblinsUnitCouldUseAbility(
        unitOnHex, units, currentPlayer.coins, hexes, citiesCache, specialBuildings, buildingsDamage, goblinsTraps,
        currentPlayer, fortressesUnderConstruction
      )) {
        this.store.dispatch(selectUnit(unitOnHex.id));
        this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, currentPlayer, unitOnHex);
        return;
      }

      // Свой отряд, не способный использовать способность сейчас, — инспекция ячейки
      this.handleInspectionClick(closestHex, state, currentPlayer);
      return;
    }

    // Пустая ячейка вне подсвеченных целей — инспекция ячейки
    this.handleInspectionClick(closestHex, state, currentPlayer);
  }

  /**
   * Обработка клика в фазе стрельбы (war -> shooting)
   * Включает:
   * - Выбор отряда катапульт (только D4 разрешены)
   * - Проверка возможности стрельбы
   * - Расчет урона и добавление в pendingShootingActions
   */
  handleShootingPhaseClick(closestHex, state, currentPlayer) {
    const hexes = selectHexes(state);
    const units = selectUnits(state);
    const players = selectPlayers(state);
    const selectedUnitId = selectSelectedUnitId(state)
    const pendingShootingActions = selectPendingShootingActions(state);
    const pendingMovementActions = selectPendingMovementActions(state);
    const citiesCache = selectCitiesCache(state);
    const specialBuildings = selectSpecialBuildings(state);
    const buildingsDamage = selectBuildingsDamage(state);

    // Проверяем отряд на кликнутой клетке
    const unitOnHex = units.find(u => u.hexKey === closestHex.key);

    // Если выбран отряд для стрельбы
    if (selectedUnitId) {
      const selectedUnit = selectSelectedUnit(state);
      const fortressesUnderConstruction = selectFortressesUnderConstruction(state);

      let availableShootingCells;

      const isUnitMeleeElf = checkIfUnitMeleeElf(selectedUnit)
      // Рассчитываем доступные цели для стрельбы
      if (isUnitMeleeElf) {
        availableShootingCells = calculateAvailableShootingCellsForElves(hexes, units, selectedUnit, pendingShootingActions, specialBuildings, buildingsDamage, fortressesUnderConstruction);
      } else {
        availableShootingCells = calculateAvailableShootingCells(
          hexes,
          units,
          selectedUnit,
          pendingShootingActions,
          citiesCache,
          currentPlayer,
          specialBuildings,
          buildingsDamage,
          fortressesUnderConstruction,
        );
      }

      // Если кликнули на валидную цель
      if (availableShootingCells.includes(closestHex.key)) {
        const targetUnit = units.find(u => u.hexKey === closestHex.key);

        const isEnemyUnit = targetUnit && targetUnit.ownerId !== currentPlayer.id;
        const isBuilding = !targetUnit;
        let isCentralQuarterDamageIgnored = false;

        if (checkIfCentralQuarter(hexes[closestHex.key])) {
          let cityWithCentralQuarter = null;

          for (const cities of Object.values(citiesCache)) {
            cityWithCentralQuarter = cities.find(city => city.centralQuarter === closestHex.key);
            if (cityWithCentralQuarter) {
              break;
            }
          }

          if (cityWithCentralQuarter.quarter && cityWithCentralQuarter.quarter.length > 0) {
            isCentralQuarterDamageIgnored = cityWithCentralQuarter.quarter.some(quarterHexKey =>
              !isBuildingDestroyed(quarterHexKey, specialBuildings, buildingsDamage, pendingShootingActions, hexes)
            );
          }
        }

        if (isEnemyUnit || isBuilding) {
          // Гейт обучения проверяется ДО бросков: заблокированный выстрел не
          // должен ни звучать, ни потреблять кости из туториальной очереди
          const probeShootingAction = addPendingShootingAction(
            null, selectedUnit.id, targetUnit ? targetUnit.id : null, closestHex.key,
            0, 0, false, false, 0, 0, false
          );
          if (!checkTutorialActionWouldApply(state, probeShootingAction)) {
            this.store.dispatch(probeShootingAction);
            return;
          }

          const id = createRandomId(ID_PREFIX_FOR_SHOOTING_ACTION)
          const isUnitTarget = !!targetUnit;
          const targetUnitId = isUnitTarget ? targetUnit.id : null
          const targetHexKey = closestHex.key;

          const targetUnitOwnerId = isUnitTarget ? getUnitById(units, targetUnitId).ownerId : null;
          const targetHexOwnerId = findOwner(targetHexKey, players, citiesCache)?.id ?? null;

          // Кости принадлежат ВЛАДЕЛЬЦУ стреляющего отряда (см. calcMeleeCombatInput) —
          // у живых игроков подкруток нет. Отряд бота стреляет отсюда, когда
          // ботом управляет админ (см. getActingPlayer): кости бота при нем —
          // кроме выстрела по боту, тогда они обычные (см. checkIfAnyOpponentIsBot).
          // Цели — те, по кому выстрел действительно бьет: у эльфов это только
          // отряд (см. getShotOpponentOwnerIds)
          const shooterOwner = getPlayerById(players, selectedUnit.ownerId);
          const shotTargetOwners = getPlayersByIds(
            players, getShotOpponentOwnerIds(selectedUnit, targetUnitOwnerId, targetHexOwnerId)
          );
          const shooterMinRoll = getBotCombatD6MinRoll(shooterOwner, shotTargetOwners);
          const shooterRollBonus = getBotD6RollBonus(shooterOwner, shotTargetOwners);
          // Фора новичкам: бот по цели игрока с форой стреляет со штрафом к
          // грани (см. getLowRatingAdvantageDicePenalty)
          const shooterFacePenalty = getLowRatingAdvantageDicePenalty(shooterOwner, shotTargetOwners);
          const diceRoll1 = getDamageDiceRoll(shooterMinRoll, shooterRollBonus, shooterFacePenalty);
          const diceRoll2 = getDamageDiceRoll(shooterMinRoll, shooterRollBonus, shooterFacePenalty);

          const {
            damage,
            bonus
          } = calculateShootingDamage(selectedUnit, pendingMovementActions, diceRoll1, diceRoll2, units, players, targetUnitOwnerId, targetHexOwnerId)

          const isFortressTarget = specialBuildings[targetHexKey] === SPECIAL_BUILDING_TOKENS.FORTRESS;
          const isBadLuck = isUnitTarget || isFortressTarget ? damage <= 0 : damage < D4_SHOOTING_DAMAGE_FOR_1_BUILDING_DAMAGE;

          this.store.dispatch(addPendingShootingAction(
            id,
            selectedUnit.id,
            targetUnitId,
            targetHexKey,
            diceRoll1,
            diceRoll2,
            isBadLuck,
            isCentralQuarterDamageIgnored,
            damage,
            bonus,
            isUnitMeleeElf
          ));
          this.soundManager.playShooting(isUnitMeleeElf, isUnitTarget, isBadLuck);
          // Залп летит к цели, итог выстрела появляется на поле с попаданием.
          // Выстрел ищется в состоянии по id: экшен могли и отбросить
          playShotAnimation(this.store, this.hexGridRenderer, {actionId: id});
          return;
        }
      } else {
        // Сброс выбора при клике не в цель
        this.store.dispatch(deselectUnit());
      }
    }

    // Обработка выбора отряда для стрельбы
    if (unitOnHex) {
      // Вражеский отряд вне подсвеченных целей — инспекция ячейки
      if (unitOnHex.ownerId !== currentPlayer.id) {
        this.handleInspectionClick(closestHex, state, currentPlayer);
        return;
      }

      if (unitOnHex.type === UNIT_TYPES.D4.id || checkIfUnitMeleeElf(unitOnHex)) {
        if (hasAdjacentEnemyUnit(unitOnHex.hexKey, currentPlayer.id, units)) {
          // Зажатый вплотную стрелок стрелять не может — не действие этапа,
          // клик уходит в инспекцию ячейки
          this.handleInspectionClick(closestHex, state, currentPlayer);
          return;
        }
        this.store.dispatch(selectUnit(unitOnHex.id));
        this.soundManager.playUnitSelect(unitOnHex.type, unitOnHex.ownerId, currentPlayer, unitOnHex)
        return;
      }

      // Свой отряд, не умеющий стрелять, — инспекция ячейки
      this.handleInspectionClick(closestHex, state, currentPlayer);
      return;
    }

    // Пустая ячейка вне подсвеченных целей — инспекция ячейки
    this.handleInspectionClick(closestHex, state, currentPlayer);
  }

  // Настройка управления камерой (zoom и pan)
  setupCameraControls(state) {
    const players = selectPlayers(state);

    const camera = this.cameras.main;

    // === УПРАВЛЕНИЕ КАМЕРОЙ С КЛАВИАТУРЫ (WASD и стрелки) ===
    // В витрине главного меню клавиатуру не занимаем ВООБЩЕ. Phaser вешает
    // перехват на эти клавиши глобально и гасит их штатное действие, а поверх
    // витрины стоят обычные поля ввода (вход по почте): человек печатал бы
    // пароль, а вместо букв w/a/s/d ездило бы поле на фоне. Управлять камерой
    // демо-партии никто и не собирается — она играет сама.
    // Цикл движения камеры this.keys не требует (см. проверку в updateCameraByKeys)
    if (!this.isDemoMode) {
      this.keys = this.input.keyboard.addKeys({
        up: 'W',
        down: 'S',
        left: 'A',
        right: 'D',
        upArrow: 'UP',
        downArrow: 'DOWN',
        leftArrow: 'LEFT',
        rightArrow: 'RIGHT',
      });
    }

    // === НАСТРОЙКИ КАМЕРЫ ===
    // Начальный зум камеры (меньше = дальше отдалено, больше = ближе)
    // Рекомендуемый диапазон: 0.3 - 1.5
    // Пример: 0.5 - средняя дальность, 0.3 - далеко, 1.0 - близко
    // Компактная витрина главного меню: поле размером с ширину телефона, и
    // при обычном стартовом зуме в кадр попадала бы горстка гексов вместо
    // партии (см. isCompactShowcase в HexGrid)
    // Реестр читается мягко: setupCameraControls вызывают и в тестах, где
    // игрового объекта Phaser нет вовсе
    const isCompactShowcase = Boolean(this.game?.registry?.get('isCompactShowcase'));
    const initialZoom = isCompactShowcase
      ? COMPACT_SHOWCASE_INITIAL_ZOOM
      : (players.length >= 3 ? 0.8 : 0.9);
    // ========================

    // Применяем начальный зум
    camera.setZoom(initialZoom);

    // Минимальный и максимальный зум
    const minZoom = CAMERA_MIN_ZOOM;
    const maxZoom = CAMERA_MAX_ZOOM;

    // Зум колёсиком мыши и pinch на тачпаде (плавный, через tween).
    // Pinch тачпада приходит как wheel с ctrlKey и мелким deltaY (единицы против ~100
    // у щелчка колеса), поэтому для него свой коэффициент.
    const WHEEL_ZOOM_SENSITIVITY = 0.004;
    const TOUCHPAD_PINCH_ZOOM_SENSITIVITY = 0.02;
    const WHEEL_LINE_DELTA_PX = 16;
    // Потолок шага одного события: быстрый pinch шлёт крупные deltaY
    const MAX_PINCH_ZOOM_LOG_STEP = 0.12;
    let wheelZoomTween = null;
    let wheelZoomTarget = camera.zoom;
    this.input.on('wheel', (pointer, gameObjects, deltaX, deltaY) => {
      // Облет захваченных городов ведет камеру сам (см. isFieldInputLocked)
      if (isFieldInputLocked(this)) {
        return;
      }
      const nativeEvent = pointer.event;
      const isPinch = Boolean(nativeEvent && nativeEvent.ctrlKey);
      // deltaMode 1 (строки, Firefox) приводим к пикселям
      const lineScale = nativeEvent && nativeEvent.deltaMode === 1 ? WHEEL_LINE_DELTA_PX : 1;
      const sensitivity = isPinch ? TOUCHPAD_PINCH_ZOOM_SENSITIVITY : WHEEL_ZOOM_SENSITIVITY;

      if (isPinch) {
        // Pinch шлёт события на каждый кадр: твин на каждом из них отстаёт от пальцев
        // и догоняет рывком, поэтому ставим зум сразу (как при pinch на телефоне)
        if (wheelZoomTween) {
          wheelZoomTween.stop();
          wheelZoomTween = null;
        }
        const step = Phaser.Math.Clamp(
          -deltaY * lineScale * sensitivity,
          -MAX_PINCH_ZOOM_LOG_STEP,
          MAX_PINCH_ZOOM_LOG_STEP
        );
        camera.setZoom(Math.max(minZoom, Math.min(maxZoom, camera.zoom * Math.exp(step))));
        wheelZoomTarget = camera.zoom;
        return;
      }

      // Цель копится от прошлой цели, пока идёт твин: поток мелких событий
      // тачпада иначе теряет прирост, т.к. камера не успевает дойти до цели
      const baseZoom = wheelZoomTween ? wheelZoomTarget : camera.zoom;
      wheelZoomTarget = Math.max(
        minZoom,
        Math.min(maxZoom, baseZoom * Math.exp(-deltaY * lineScale * sensitivity))
      );

      if (wheelZoomTween) {
        wheelZoomTween.stop();
        wheelZoomTween = null;
      }

      wheelZoomTween = this.tweens.add({
        targets: camera,
        zoom: wheelZoomTarget,
        duration: 150,
        ease: 'Sine.easeOut',
        onComplete: () => {
          wheelZoomTween = null;
        }
      });
    });

    // === PINCH TO ZOOM (мобильные устройства) ===
    // Ручное отслеживание указателей для pinch-to-zoom
    const pointers = new Map();
    let pinchStartDistance = 0;
    let pinchStartZoom = 0;
    let wasPinchActive = false; // Флаг для предотвращения скачка камеры после pinch
    // Сколько пальцев на экране по данным самого браузера (длина event.touches
    // последнего нативного touch-события). Наш учет указателей залипает на
    // потерянных touchend, браузерный счетчик — нет, поэтому решение «пан или
    // pinch» принимается по обоим, см. resolveCameraGestureMode
    let nativeTouchCount = 0;

    const getCameraGestureMode = () => resolveCameraGestureMode(pointers.size, nativeTouchCount);

    // Мобильные браузеры теряют завершающие touch-события (шторка уведомлений,
    // сворачивание PWA/вкладки, системные свайпы iOS): запись в pointers
    // остается навсегда, и один реальный палец дальше выглядит как pinch
    // (пан зумит), а реальный pinch — как лишний палец (зум панит). Лечится
    // только перезагрузкой. Самоизлечение: перед обработкой нового касания
    // выбрасываем записи, чей Phaser-указатель уже не зажат
    const dropStalePointers = () => {
      for (const trackedId of [...pointers.keys()]) {
        const managedPointer = this.input.manager.pointers.find(
          (candidate) => candidate.id === trackedId
        );
        if (!managedPointer || !managedPointer.isDown) {
          pointers.delete(trackedId);
        }
      }
      if (getCameraGestureMode() !== CAMERA_GESTURE.PINCH) {
        pinchStartDistance = 0;
      }
    };

    this.input.on('pointerdown', (pointer) => {
      dropStalePointers();
      pointers.set(pointer.id, {x: pointer.x, y: pointer.y});

      // Pinch начинаем, только если пальцев действительно два: при фантомном
      // указателе (pointers.size === 2 при одном реальном пальце) это пан
      if (getCameraGestureMode() === CAMERA_GESTURE.PINCH) {
        const pointerValues = Array.from(pointers.values());
        pinchStartDistance = Phaser.Math.Distance.Between(
          pointerValues[0].x, pointerValues[0].y,
          pointerValues[1].x, pointerValues[1].y
        );
        pinchStartZoom = camera.zoom;
        wasPinchActive = true;
      }
    });

    this.input.on('pointermove', (pointer) => {
      if (pointers.has(pointer.id)) {
        pointers.set(pointer.id, {x: pointer.x, y: pointer.y});
      }

      // Обрабатываем pinch zoom
      if (getCameraGestureMode() === CAMERA_GESTURE.PINCH && pinchStartDistance > 0) {
        const pointerValues = Array.from(pointers.values());
        const currentDistance = Phaser.Math.Distance.Between(
          pointerValues[0].x, pointerValues[0].y,
          pointerValues[1].x, pointerValues[1].y
        );

        // Камеру ведет облет захваченных городов: щипок не зумит, а его
        // начало переносится сюда — после облета зум не скакнет
        if (isFieldInputLocked(this)) {
          pinchStartDistance = currentDistance;
          pinchStartZoom = camera.zoom;
          return;
        }

        const scale = currentDistance / pinchStartDistance;
        let newZoom = pinchStartZoom * scale;
        newZoom = Math.max(minZoom, Math.min(maxZoom, newZoom));
        camera.setZoom(newZoom);
      }
    });

    const handlePointerRelease = (pointer) => {
      // Если был активный pinch, предотвращаем скачок камеры
      if (wasPinchActive && pointers.size === 2) {
        // Обновляем cameraStart на текущую позицию, чтобы не было скачка
        cameraStart.x = camera.scrollX;
        cameraStart.y = camera.scrollY;
      }

      pointers.delete(pointer.id);
      pinchStartDistance = 0;

      // Сбрасываем флаг, как только жест перестал быть pinch
      if (getCameraGestureMode() !== CAMERA_GESTURE.PINCH) {
        wasPinchActive = false;
      }

      // Если после завершения pinch остался ровно один палец,
      // переинициализируем dragStart для оставшегося пальца,
      // чтобы при продолжении перетаскивания камера не скачала
      if (pointers.size === 1) {
        const remaining = Array.from(pointers.values())[0];
        dragStart.x = remaining.x;
        dragStart.y = remaining.y;
        cameraStart.x = camera.scrollX;
        cameraStart.y = camera.scrollY;
      }
    };

    this.input.on('pointerup', handlePointerRelease);
    // Палец отпущен за пределами канваса (частый мобильный кейс у кромки
    // экрана) — Phaser шлет отдельное событие, без него запись залипает
    this.input.on('pointerupoutside', handlePointerRelease);

    // === Панорамирование (один палец или мышь) ===
    let dragStart = {x: 0, y: 0};
    let cameraStart = {x: 0, y: 0};

    this.input.on('pointerdown', (pointer) => {
      // Начинаем перетаскивание, только если жест признан паном и не было
      // pinch. Через getCameraGestureMode, а не по pointers.size: при
      // фантомном указателе размер Map завышен, но палец реально один —
      // dragStart в этом случае обязан переинициализироваться, иначе камера
      // скакнет на накопленную с прошлого жеста дельту
      if (getCameraGestureMode() === CAMERA_GESTURE.PAN && !wasPinchActive) {
        // Едущий кадр (см. focusCameraOnAutoSelectedUnit) гасим ДО того, как
        // запомнили позицию: иначе pan продолжил бы двигать камеру, а драг
        // считался бы от устаревшего cameraStart — и кадр скакнул бы. Камеру
        // облета захваченных городов не гасим: она не игрока
        if (!isFieldInputLocked(this)) {
          this.stopCameraPan();
        }
        dragStart.x = pointer.x;
        dragStart.y = pointer.y;
        cameraStart.x = camera.scrollX;
        cameraStart.y = camera.scrollY;
      }
    });

    this.input.on('pointermove', (pointer) => {
      // Перемещаем камеру только если жест — пан, нет pinch и не было pinch
      if (pointer.isDown && getCameraGestureMode() === CAMERA_GESTURE.PAN && !wasPinchActive) {
        // Камеру ведет облет захваченных городов: драг ее не двигает, а его
        // начало переносится сюда — после облета кадр не скакнет
        if (isFieldInputLocked(this)) {
          dragStart.x = pointer.x;
          dragStart.y = pointer.y;
          cameraStart.x = camera.scrollX;
          cameraStart.y = camera.scrollY;
          return;
        }
        this.isCameraMovedByPlayer = true;
        const diffX = (pointer.x - dragStart.x) / camera.zoom;
        const diffY = (pointer.y - dragStart.y) / camera.zoom;

        camera.scrollX = cameraStart.x - diffX;
        camera.scrollY = cameraStart.y - diffY;
      }
    });

    this.input.on('pointerup', () => {
      // Обновляем позицию камеры
      cameraStart.x = camera.scrollX;
      cameraStart.y = camera.scrollY;
    });

    // Отключаем контекстное меню правой кнопки
    this.game.canvas.addEventListener('contextmenu', (e) => {
      e.preventDefault();
    });

    // === Восстановление после сворачивания PWA/вкладки/браузера ===
    // При уходе страницы в фон завершающие touch-события теряются: и наша
    // Map, и указатели Phaser остаются «зажатыми», после чего пан и зум
    // путаются между собой до перезагрузки страницы — поэтому сбрасываем всё
    const resetTouchState = () => {
      this.input.manager.pointers.forEach((managedPointer) => {
        if (managedPointer.isDown) {
          managedPointer.reset();
        }
      });
      pointers.clear();
      pinchStartDistance = 0;
      wasPinchActive = false;
      nativeTouchCount = 0;
      cameraStart.x = camera.scrollX;
      cameraStart.y = camera.scrollY;
    };
    // === Вотчдог фантомных касаний (без сворачивания страницы) ===
    // Потерянный touchend/touchcancel (системный жест iOS, шторка, edge-swipe,
    // long-press callout) оставляет Phaser-указатель «зажатым» навсегда:
    // dropStalePointers его не выбрасывает (isDown остается true), а указателей
    // под касания всего два — следующий реальный палец превращает пан в
    // pinch-зум. Истина о реально зажатых пальцах — нативные touch-события:
    // сверяем на каждом из них и ресетим указатели, чьих identifier среди живых
    // касаний нет (Phaser Pointer.identifier — это identifier породившего
    // касания, см. Pointer.touchstart). Что считать живым — resolveLiveTouch-
    // Identifiers: на touchstart новые касания вычитаются, иначе фантом с
    // переиспользованным браузером identifier выглядел бы живым вечно
    const syncWithNativeTouches = (event) => {
      // event.touches — пальцы, ЕЩЕ находящиеся на экране ПОСЛЕ этого события.
      // Это и есть браузерная истина о количестве пальцев: наш учет указателей
      // может быть завышен фантомом, а этот счетчик — нет
      nativeTouchCount = event.touches.length;

      const liveTouchIds = resolveLiveTouchIdentifiers(event);

      this.input.manager.pointers.forEach((managedPointer) => {
        // Мышь (id 0) касанием не является — сверять ее с touch-списком нельзя
        if (managedPointer.id === 0 || !managedPointer.isDown) {
          return;
        }
        if (!liveTouchIds.has(managedPointer.identifier)) {
          managedPointer.reset();
          pointers.delete(managedPointer.id);
        }
      });
      // Мышь при живых касаниях — артефакт совместимости: мобильные браузеры
      // шлют mousedown/mouseup вдогонку за touch-событиями, и потерянный
      // mouseup оставляет указатель мыши зажатым навсегда. По identifier его
      // не проверить (у мыши его нет), поэтому на устройстве, где сейчас есть
      // касания, просто не считаем ее за палец
      if (nativeTouchCount > 0) {
        pointers.delete(0);
      }
      dropStalePointers();
      if (getCameraGestureMode() !== CAMERA_GESTURE.PINCH) {
        pinchStartDistance = 0;
        wasPinchActive = false;
      }
      // Остался один реальный палец — переинициализируем пан по нему, чтобы
      // камера не скакнула на накопленную с прежнего dragStart дельту
      if (pointers.size === 1) {
        const remaining = Array.from(pointers.values())[0];
        dragStart.x = remaining.x;
        dragStart.y = remaining.y;
      }
      cameraStart.x = camera.scrollX;
      cameraStart.y = camera.scrollY;
    };
    const nativeTouchEvents = ['touchstart', 'touchmove', 'touchend', 'touchcancel'];
    nativeTouchEvents.forEach((eventName) => {
      window.addEventListener(eventName, syncWithNativeTouches, {passive: true, capture: true});
    });

    // ГЛАВНАЯ причина попаданий «на ячейку ниже» — скролл страницы.
    // ScaleManager.updateBounds() запоминает canvasBounds.y как
    // clientRect.top + pageYOffset, а хит-тест считает
    // transformY(pageY) = (pageY - canvasBounds.top) * displayScale.y, где
    // pageY у касания включает ТЕКУЩИЙ скролл. Канва у нас в
    // .game-container с position: fixed (App.css) — на экране она не
    // двигается, поэтому после скролла страницы на S пикселей каждое
    // касание уезжает ровно на S вниз.
    //
    // Сам Phaser это не чинит и не может заметить: scroll-слушателя у
    // ScaleManager нет вовсе (startListeners — только orientationchange,
    // resize и fullscreen), а его сторожевой опрос getParentBounds()
    // сравнивает canvasRect.y (без скролла) с canvasBounds.y (со скроллом
    // на момент последнего обновления) — у фиксированной канвы при
    // нулевом запомненном скролле они равны, и refresh() не зовется.
    //
    // Скроллиться нам есть чем: .App — min-height: 100vh с
    // touch-action: pan-x pan-y, overflow у html/body не заперт, так что
    // страница ездит на высоту мобильного тулбара (те самые ~50-90 px,
    // порядок одной клетки), на рубер-бэнде и на прокрутке меню/модалок.
    const syncCanvasBounds = () => {
      this.scale.updateBounds();
    };
    // Скролл ловим и на окне, и на визуальном вьюпорте (iOS прячет
    // адресную строку, не трогая window). Capture — чтобы отработать до
    // обработчиков Phaser, passive — чтобы не мешать прокрутке
    window.addEventListener('scroll', syncCanvasBounds, {passive: true, capture: true});
    // Страховка на случай скролла, о котором событие не пришло: границы
    // обновляются перед КАЖДЫМ касанием. Phaser слушает канву в фазе
    // всплытия, наш перехват на window гарантированно раньше, так что
    // пересчет успевает до хит-теста. Одно чтение геометрии на тап —
    // на touchmove это вешать нельзя, там был бы layout на каждый кадр
    const boundsSyncEvents = ['touchstart', 'mousedown'];
    boundsSyncEvents.forEach((eventName) => {
      window.addEventListener(eventName, syncCanvasBounds, {passive: true, capture: true});
    });

    // При возвращении вьюпорт мог измениться без события resize (высота
    // адресной строки/тулбаров), Phaser продолжает хит-тестить по старым
    // границам канваса — клики попадают на ячейку ниже. this.scale.refresh()
    // тут пересчитывает только CSS-границы/хит-тест, до самого WebGL-рендерера
    // не достает (ScaleManager.refresh не трогает renderer вообще).
    //
    // Настоящая же передорисовка поля после сворачивания требует двух вещей:
    // 1) forceRendererResize — из исходников Phaser: WebGLRenderer.onResize
    //    вызывает реальный renderer.resize() (gl.viewport/gl.scissor/
    //    setProjectionMatrix) ТОЛЬКО если baseSize отличается от текущих
    //    renderer.width/height. Обычное сворачивание/разворачивание окна
    //    размеры не меняет, поэтому этот путь никогда не срабатывает сам —
    //    а именно ручной resize окна (по наблюдению) чинит зависшее поле,
    //    так что зовем renderer.resize() напрямую в обход этой проверки.
    // 2) handleStoreUpdate — полная передорисовка токенов/маркеров.
    // Если поле виснет из-за потерянного WebGL-контекста, а не только
    // "устаревших" данных, одного handleStoreUpdate (что и так гоняется на
    // каждый store-dispatch) недостаточно — отсюда и явный resize-надзор
    const forceRendererResize = () => {
      const renderer = this.sys.renderer;
      // На потерянном контексте звать resize() нельзя: он читает
      // gl.drawingBufferHeight (там 0) и выставляет из него ножницы
      // gl.scissor(0, -height, ...) с отрицательным началом — кадр после
      // восстановления обрезается целиком. Восстановление и без нас зовет
      // resize само, в самом конце dispatchContextRestored
      if (renderer && !renderer.contextLost && this.scale?.baseSize) {
        renderer.resize(this.scale.baseSize.width, this.scale.baseSize.height);
      }
    };
    const handlePageRestore = () => {
      this.scale.refresh();
      resetTouchState();
      forceRendererResize();
      this.handleStoreUpdate();
    };
    // Изменение видимой области без window.resize (тулбар iOS Safari):
    // только границы, жесты не трогаем — палец может быть легитимно зажат
    const handleVisualViewportResize = () => {
      this.scale.refresh();
    };
    // Потеря/восстановление WebGL-контекста может произойти и без смены
    // видимости вкладки (GPU освобождает память под давлением, пока вкладка
    // все время оставалась visible) — отдельная подписка гарантирует полную
    // передорисовку ровно в момент, когда текстуры снова доступны.
    // Сгенерированные текстуры (обводки, затухание подложки) хранят пиксели в
    // памяти и восстанавливаются вместе со всеми (см. generateOutlineTexture).
    // dispatchContextRestored уже пересоздал все GL-ресурсы и сам вызвал
    // renderer.resize(baseSize) перед тем, как отдать это событие, — нам
    // остается только заново собрать сцену из состояния
    const handleContextRestore = () => {
      this.handleStoreUpdate();
    };

    this.game.events.on(Phaser.Core.Events.HIDDEN, resetTouchState);
    this.game.events.on(Phaser.Core.Events.BLUR, resetTouchState);
    this.game.events.on(Phaser.Core.Events.VISIBLE, handlePageRestore);
    this.sys.renderer?.on(Phaser.Renderer.Events.RESTORE_WEBGL, handleContextRestore);
    window.addEventListener('pagehide', resetTouchState);
    // bfcache-восстановление (iOS Safari): страница оживает без перезагрузки
    window.addEventListener('pageshow', handlePageRestore);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleVisualViewportResize);
      window.visualViewport.addEventListener('scroll', syncCanvasBounds);
    }

    this.disposeCameraControls = () => {
      this.game.events.off(Phaser.Core.Events.HIDDEN, resetTouchState);
      this.game.events.off(Phaser.Core.Events.BLUR, resetTouchState);
      this.game.events.off(Phaser.Core.Events.VISIBLE, handlePageRestore);
      this.sys.renderer?.off(Phaser.Renderer.Events.RESTORE_WEBGL, handleContextRestore);
      window.removeEventListener('pagehide', resetTouchState);
      window.removeEventListener('pageshow', handlePageRestore);
      window.removeEventListener('scroll', syncCanvasBounds, {capture: true});
      boundsSyncEvents.forEach((eventName) => {
        window.removeEventListener(eventName, syncCanvasBounds, {capture: true});
      });
      nativeTouchEvents.forEach((eventName) => {
        window.removeEventListener(eventName, syncWithNativeTouches, {capture: true});
      });
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleVisualViewportResize);
        window.visualViewport.removeEventListener('scroll', syncCanvasBounds);
      }
    };
  }

  // Ограничивает панорамирование камеры фактической протяженностью
  // отрисованной гекс-сетки (плюс небольшой отступ) — поля разного размера
  // (2 игрока, экспериментальная Европа и т.д.) задают разный radius/hexSize
  // в gridConfig, поэтому границы считаются из уже сгенерированных гексов, а
  // не хардкодятся под конкретное поле. Phaser сам клэмпит scrollX/scrollY
  // каждый кадр (Camera.preRender) для любого способа их изменения — драга,
  // WASD/стрелок, тween-зума, — отдельно обрабатывать каждый ввод не нужно.
  // При зуме, когда поле с отступом меньше вьюпорта, Camera.clampX/clampY
  // сами центрируют его, а не позволяют довернуть до края
  setupCameraBounds() {
    const hexes = Object.values(this.hexGridRenderer.hexes);
    if (hexes.length === 0) {
      return;
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    hexes.forEach((hex) => {
      hex.points.forEach((point) => {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.y > maxY) maxY = point.y;
      });
    });

    // Отступ за крайние гексы — чтобы граница панорамирования не упиралась
    // жестко точно в край поля, оставляя немного "воздуха" вокруг
    // Плюс самый широкий отступ интерфейса при самом дальнем зуме: крайние
    // ячейки можно вывести из-под шапки, футера и панелей в свободную область.
    // И не меньше, чем нужно кадру самого дальнего зума (calcCameraBoundsPadding):
    // иначе на большом окне Phaser прижимал кадр к левому краю границ
    const dpr = this.scale?.zoom ? 1 / this.scale.zoom : 1;
    const freeAreaPadding = getFreeAreaBoundsPadding(this.fieldInsets || ZERO_FIELD_INSETS, dpr, CAMERA_MIN_ZOOM);
    const padding = calcCameraBoundsPadding({
      gridWidth: maxX - minX,
      gridHeight: maxY - minY,
      cameraWidth: this.cameras.main.width,
      cameraHeight: this.cameras.main.height,
      minZoom: CAMERA_MIN_ZOOM,
      basePadding: (this.gridConfig?.hexSize || 60) * 20 + freeAreaPadding,
      freeAreaPadding,
    });
    this.cameras.main.setBounds(
      minX - padding.x,
      minY - padding.y,
      (maxX - minX) + padding.x * 2,
      (maxY - minY) + padding.y * 2
    );
  }

  /**
   * Границы игровой части поля — всех ячеек, КРОМЕ морских. Море лишь помечает
   * неигровую часть сетки и не рисуется вовсе, поэтому подложку центрируем и
   * масштабируем именно по этой области.
   * @returns {{centerX: number, centerY: number, width: number, height: number}|null}
   */
  calcPlayAreaBounds(state) {
    const hexes = selectHexes(state);
    // Спрашивают на каждой перерисовке, а поле меняется редко: состояние неизменяемое,
    // так что сверки по ссылке достаточно
    const cache = this.playAreaBoundsCache;
    if (cache?.hexes === hexes && cache.cells === this.hexGridRenderer.hexes) {
      return cache.bounds;
    }
    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    Object.entries(this.hexGridRenderer.hexes).forEach(([key, hex]) => {
      if (hexes[key] === TERRAIN_TOKENS.SEA) {
        return;
      }
      hex.points.forEach((point) => {
        if (point.x < minX) minX = point.x;
        if (point.x > maxX) maxX = point.x;
        if (point.y < minY) minY = point.y;
        if (point.y > maxY) maxY = point.y;
      });
    });

    const bounds = Number.isFinite(minX) ? {
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2,
      width: maxX - minX,
      height: maxY - minY,
    } : null;
    this.playAreaBoundsCache = {hexes, cells: this.hexGridRenderer.hexes, bounds};
    return bounds;
  }

  /**
   * Текстура затухания подложки: прозрачная в середине, к краям — сплошной
   * черный. Рисуется поверх картинки, поэтому пейзаж у границ плавно уходит в
   * цвет фона сцены, а не обрывается прямоугольником.
   *
   * Затухание считается по расстоянию до БЛИЖАЙШЕГО края, а не от центра:
   * так полоса одинаковой ширины идет вдоль всех четырех сторон, и углы не
   * чернеют раньше середины сторон, как было бы с радиальным градиентом.
   */
  ensureGroundFadeTexture() {
    if (this.textures.exists(GROUND_FADE_TEXTURE_KEY)) {
      return GROUND_FADE_TEXTURE_KEY;
    }

    // Пиксели держим в памяти, а не в DOM-канвасе: после потери GPU-контекста
    // (мобильные в фоне) Phaser заново заливает текстуру из ее источника, а
    // содержимое 2D-канваса к этому моменту браузер уже мог очистить — края
    // подложки обрывались бы прямоугольником (подробно — generateOutlineTexture)
    const size = GROUND_FADE_TEXTURE_SIZE;
    const pixels = new Uint8Array(size * size * 4);
    const band = Math.max(1, size * GROUND_FADE_FRACTION);

    for (let y = 0; y < size; y++) {
      for (let x = 0; x < size; x++) {
        const distanceToEdge = Math.min(x, y, size - 1 - x, size - 1 - y);
        const progress = Math.min(1, distanceToEdge / band);
        // Сглаживание: у линейного перехода на границе полосы видна ступенька
        const smooth = progress * progress * (3 - 2 * progress);
        const offset = (y * size + x) * 4;
        pixels[offset + 3] = Math.round(255 * (1 - smooth));
      }
    }

    const texture = this.textures.addUint8Array(GROUND_FADE_TEXTURE_KEY, pixels, size, size);
    if (!texture) {
      return null;
    }
    // Текстура растягивается на всю подложку — без линейной фильтрации переход
    // пошел бы ступеньками
    texture.setFilter(Phaser.Textures.FilterMode.LINEAR);

    return GROUND_FADE_TEXTURE_KEY;
  }

  /**
   * Поставить точку мира в центр свободной от интерфейса области, а не окна
   * (см. utils/visuals/fieldInsets). Через нее идут все автоматические кадры:
   * стартовый, обучения, подъезд к отряду автовыбора и подстройка после ресайза
   */
  centerOnFreeArea(worldX, worldY) {
    centerCameraOnFreeArea(this, worldX, worldY);
  }

  /** Кадр камеры: точка мира в центре свободной области и зум */
  getCameraFrame() {
    const center = this.getFreeAreaCenterWorld();
    return {x: center.x, y: center.y, zoom: this.cameras.main.zoom};
  }

  /** Размер свободной области (без шапки, футера и панелей) в пикселях канвы */
  getFreeAreaSizePx() {
    const camera = this.cameras.main;
    const dpr = this.scale?.zoom ? 1 / this.scale.zoom : 1;
    const insets = (this.isMatchField && this.fieldInsets) || ZERO_FIELD_INSETS;
    return {
      width: Math.max(1, camera.width - ((insets.left || 0) + (insets.right || 0)) * dpr),
      height: Math.max(1, camera.height - ((insets.top || 0) + (insets.bottom || 0)) * dpr),
    };
  }

  /** Точка мира, которая сейчас в центре свободной области */
  getFreeAreaCenterWorld() {
    const camera = this.cameras.main;
    const offset = resolveFreeAreaWorldOffset(this);
    return {
      x: camera.scrollX + camera.width / 2 + offset.x,
      y: camera.scrollY + camera.height / 2 + offset.y,
    };
  }

  /**
   * Стартовый кадр: центр занятых ячеек (в подготовке — всего поля) в центре
   * свободной области. Ставится, когда загрузилось поле и когда партия вышла
   * из подготовки; между ними — не трогаем, чтобы кадр не ездил за каждым
   * новым строением. Пока игрок сам не двигал камеру, тот же центр держится
   * при изменении отступов (см. handleFieldInsetsChanged)
   */
  updateStartCameraFrame(state) {
    if (!this.isMatchField || this.isCameraMovedByPlayer || selectIsTutorialActive(state)
      || isFieldInputLocked(this)) {
      return;
    }
    const playArea = this.calcPlayAreaBounds(state);
    if (!playArea) {
      return;
    }
    const isPreparation = checkIfAnyGamePreparationPhase(selectPhase(state));
    const frameKey = `${Math.round(playArea.centerX)}:${Math.round(playArea.centerY)}:`
      + `${Math.round(playArea.width)}:${Math.round(playArea.height)}:${isPreparation ? 'prep' : 'game'}`;
    if (frameKey === this.startCameraFrameKey) {
      return;
    }
    this.startCameraFrameKey = frameKey;

    const occupied = isPreparation
      ? null
      : calcOccupiedCellsBounds(selectHexes(state), selectUnits(state), this.hexGridRenderer.hexes);
    const target = occupied || playArea;
    this.startCameraTarget = {x: target.centerX, y: target.centerY};
    this.centerOnFreeArea(this.startCameraTarget.x, this.startCameraTarget.y);
  }

  /**
   * Интерфейс поменял занятые края (шапка, футер, панели): границы прокрутки
   * пересчитываются, а автоматический кадр, пока игрок не двигал камеру,
   * переставляется под новую свободную область
   */
  handleFieldInsetsChanged(insets) {
    if (!this.isSceneActive || !this.cameras?.main) {
      return;
    }
    this.fieldInsets = insets;
    this.setupCameraBounds();
    // Камеру, которую игрок поставил сам, не двигаем: футер меняет высоту на
    // каждом этапе, и поле дергалось бы под рукой. Камеру облета захваченных
    // городов — тоже: она сама держит город в центре свободной области
    if (this.isCameraMovedByPlayer || isFieldInputLocked(this)) {
      return;
    }
    const state = this.store.getState();
    if (selectIsTutorialActive(state)) {
      this.applyTutorialCameraShift(state);
    } else if (selectPhase(state) === PHASES.WAR) {
      // В фазе войны кадр двигают только игрок и автовыбор отрядов: к
      // стартовой точке матча он не возвращается, иначе смена высоты футера
      // на телефоне перебивала подъезд к первому отряду этапа. Выбранный
      // автовыбором отряд вписывается в новую свободную область по тому же
      // правилу, что и при выборе: в центральной зоне кадр стоит на месте
      const target = this.autoSelectedUnitCameraTarget;
      if (target && target.unitId === selectSelectedUnitId(state)) {
        this.frameAutoSelectedUnit(target.hexKey);
      }
    } else if (this.startCameraTarget) {
      this.panToFreeArea(this.startCameraTarget.x, this.startCameraTarget.y);
    }
  }

  /**
   * Плавно подвести точку мира в центр свободной области. Без эффекта pan
   * (заглушки камеры в тестах) — сразу. force: новая смена отступов
   * перенацеливает уже едущий кадр
   */
  panToFreeArea(worldX, worldY) {
    const camera = this.cameras.main;
    if (typeof camera.pan !== 'function') {
      this.centerOnFreeArea(worldX, worldY);
      return;
    }
    const offset = resolveFreeAreaWorldOffset(this);
    camera.pan(worldX - offset.x, worldY - offset.y, FREE_AREA_PAN_DURATION_MS, FREE_AREA_PAN_EASE, true);
  }

  /**
   * Окно изменило размер — буфер канвы подстраивается на месте, без
   * пересоздания сцены: поле, выбор и зум остаются, а точка мира в центре
   * свободной области остается в центре. Гексы не искажаются: буфер меняет
   * размер вместе с окном (Scale.NONE, см. HexGrid)
   * @returns {boolean} false — сцена не готова или это не поле матча, и
   *   вызывающему остается прежнее пересоздание
   */
  resizeToWindow() {
    if (!this.isSceneActive || !this.isMatchField || !this.scale || !this.cameras?.main) {
      return false;
    }
    const dpr = this.scale.zoom ? 1 / this.scale.zoom : 1;
    const {width, height} = getMatchFieldBufferSize(dpr);
    if (width === this.scale.width && height === this.scale.height) {
      return true;
    }
    const centerBefore = this.getFreeAreaCenterWorld();
    this.scale.resize(width, height);
    // Камеру ScaleManager подгоняет сам, но страхуемся: размер кадра обязан
    // совпасть с буфером до того, как мы поставим центр
    this.cameras.main.setSize(width, height);
    this.scale.updateBounds();
    this.fieldInsets = getFieldInsets();
    this.setupCameraBounds();
    this.centerOnFreeArea(centerBefore.x, centerBefore.y);
    return true;
  }

  /**
   * Обучение: кадр держится ниже середины поля — сценарий главы почти
   * целиком идет в его нижней половине (свой город, стрелки на ячейки), и при
   * обычном центрировании игрок первым делом видит пустой верх.
   *
   * Центр кадра ставится АБСОЛЮТНО (centerOn), а не сдвигом от текущего
   * положения, и заново на каждой перерисовке, пока игрок сам не двинул
   * камеру: размер канвы на старте еще меняется (раскладка, появление
   * футера, поворот экрана), а от высоты кадра зависит, что попадает в вид —
   * одноразовый сдвиг на первом апдейте оставлял бы кадр где попало. Зум
   * центр не смещает, так что приближение игрока это не ломает; пан (драг и
   * клавиши) помечает камеру как его, и дальше мы в нее не лезем.
   *
   * Нижний край игровой части при этом может уйти выше низа экрана, открыв
   * полосу пейзажа: на высоком экране поле помещается целиком, и без этого
   * сдвигать кадр было бы некуда — а низ экрана все равно занят панелью
   * задач и модалками обучения. Сила сдвига настраивается главой
   * (cameraDownShiftFraction).
   */
  applyTutorialCameraShift(state) {
    if (this.isCameraMovedByPlayer || !selectIsTutorialActive(state) || isFieldInputLocked(this)) {
      return;
    }
    const bounds = this.calcPlayAreaBounds(state);
    if (!bounds) {
      return;
    }
    const {chapter} = getActiveTutorialContext(state);
    const shiftFraction = chapter?.cameraDownShiftFraction ?? TUTORIAL_CAMERA_DOWN_SHIFT_FRACTION;

    centerCameraOnFreeArea(this, bounds.centerX, bounds.centerY + bounds.height * shiftFraction);
  }

  /**
   * Подвод кадра к отряду, выбранному автовыбором (change unit-auto-selection) —
   * единственный автоматический переезд кадра в фазе войны: осмотр ячейки и
   * выбор отряда кликом камеру не двигают. В обучении и демо-партии автовыбора
   * нет вовсе (checkIfUnitAutoSelectionActive)
   */
  focusCameraOnAutoSelectedUnit(hexKey) {
    // Облет захваченных городов ведет камеру сам (см. isFieldInputLocked)
    if (this.isDemoMode || isFieldInputLocked(this)) {
      return;
    }
    // Когда интерфейс поменяет занятые края (на телефоне футер меняет высоту
    // на смене этапа и при выборе отряда), handleFieldInsetsChanged заново
    // вписывает этот отряд в кадр, пока он выбран
    const hex = this.hexGridRenderer?.hexes?.[hexKey];
    const unitId = selectSelectedUnitId(this.store.getState());
    this.autoSelectedUnitCameraTarget = hex && unitId ? {unitId, hexKey} : null;
    this.frameAutoSelectedUnit(hexKey);
  }

  /**
   * Центр свободной области для кадра автовыбора, ограниченный игровой частью
   * поля (calcPlayAreaBounds — все ячейки, кроме моря): у края поля кадр не
   * показывает половину экрана пустой подложки, а сдвигается внутрь, и точка
   * при этом остается в свободной области (см. clampFreeAreaCenterToPlayArea).
   * Запас за краем — полгекса, чтобы рамка сетки не упиралась в интерфейс
   * @param {{x: number, y: number}} center — желаемый центр, мир
   * @param {{width: number, height: number}} frame — свободная область, мир
   * @returns {{x: number, y: number}}
   */
  clampAutoFrameCenterToPlayArea(center, frame, state) {
    const area = state && this.hexGridRenderer?.hexes ? this.calcPlayAreaBounds(state) : null;
    if (!area) {
      return center;
    }
    const margin = (this.gridConfig?.hexSize || 60) / 2;
    return clampFreeAreaCenterToPlayArea(center, area, frame, margin);
  }

  /**
   * Вписать отряд, выбранный автовыбором, в кадр (utils/camera/autoSelectionFraming):
   * отряд в центральной зоне свободной области (две средние четверти по каждой
   * оси) кадр не двигает; за ней кадр сдвигается ровно настолько, чтобы все
   * ячейки, доступные отряду на этапе, оказались в зоне, а если они туда не
   * помещаются (сильное приближение) — отряд встает в центр. Оси — по
   * отдельности. При малом сдвиге кадр не двигается.
   *
   * Переезд делает эффект pan, а не centerOn: он плавный и сам клэмпится по
   * границам поля (setupCameraBounds). force: true — следующий отряд
   * перенацеливает уже едущую камеру, а не ждет ее. Пока отступ футера не
   * измерен, кадр считается с футером худшего случая (см.
   * AUTO_SELECTION_CAMERA_UP_SHIFT_FRACTION)
   */
  frameAutoSelectedUnit(hexKey) {
    const camera = this.cameras?.main;
    const hex = this.hexGridRenderer?.hexes?.[hexKey];
    if (!camera || !hex) {
      return;
    }

    const dpr = this.scale?.zoom ? 1 / this.scale.zoom : 1;
    const measuredInsets = this.isMatchField ? (this.fieldInsets || ZERO_FIELD_INSETS) : ZERO_FIELD_INSETS;
    const insets = measuredInsets.bottom > 0 ? measuredInsets : {
      ...measuredInsets,
      bottom: (camera.height / dpr) * AUTO_SELECTION_CAMERA_UP_SHIFT_FRACTION * 2,
    };
    const offset = getFreeAreaWorldOffset(insets, dpr, camera.zoom);
    const frame = getFreeAreaWorldSize({width: camera.width, height: camera.height}, insets, dpr, camera.zoom);

    // Центр кадра — scroll плюс половина вьюпорта (зум его не смещает, см.
    // Camera.centerOn), центр свободной области — со сдвигом отступов
    const currentCenter = {
      x: camera.scrollX + camera.width / 2 + offset.x,
      y: camera.scrollY + camera.height / 2 + offset.y,
    };
    // Ячейки действий отряда вместе с его ячейкой; без рендерера с маркерами —
    // одна ячейка отряда по ее радиусу
    const radius = this.gridConfig?.hexSize || 60;
    const cellsBounds = this.hexGridRenderer.getUnitActionCellsBounds?.(hexKey) || {
      minX: hex.centerX - radius, maxX: hex.centerX + radius, minY: hex.centerY - radius, maxY: hex.centerY + radius,
    };
    const framed = getAutoSelectionFrameCenter({
      center: currentCenter,
      frame,
      unitPoint: {x: hex.centerX, y: hex.centerY},
      cellsBounds,
    });
    // У края поля сдвинутый центр уезжает внутрь: в кадре — игровая часть, а
    // не пустая подложка за ней. Ось, по которой отряд и так в зоне, не трогаем
    const clamped = this.clampAutoFrameCenterToPlayArea(framed, frame, this.store?.getState?.());
    const center = {
      x: framed.x === currentCenter.x ? currentCenter.x : clamped.x,
      y: framed.y === currentCenter.y ? currentCenter.y : clamped.y,
    };
    const targetX = center.x - offset.x;
    const targetY = center.y - offset.y;

    // Центр кадра — scroll плюс половина вьюпорта (зум его не смещает, см.
    // Camera.centerOn); переводим расхождение в пиксели канвы
    const shiftXPx = Math.abs(targetX - (camera.scrollX + camera.width / 2)) * camera.zoom;
    const shiftYPx = Math.abs(targetY - (camera.scrollY + camera.height / 2)) * camera.zoom;
    if (shiftXPx < AUTO_SELECTION_CAMERA_MIN_SHIFT_PX && shiftYPx < AUTO_SELECTION_CAMERA_MIN_SHIFT_PX) {
      return;
    }

    camera.pan(
      targetX,
      targetY,
      AUTO_SELECTION_CAMERA_PAN_DURATION_MS,
      AUTO_SELECTION_CAMERA_PAN_EASE,
      true
    );
  }

  /**
   * Ячейка в центре свободной области поля — начало отсчета автовыбора отрядов
   * в начале этапа (см. pickNextActionableUnit). Ближайшая к центру, даже если
   * сам центр пришелся мимо поля
   * @returns {?string} ключ ячейки
   */
  getFreeAreaCenterHexKey() {
    const camera = this.cameras?.main;
    const hexes = this.hexGridRenderer?.hexes;
    if (!camera || !hexes) {
      return null;
    }
    const offset = resolveFreeAreaWorldOffset(this);
    const centerX = camera.midPoint.x + offset.x;
    const centerY = camera.midPoint.y + offset.y;

    let closestKey = null;
    let minDistance = Infinity;
    Object.values(hexes).forEach((hex) => {
      const distance = Math.hypot(centerX - hex.centerX, centerY - hex.centerY);
      if (distance < minDistance) {
        minDistance = distance;
        closestKey = hex.key;
      }
    });
    return closestKey;
  }

  /**
   * Останавливает едущий кадр (см. focusCameraOnAutoSelectedUnit) там, где его
   * перехватывает сам игрок: иначе эффект pan продолжал бы каждый кадр
   * переписывать scroll поверх драга и клавиш. Камера остается там, где ее
   * застали, — без рывка назад
   */
  stopCameraPan() {
    const panEffect = this.cameras?.main?.panEffect;
    if (panEffect?.isRunning) {
      panEffect.reset();
    }
  }

  /**
   * Подложка-пейзаж под сеткой: картинка масштабируется так, чтобы игровая
   * часть поля уместилась в ее центральную равнину, и центрируется по этой же
   * части. Поверх ложится затухание в черный по краям.
   */
  ensureGroundBackground(state) {
    if (!this.textures.exists(GROUND_IMAGE_KEY)) {
      return;
    }

    const bounds = this.calcPlayAreaBounds(state);
    if (!bounds) {
      return;
    }

    // Пересчитываем, только когда игровая часть действительно поменялась
    // (загрузилось поле, редактор перекрасил море): спрайт большой, дергать
    // его на каждом апдейте стора незачем
    const signature = `${Math.round(bounds.centerX)}:${Math.round(bounds.centerY)}:${Math.round(bounds.width)}:${Math.round(bounds.height)}`;
    if (signature === this.groundBoundsSignature && this.groundSprite) {
      return;
    }

    // Картинка квадратная, поэтому равняемся на большую сторону игровой части:
    // по меньшей запас окажется только больше
    const groundSize = Math.max(bounds.width, bounds.height) / GROUND_PLAIN_FRACTION;

    if (!this.groundSprite) {
      this.groundSprite = this.add.image(bounds.centerX, bounds.centerY, GROUND_IMAGE_KEY);
      this.groundSprite.setDepth(GROUND_DEPTH);
    }
    this.groundSprite.setPosition(bounds.centerX, bounds.centerY);
    this.groundSprite.setDisplaySize(groundSize, groundSize);

    const fadeTextureKey = this.groundFadeFailed ? null : this.ensureGroundFadeTexture();
    if (!fadeTextureKey) {
      // Подложка без затухания лучше, чем ее отсутствие: картинка останется,
      // просто оборвется по краю. Повторять попытку на каждом апдейте стора
      // незачем — причина не рассосется
      this.groundFadeFailed = true;
      console.warn('[GameScene] Не удалось создать текстуру затухания подложки');
    } else {
      if (!this.groundFadeSprite) {
        this.groundFadeSprite = this.add.image(bounds.centerX, bounds.centerY, fadeTextureKey);
        this.groundFadeSprite.setDepth(GROUND_FADE_DEPTH);
        const willRender = this.groundFadeSprite.willRender?.bind(this.groundFadeSprite);
        this.groundFadeSprite.willRender = (camera) => Boolean(willRender?.(camera))
          && checkIfGroundFadeInView(camera.worldView, this.groundPlace, GROUND_FADE_FRACTION);
      }
      this.groundFadeSprite.setPosition(bounds.centerX, bounds.centerY);
      this.groundFadeSprite.setDisplaySize(groundSize, groundSize);
    }

    // Где лежит картинка пейзажа — для антуража над ней (см. syncFieldAmbience)
    this.groundPlace = {centerX: bounds.centerX, centerY: bounds.centerY, size: groundSize};

    // Отметку о посчитанных границах ставим в самом конце: сорвись что-то выше,
    // следующий апдейт попробует снова, а не решит, что подложка уже готова
    this.groundBoundsSignature = signature;
  }

  /**
   * Антураж над пейзажем и полем — облака с тенями, стаи птиц и орлы над
   * горами (см. FieldAmbience). Зовется на каждом обновлении стора, сразу за
   * подложкой:
   *  - строится заново, когда пейзаж лег или переехал либо изменилась
   *    игровая часть поля. У антуража свой признак поля: подложку двигает
   *    только рамка поля, а облакам важна каждая ячейка — редактор может
   *    перекрасить ячейку в море и обратно, не тронув рамки;
   *  - в остальное время получает признак партии (время ее начала): по нему
   *    выбирается ветер, один на весь матч. Партия может начаться на уже
   *    нарисованном поле;
   *  - горы (над ними кружат орлы) передаются отдельно: поставить или убрать
   *    жетон горы — не повод раскладывать облака заново.
   *
   * Антураж — украшение: его сбой не должен сорвать перерисовку поля
   */
  syncFieldAmbience(state) {
    if (this.isFieldAmbienceFailed || !this.groundPlace) {
      return;
    }
    try {
      const {field: signature, mountains: mountainsSignature} = this.calcFieldAmbienceSignature(state);
      const hexes = selectHexes(state);
      const collectCenters = (checkIfFits) => Object.entries(this.hexGridRenderer.hexes)
        .filter(([key]) => checkIfFits(hexes[key]))
        .map(([, hex]) => ({x: hex.centerX, y: hex.centerY}));
      const collectMountainCenters = () => collectCenters((token) => token === TERRAIN_TOKENS.MOUNTAIN);

      if (this.fieldAmbience && signature === this.fieldAmbienceSignature) {
        this.fieldAmbience.setMatchSeed(selectGameStartedAt(state));
        if (mountainsSignature !== this.fieldMountainsSignature) {
          this.fieldAmbience.setMountains(collectMountainCenters());
          this.fieldMountainsSignature = mountainsSignature;
        }
        return;
      }

      if (!this.fieldAmbience) {
        this.fieldAmbience = new FieldAmbience(this);
      }
      const cellCenters = collectCenters((token) => token !== TERRAIN_TOKENS.SEA);
      const {centerX, centerY, size} = this.groundPlace;
      const halfSize = size / 2;
      this.fieldAmbience.setField({
        rect: {
          left: centerX - halfSize, right: centerX + halfSize,
          top: centerY - halfSize, bottom: centerY + halfSize,
        },
        cellCenters,
        mountainCenters: collectMountainCenters(),
        hexSize: this.gridConfig?.hexSize,
        groundBackgroundId: this.groundBackgroundId,
        matchSeed: selectGameStartedAt(state),
      });
      this.fieldAmbienceSignature = signature;
      this.fieldMountainsSignature = mountainsSignature;
    } catch (error) {
      console.warn('[GameScene] Антураж поля отключен: сбой при построении', error);
      this.dropFieldAmbience();
    }
  }

  /**
   * Признаки поля для антуража: field — где лежит пейзаж и какие ячейки
   * игровые; mountains — на каких ячейках горы. Считаются на каждом обновлении
   * стора, поэтому дешево: число ячеек и сумма по их местам (порядок ячеек не
   * важен)
   */
  calcFieldAmbienceSignature(state) {
    const hexes = selectHexes(state);
    let cellCount = 0;
    let cellsHash = 0;
    let mountainCount = 0;
    let mountainsHash = 0;
    Object.entries(this.hexGridRenderer.hexes).forEach(([key, hex]) => {
      const token = hexes[key];
      if (token === TERRAIN_TOKENS.SEA) {
        return;
      }
      const placeHash = Math.imul(Math.round(hex.centerX), 73856093) ^ Math.imul(Math.round(hex.centerY), 19349663);
      cellCount += 1;
      cellsHash = (cellsHash + placeHash) | 0;
      if (token === TERRAIN_TOKENS.MOUNTAIN) {
        mountainCount += 1;
        mountainsHash = (mountainsHash + placeHash) | 0;
      }
    });
    const {centerX, centerY, size} = this.groundPlace;
    return {
      field: `${Math.round(centerX)}:${Math.round(centerY)}:${Math.round(size)}:${cellCount}:${cellsHash}`,
      mountains: `${mountainCount}:${mountainsHash}`,
    };
  }

  onDestroy() {
    console.log('GAME SCENE shutdown');
    this.isSceneActive = false;
    // Облет захваченных городов обрывается вместе со сценой и отдает очередь
    // модалке захвата — иначе она осталась бы скрытой
    this.cityCaptureAnimation?.abort();
    this.ruinsIncomeAnimation?.abort();
    this.cancelDeferredFirstClickEffect();
    // Анимации способностей обрываются вместе со сценой — их итог больше не придержан
    resetAbilityResultHolds();
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.game?.events?.off(Phaser.Core.Events.POST_STEP, this.flushStoreUpdate, this);
    this.isStoreUpdatePending = false;
    if (this.disposeCameraControls) {
      this.disposeCameraControls();
      this.disposeCameraControls = null;
    }
    if (this.unsubscribeFieldInsets) {
      this.unsubscribeFieldInsets();
      this.unsubscribeFieldInsets = null;
    }
    if (this.fieldAmbience) {
      try {
        this.fieldAmbience.destroy();
      } catch (error) {
        console.warn('[GameScene] Не удалось убрать антураж поля', error);
      }
      this.fieldAmbience = null;
    }
    // Очистка ресурсов HexGridRenderer
    this.hexGridRenderer.clear();
    this.soundManager.shutdown();
    REGISTRY_SINGLETON.resetGameScene(this);
  }
}
